import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import * as React from 'react';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { Slottable, Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { ShoppingCart, Sparkle, ArrowsClockwise, Empty as Empty$1, ShieldWarning, WarningCircle, Prohibit, Info, XCircle, Warning, CheckCircle, TreeStructure, Database, ChartLine, Pulse, TrendUp, ChartBar, Flag, Clock, ClockCounterClockwise, Calendar as Calendar$1, Archive, FileText, Envelope, ChatText, ListChecks, Lock, Key, Users, User, Briefcase, Buildings, Scales, CurrencyDollar, CreditCard, Invoice, Receipt, ClipboardText, Package, ShippingContainer, Truck, Airplane, Boat, Barcode, MapPin, Broadcast, Path, Crane, Anchor as Anchor$1, Warehouse, Stack, Cube, Bell, Gear, MagnifyingGlass, House, SquaresFour, CircleNotch, CaretDown, X, Check, CaretUp, Minus, CaretRight, CaretLeft, CaretLineLeft, CaretLineRight, Circle, ArrowUp, ArrowDown, CaretUpDown, SlidersHorizontal, DotsThree, SidebarSimple, Sun, Moon } from '@phosphor-icons/react';
import * as RadixSeparator from '@radix-ui/react-separator';
import * as LabelPrimitive from '@radix-ui/react-label';
import * as RadixSelect from '@radix-ui/react-select';
import * as Popover$1 from '@radix-ui/react-popover';
import * as RadixCheckbox from '@radix-ui/react-checkbox';
import * as RadixRadio from '@radix-ui/react-radio-group';
import * as RadixSwitch from '@radix-ui/react-switch';
import * as RadixSlider from '@radix-ui/react-slider';
import { FormProvider, useFormContext, Controller } from 'react-hook-form';
import { DayPicker } from 'react-day-picker';
import * as RadixAccordion from '@radix-ui/react-accordion';
import { Command as Command$1 } from 'cmdk';
import * as RadixMenubar from '@radix-ui/react-menubar';
import * as RadixTabs from '@radix-ui/react-tabs';
import * as RadixDialog from '@radix-ui/react-dialog';
import * as RadixAlertDialog from '@radix-ui/react-alert-dialog';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import * as RadixMenu from '@radix-ui/react-dropdown-menu';
import * as RadixCtxMenu from '@radix-ui/react-context-menu';
import * as RadixAvatar from '@radix-ui/react-avatar';
import * as RadixProgress from '@radix-ui/react-progress';
import * as RadixToast from '@radix-ui/react-toast';
import { YAxis, XAxis, CartesianGrid, Tooltip as Tooltip$1 } from 'recharts';

const colors = {
    neutral: {
        50: '#f9fafd',
        100: '#f2f4f8',
        200: '#e3e6ed',
        300: '#d0d3dc',
        400: '#9fa3ae',
        500: '#696e7a',
        600: '#565b66',
        700: '#414551',
        800: '#292d39',
        900: '#121622',
    },
    primary: {
        50: '#f1f5ff',
        100: '#e1eaff',
        200: '#cad9ff',
        300: '#a9bfff',
        400: '#7494fb',
        500: '#4865da',
        600: '#3952c1',
        700: '#2d439f',
        800: '#22347d',
        900: '#162358',
    },
    accent: {
        50: '#f6f3fe',
        100: '#ede5fe',
        200: '#e0d0ff',
        300: '#ccb2fc',
        400: '#ab82ec',
        500: '#8450c9',
        600: '#713eb2',
        700: '#5c3293',
        800: '#482773',
        900: '#311a50',
    },
    semantic: {
        success: { default: '#26a65a', subtle: '#ecfaef', foreground: '#006732', border: '#bce6c5' },
        warning: { default: '#eba000', subtle: '#fff4e6', foreground: '#744c00', border: '#f2d4ac' },
        destructive: { default: '#d22e2c', subtle: '#fff3f1', foreground: '#90302b', border: '#ffcbc4' },
        info: { default: '#008cbc', subtle: '#ecf8ff', foreground: '#005d7f', border: '#b1e1fb' },
        neutral: { default: '#696e7a', subtle: '#f2f4f8', foreground: '#414551', border: '#e3e6ed' },
    },
    mode: {
        ocean: { default: '#0083a8', subtle: '#e9f8fd', foreground: '#005166', border: '#a9e0ee' },
        air: { default: '#c2571e', subtle: '#fef2ea', foreground: '#8a3d14', border: '#f6cbaf' },
        truck: { default: '#1c8f5f', subtle: '#e9f9f1', foreground: '#04593a', border: '#aee2c9' },
        rail: { default: '#8450c9', subtle: '#f6f3fe', foreground: '#5c3293', border: '#ddcbf6' },
        courier: { default: '#c03a63', subtle: '#fdf0f4', foreground: '#8b2544', border: '#f5c2d1' },
    },
};

const spacing = {
    0: '0px',
    1: '4px',
    2: '8px',
    3: '12px',
    4: '16px',
    5: '20px',
    6: '24px',
    8: '32px',
    10: '40px',
    12: '48px',
    16: '64px',
    20: '80px',
    24: '96px',
};
const density = {
    default: {
        pageContainer: '20px',
        stackVertical: '16px',
        cardPadding: '16px',
        tableCell: '8px',
        inlineGap: '8px',
    }};
const semanticSpacing = {
    ...density.default,
    buttonGroup: spacing[3],
    headingH1: spacing[6],
    headingH2: spacing[4],
    headingH3: spacing[3],
};

const radius = {
    none: '0px',
    sm: '4px',
    md: '6px',
    lg: '8px',
    xl: '10px',
    '2xl': '12px',
    full: '9999px',
};

const shadow = {
    none: 'none',
    xs: '0 1px 2px rgba(0, 0, 0, 0.04)',
    sm: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px rgba(0, 0, 0, 0.05), 0 2px 4px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.08), 0 4px 6px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.10), 0 8px 10px rgba(0, 0, 0, 0.04)',
};

const fonts = {
    body: 'Geist, system-ui, -apple-system, sans-serif',
    mono: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
};
const fontSize = {
    xs: '11px',
    sm: '12px',
    base: '14px',
    md: '16px',
    lg: '18px',
    xl: '20px',
    '2xl': '24px',
    '3xl': '30px',
};
const fontWeight = {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
};
const lineHeight = {
    tight: '1.2',
    snug: '1.375',
    normal: '1.5',
    relaxed: '1.6',
};
const tracking = {
    tight: '-0.02em',
    snug: '-0.01em',
    normal: '0',
    wide: '0.04em',
    wider: '0.08em',
};
const typography = {
    display: { fontFamily: fonts.body, fontSize: '30px', lineHeight: '36px', fontWeight: 700, letterSpacing: '-0.02em', textTransform: 'none' },
    h1: { fontFamily: fonts.body, fontSize: '24px', lineHeight: '32px', fontWeight: 700, letterSpacing: '-0.01em', textTransform: 'none' },
    h2: { fontFamily: fonts.body, fontSize: '18px', lineHeight: '28px', fontWeight: 600, letterSpacing: '0', textTransform: 'none' },
    h3: { fontFamily: fonts.body, fontSize: '16px', lineHeight: '24px', fontWeight: 600, letterSpacing: '0', textTransform: 'none' },
    bodyLg: { fontFamily: fonts.body, fontSize: '16px', lineHeight: '24px', fontWeight: 400, letterSpacing: '0', textTransform: 'none' },
    body: { fontFamily: fonts.body, fontSize: '14px', lineHeight: '20px', fontWeight: 400, letterSpacing: '0', textTransform: 'none' },
    label: { fontFamily: fonts.body, fontSize: '12px', lineHeight: '16px', fontWeight: 500, letterSpacing: '0', textTransform: 'none' },
    caption: { fontFamily: fonts.body, fontSize: '12px', lineHeight: '16px', fontWeight: 400, letterSpacing: '0', textTransform: 'none' },
    eyebrow: { fontFamily: fonts.body, fontSize: '11px', lineHeight: '14px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' },
    code: { fontFamily: fonts.mono, fontSize: '12px', lineHeight: '18px', fontWeight: 500, letterSpacing: '0', textTransform: 'none' },
    numeric: { fontFamily: fonts.mono, fontSize: '13px', lineHeight: '20px', fontWeight: 500, letterSpacing: '0', textTransform: 'none', fontVariantNumeric: 'tabular-nums' },
    identifier: { fontFamily: fonts.mono, fontSize: '13px', lineHeight: '20px', fontWeight: 500, letterSpacing: '-0.01em', textTransform: 'none', fontVariantNumeric: 'tabular-nums' },
};

const duration = {
    instant: '0ms',
    fast: '120ms',
    default: '180ms',
    slow: '260ms',
    slower: '400ms',
};
const easing = {
    standard: 'cubic-bezier(0.16, 1, 0.3, 1)',
    in: 'cubic-bezier(0.7, 0, 0.84, 0)',
    out: 'cubic-bezier(0.16, 1, 0.3, 1)',
    inOut: 'cubic-bezier(0.87, 0, 0.13, 1)',
    move: 'cubic-bezier(0.32, 0.72, 0, 1)',
};
const keyframes = {
    'fade-in-up': { '0%': { opacity: '0', transform: 'translateY(6px)' }, '100%': { opacity: '1', transform: 'translateY(0)' } },
    'fade-in-down': { '0%': { opacity: '0', transform: 'translateY(-6px)' }, '100%': { opacity: '1', transform: 'translateY(0)' } },
    'slide-down': { '0%': { 'grid-template-rows': '0fr' }, '100%': { 'grid-template-rows': '1fr' } },
    'slide-up': { '0%': { 'grid-template-rows': '1fr' }, '100%': { 'grid-template-rows': '0fr' } },
    'accordion-down': { from: { height: '0' }, to: { height: 'var(--radix-accordion-content-height)' } },
    'accordion-up': { from: { height: 'var(--radix-accordion-content-height)' }, to: { height: '0' } },
    'shimmer': { '0%': { 'background-position': '-200% 0' }, '100%': { 'background-position': '200% 0' } },
    'status-change': {
        '0%': { 'box-shadow': '0 0 0 0 currentColor', opacity: '0.9' },
        '70%': { 'box-shadow': '0 0 0 6px transparent', opacity: '1' },
        '100%': { 'box-shadow': '0 0 0 0 transparent', opacity: '1' },
    },
};

const zIndex = {
    base: 0,
    dropdown: 1000,
    sticky: 1100,
    overlay: 1200,
    dialog: 1300,
    popover: 1400,
    toast: 1500,
};

const lightTheme = {
    background: colors.neutral[50],
    foreground: colors.neutral[900],
    surface: '#ffffff',
    surfaceForeground: colors.neutral[900],
    surfaceRaised: '#ffffff',
    border: colors.neutral[200],
    borderStrong: colors.neutral[300],
    muted: colors.neutral[100],
    mutedForeground: colors.neutral[500],
    primary: colors.primary[500],
    primaryForeground: '#ffffff',
    primarySubtle: colors.primary[50],
    accent: colors.accent[500],
    accentForeground: '#ffffff',
    accentSubtle: colors.accent[50],
    accentBorder: colors.accent[200],
    accentOnSubtle: colors.accent[600],
    ring: colors.primary[500],
    successDefault: colors.semantic.success.default,
    successSubtle: colors.semantic.success.subtle,
    successForeground: colors.semantic.success.foreground,
    successBorder: colors.semantic.success.border,
    warningDefault: colors.semantic.warning.default,
    warningSubtle: colors.semantic.warning.subtle,
    warningForeground: colors.semantic.warning.foreground,
    warningBorder: colors.semantic.warning.border,
    destructiveDefault: colors.semantic.destructive.default,
    destructiveSubtle: colors.semantic.destructive.subtle,
    destructiveForeground: colors.semantic.destructive.foreground,
    destructiveBorder: colors.semantic.destructive.border,
    infoDefault: colors.semantic.info.default,
    infoSubtle: colors.semantic.info.subtle,
    infoForeground: colors.semantic.info.foreground,
    infoBorder: colors.semantic.info.border,
    neutralDefault: colors.semantic.neutral.default,
    neutralSubtle: colors.semantic.neutral.subtle,
    neutralForeground: colors.semantic.neutral.foreground,
    neutralBorder: colors.semantic.neutral.border,
    sidebarBackground: colors.neutral[800],
    sidebarForeground: colors.neutral[300],
    sidebarPrimary: colors.primary[500],
    sidebarAccent: colors.primary[500],
    sidebarAccentForeground: '#ffffff',
    sidebarBorder: colors.neutral[700],
    sidebarRing: colors.primary[500],
    sidebarHover: 'rgba(255, 255, 255, 0.09)',
};

const darkTheme = {
    background: colors.neutral[900],
    foreground: colors.neutral[100],
    surface: colors.neutral[800],
    surfaceForeground: colors.neutral[100],
    surfaceRaised: colors.neutral[700],
    border: 'rgba(255, 255, 255, 0.06)',
    borderStrong: 'rgba(255, 255, 255, 0.12)',
    muted: colors.neutral[800],
    mutedForeground: colors.neutral[400],
    primary: colors.primary[400],
    primaryForeground: colors.neutral[900],
    primarySubtle: 'rgba(116, 148, 251, 0.14)',
    accent: colors.accent[400],
    accentForeground: colors.neutral[900],
    accentSubtle: 'rgba(171, 130, 236, 0.14)',
    accentBorder: 'rgba(171, 130, 236, 0.34)',
    accentOnSubtle: colors.accent[300],
    ring: colors.primary[400],
    successDefault: '#4cc47c',
    successSubtle: 'rgba(38, 166, 90, 0.14)',
    successForeground: '#a6e6bf',
    successBorder: 'rgba(38, 166, 90, 0.34)',
    warningDefault: '#f5b52e',
    warningSubtle: 'rgba(235, 160, 0, 0.14)',
    warningForeground: '#f7d99a',
    warningBorder: 'rgba(235, 160, 0, 0.34)',
    destructiveDefault: '#ef6a63',
    destructiveSubtle: 'rgba(210, 46, 44, 0.14)',
    destructiveForeground: '#f6b8b3',
    destructiveBorder: 'rgba(210, 46, 44, 0.34)',
    infoDefault: '#3fb0dc',
    infoSubtle: 'rgba(0, 140, 188, 0.14)',
    infoForeground: '#a5dcf0',
    infoBorder: 'rgba(0, 140, 188, 0.34)',
    neutralDefault: colors.neutral[400],
    neutralSubtle: 'rgba(255, 255, 255, 0.04)',
    neutralForeground: colors.neutral[300],
    neutralBorder: 'rgba(255, 255, 255, 0.08)',
    sidebarBackground: colors.neutral[900],
    sidebarForeground: colors.neutral[300],
    sidebarPrimary: colors.primary[500],
    sidebarAccent: colors.primary[500],
    sidebarAccentForeground: '#ffffff',
    sidebarBorder: 'rgba(255, 255, 255, 0.06)',
    sidebarRing: colors.primary[500],
    sidebarHover: 'rgba(255, 255, 255, 0.09)',
};

const themes = {
    light: lightTheme,
    dark: darkTheme,
};

function cn(...inputs) {
    return twMerge(clsx(inputs));
}

const SPACING_PAGE_SECTION = 'mb-6';
const SPACING_PAGE_CONTAINER = 'p-6';
const SPACING_SECTION_ELEMENT = 'mb-4';
const SPACING_CARD_PADDING = 'p-6';
const SPACING_TABLE_CELL = 'p-4';
const SPACING_TABLE_TO_PAGINATION = 'mt-6';
const SPACING_KPI_GRID_GAP = 'gap-4';
const SPACING_KPI_GRID_GAP_PREMIUM = 'gap-6';
const SPACING_STACK_VERTICAL = 'space-y-6';
const SPACING_INLINE_GAP = 'gap-2';
const SPACING_BUTTON_GROUP_GAP = 'gap-3';
const SPACING_HEADING_H1 = 'mb-6';
const SPACING_HEADING_H2 = 'mb-4';
const SPACING_HEADING_H3 = 'mb-3';
const SPACING_WORKFLOW_STEPPER = 'mb-6';
const SPACING_FORM_FIELD = 'mb-4';
const SPACING_INFO_BOX_PADDING = 'p-3';
const SPACING_PRESETS = {
    page: {
        container: SPACING_PAGE_CONTAINER,
        section: SPACING_PAGE_SECTION,
    },
    card: {
        padding: SPACING_CARD_PADDING,
    },
    grid: {
        kpi: SPACING_KPI_GRID_GAP,
        kpiPremium: SPACING_KPI_GRID_GAP_PREMIUM,
    },
    table: {
        cell: SPACING_TABLE_CELL,
        toPagination: SPACING_TABLE_TO_PAGINATION,
    },
    heading: {
        h1: SPACING_HEADING_H1,
        h2: SPACING_HEADING_H2,
        h3: SPACING_HEADING_H3,
    },
    workflow: {
        stepper: SPACING_WORKFLOW_STEPPER,
        formField: SPACING_FORM_FIELD,
    },
};

const TOAST_LIMIT = 1;
const TOAST_REMOVE_DELAY = 1000000;
let count = 0;
function genId() {
    count = (count + 1) % Number.MAX_SAFE_INTEGER;
    return count.toString();
}
const toastTimeouts = new Map();
const addToRemoveQueue = (toastId) => {
    if (toastTimeouts.has(toastId))
        return;
    const timeout = setTimeout(() => {
        toastTimeouts.delete(toastId);
        dispatch({ type: 'REMOVE_TOAST', toastId });
    }, TOAST_REMOVE_DELAY);
    toastTimeouts.set(toastId, timeout);
};
const reducer = (state, action) => {
    switch (action.type) {
        case 'ADD_TOAST':
            return { ...state, toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT) };
        case 'UPDATE_TOAST':
            return {
                ...state,
                toasts: state.toasts.map((t) => t.id === action.toast.id ? { ...t, ...action.toast } : t),
            };
        case 'DISMISS_TOAST': {
            const { toastId } = action;
            if (toastId) {
                addToRemoveQueue(toastId);
            }
            else {
                state.toasts.forEach((t) => addToRemoveQueue(t.id));
            }
            return {
                ...state,
                toasts: state.toasts.map((t) => t.id === toastId || toastId === undefined ? { ...t, open: false } : t),
            };
        }
        case 'REMOVE_TOAST':
            if (action.toastId === undefined)
                return { ...state, toasts: [] };
            return { ...state, toasts: state.toasts.filter((t) => t.id !== action.toastId) };
    }
};
const listeners = [];
let memoryState = { toasts: [] };
function dispatch(action) {
    memoryState = reducer(memoryState, action);
    listeners.forEach((listener) => listener(memoryState));
}
function toast({ variant, ...props }) {
    const id = genId();
    const tone = props.tone ?? (variant && variant !== 'default' ? variant : undefined);
    const update = (p) => dispatch({ type: 'UPDATE_TOAST', toast: { ...p, id } });
    const dismiss = () => dispatch({ type: 'DISMISS_TOAST', toastId: id });
    dispatch({
        type: 'ADD_TOAST',
        toast: {
            ...props,
            ...(tone ? { tone } : {}),
            id,
            open: true,
            onOpenChange: (open) => {
                if (!open)
                    dismiss();
            },
        },
    });
    return { id, dismiss, update };
}
function useToast() {
    const [state, setState] = React.useState(memoryState);
    React.useEffect(() => {
        listeners.push(setState);
        return () => {
            const index = listeners.indexOf(setState);
            if (index > -1)
                listeners.splice(index, 1);
        };
    }, [state]);
    return {
        ...state,
        toast,
        dismiss: (toastId) => toastId !== undefined
            ? dispatch({ type: 'DISMISS_TOAST', toastId })
            : dispatch({ type: 'DISMISS_TOAST' }),
    };
}

function measureAlong(container, target, axis) {
    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    return axis === 'horizontal'
        ? {
            offset: targetRect.left - containerRect.left + container.scrollLeft,
            size: targetRect.width,
        }
        : {
            offset: targetRect.top - containerRect.top + container.scrollTop,
            size: targetRect.height,
        };
}
function slidingStyle(geometry, axis, visible, instant) {
    const offset = geometry?.offset ?? 0;
    const size = geometry?.size ?? 0;
    const sizeProperty = axis === 'horizontal' ? 'width' : 'height';
    return {
        transform: axis === 'horizontal' ? `translateX(${offset}px)` : `translateY(${offset}px)`,
        [sizeProperty]: size,
        opacity: visible ? 1 : 0,
        transitionProperty: instant ? 'opacity' : `transform, ${sizeProperty}, opacity`,
    };
}
function useSlidingHighlight({ selector = 'a, button', axis = 'vertical' } = {}) {
    const containerRef = React.useRef(null);
    const [geometry, setGeometry] = React.useState(null);
    const [visible, setVisible] = React.useState(false);
    const [instant, setInstant] = React.useState(true);
    const track = React.useCallback((target) => {
        const container = containerRef.current;
        if (!container || !(target instanceof Element))
            return;
        const item = target.closest(selector);
        if (!item || !container.contains(item))
            return;
        setInstant(!visible);
        setGeometry(measureAlong(container, item, axis));
        setVisible(true);
    }, [selector, axis, visible]);
    const hide = React.useCallback(() => setVisible(false), []);
    const onBlur = React.useCallback((event) => {
        const next = event.relatedTarget;
        if (next instanceof Node && containerRef.current?.contains(next))
            return;
        setVisible(false);
    }, []);
    return {
        containerProps: {
            ref: containerRef,
            onMouseOver: (event) => track(event.target),
            onMouseLeave: hide,
            onFocus: (event) => track(event.target),
            onBlur,
        },
        style: slidingStyle(geometry, axis, visible, instant),
        visible,
    };
}
function useSlidingMarker(containerRef, selector, deps, { axis = 'vertical', observeAttributes } = {}) {
    const [geometry, setGeometry] = React.useState(null);
    const wasVisible = React.useRef(false);
    const [instant, setInstant] = React.useState(true);
    const measure = React.useCallback(() => {
        const container = containerRef.current;
        const target = container?.querySelector(selector);
        if (!container || !target) {
            wasVisible.current = false;
            setGeometry(null);
            return;
        }
        setInstant(!wasVisible.current);
        wasVisible.current = true;
        setGeometry(measureAlong(container, target, axis));
    }, [containerRef, selector, axis]);
    const attributeFilter = React.useRef(observeAttributes);
    attributeFilter.current = observeAttributes;
    React.useLayoutEffect(() => {
        measure();
        const container = containerRef.current;
        if (!container)
            return;
        const disconnect = [];
        if (typeof ResizeObserver !== 'undefined') {
            const resize = new ResizeObserver(measure);
            resize.observe(container);
            disconnect.push(() => resize.disconnect());
        }
        const filter = attributeFilter.current;
        if (filter && typeof MutationObserver !== 'undefined') {
            const mutations = new MutationObserver(measure);
            mutations.observe(container, {
                subtree: true,
                attributes: true,
                attributeFilter: filter,
                childList: true,
            });
            disconnect.push(() => mutations.disconnect());
        }
        return () => disconnect.forEach((fn) => fn());
    }, [measure, ...deps]);
    return {
        style: slidingStyle(geometry, axis, geometry !== null, instant),
        visible: geometry !== null,
    };
}

function useRowTransitions(keys, enabled) {
    const nodes = React.useRef(new Map());
    const offsets = React.useRef(new Map());
    const signature = keys.join('\u0001');
    const [seen, setSeen] = React.useState(() => ({ signature, keys, entering: new Set() }));
    if (seen.signature !== signature) {
        const had = new Set(seen.keys);
        setSeen({
            signature,
            keys,
            entering: enabled ? new Set(keys.filter((k) => !had.has(k))) : new Set(),
        });
    }
    const entering = seen.signature === signature ? seen.entering : new Set();
    React.useLayoutEffect(() => {
        if (!enabled) {
            offsets.current.clear();
            return;
        }
        const previous = offsets.current;
        const next = new Map();
        for (const [key, node] of nodes.current) {
            next.set(key, node.offsetTop);
        }
        const moved = [];
        for (const [key, node] of nodes.current) {
            const before = previous.get(key);
            const after = next.get(key);
            if (before === undefined || after === undefined || before === after)
                continue;
            node.style.transition = 'none';
            node.style.transform = `translateY(${before - after}px)`;
            moved.push(node);
        }
        offsets.current = next;
        if (moved.length === 0)
            return;
        const release = requestAnimationFrame(() => {
            for (const node of moved) {
                node.style.transition = 'transform 260ms cubic-bezier(0.87, 0, 0.13, 1)';
                node.style.transform = '';
            }
        });
        return () => cancelAnimationFrame(release);
    }, [signature, enabled]);
    const register = React.useCallback((key) => (node) => {
        if (node)
            nodes.current.set(key, node);
        else
            nodes.current.delete(key);
    }, []);
    return {
        isEntering: (key) => entering.has(key),
        register,
    };
}

const Icon$1 = React.forwardRef(({ icon: Glyph, size = 16, weight = 'regular', decorative = true, className, ...rest }, ref) => {
    const ariaProps = decorative
        ? { 'aria-hidden': true }
        : { role: 'img' };
    return (jsx(Glyph, { ref: ref, size: size, weight: weight, className: cn('shrink-0', className), ...ariaProps, ...rest }));
});
Icon$1.displayName = 'Icon';

const iconRegistry = {
    dashboard: SquaresFour,
    home: House,
    search: MagnifyingGlass,
    settings: Gear,
    notifications: Bell,
    shipment: ShippingContainer,
    container: ShippingContainer,
    package: Package,
    product: Cube,
    inventory: Stack,
    warehouse: Warehouse,
    port: Anchor$1,
    terminal: Crane,
    route: Path,
    tracking: Broadcast,
    location: MapPin,
    barcode: Barcode,
    ocean: Boat,
    air: Airplane,
    truck: Truck,
    rail: ShippingContainer,
    courier: Package,
    order: ClipboardText,
    purchaseOrder: Receipt,
    invoice: Invoice,
    payment: CreditCard,
    currency: CurrencyDollar,
    cost: Scales,
    company: Buildings,
    vendor: Briefcase,
    user: User,
    team: Users,
    permission: Key,
    security: Lock,
    task: ListChecks,
    message: ChatText,
    email: Envelope,
    document: FileText,
    archive: Archive,
    calendar: Calendar$1,
    history: ClockCounterClockwise,
    pending: Clock,
    flag: Flag,
    analytics: ChartBar,
    trend: TrendUp,
    activity: Pulse,
    timeseries: ChartLine,
    dataSource: Database,
    workflow: TreeStructure,
    success: CheckCircle,
    warning: Warning,
    error: XCircle,
    info: Info,
    blocked: Prohibit,
    alert: WarningCircle,
    risk: ShieldWarning,
    empty: Empty$1,
    sync: ArrowsClockwise,
    ai: Sparkle,
    cart: ShoppingCart,
};
const Icon = React.forwardRef(({ name, ...props }, ref) => {
    const Glyph = iconRegistry[name];
    return jsx(Glyph, { ref: ref, ...props });
});
Icon.displayName = 'Icon';

const buttonStyles = cva([
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium',
    'transition-all duration-200',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:pointer-events-none disabled:opacity-50',
], {
    variants: {
        variant: {
            primary: 'bg-primary text-primary-foreground shadow-sm hover:opacity-90 active:scale-[0.98]',
            secondary: 'bg-surface text-surface-foreground border border-border shadow-xs hover:bg-muted',
            ghost: 'bg-transparent text-foreground hover:bg-muted',
            outline: 'bg-transparent text-foreground border border-border-strong hover:bg-muted',
            destructive: 'bg-destructive text-white shadow-sm hover:opacity-90 active:scale-[0.98]',
            subtle: 'bg-primary-subtle text-primary hover:bg-primary-subtle/80',
        },
        size: {
            xs: 'h-7  px-2   text-xs',
            sm: 'h-8  px-3   text-sm',
            md: 'h-9  px-3.5 text-sm',
            lg: 'h-10 px-4   text-base',
        },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
});
const Button = React.forwardRef(({ className, variant, size, asChild = false, isLoading = false, leftIcon, rightIcon, children, disabled, type = 'button', ...rest }, ref) => {
    const Comp = asChild ? Slot : 'button';
    const content = asChild ? ([
        jsx(React.Fragment, { children: isLoading ? jsx(Icon$1, { icon: CircleNotch, size: 14, className: "animate-spin" }) : leftIcon }, "lead"),
        jsx(Slottable, { children: children }, "child"),
        jsx(React.Fragment, { children: !isLoading && rightIcon }, "trail"),
    ]) : (jsxs(Fragment, { children: [jsxs("span", { className: cn('inline-flex w-full min-w-0 items-center justify-center gap-2 transition-opacity duration-fast', isLoading && 'opacity-0'), children: [leftIcon, children, rightIcon] }), isLoading && (jsx("span", { className: "absolute inset-0 flex items-center justify-center", children: jsx(Icon$1, { icon: CircleNotch, size: 14, className: "animate-spin" }) }))] }));
    return (jsx(Comp, { ref: ref, type: type, "aria-busy": isLoading || undefined, disabled: disabled || isLoading, className: cn(buttonStyles({ variant, size }), !asChild && 'relative', className), ...rest, children: content }));
});
Button.displayName = 'Button';

const iconButtonStyles = cva([
    'inline-flex items-center justify-center rounded-lg transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:pointer-events-none disabled:opacity-50',
], {
    variants: {
        variant: {
            primary: 'bg-primary text-primary-foreground shadow-sm hover:opacity-90',
            secondary: 'bg-surface text-surface-foreground border border-border hover:bg-muted',
            ghost: 'bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground',
            destructive: 'bg-transparent text-destructive hover:bg-destructive-subtle',
        },
        size: {
            sm: 'h-7 w-7',
            md: 'h-8 w-8',
            lg: 'h-10 w-10',
        },
    },
    defaultVariants: { variant: 'ghost', size: 'md' },
});
const ICON_SIZE_FOR_BUTTON = { sm: 12, md: 16, lg: 20 };
const IconButton = React.forwardRef(({ icon, variant, size = 'md', className, type = 'button', ...rest }, ref) => (jsx("button", { ref: ref, type: type, className: cn(iconButtonStyles({ variant, size }), className), ...rest, children: jsx(Icon$1, { icon: icon, size: ICON_SIZE_FOR_BUTTON[size ?? 'md'] }) })));
IconButton.displayName = 'IconButton';

const Separator$4 = React.forwardRef(({ className, orientation = 'horizontal', decorative = true, ...rest }, ref) => (jsx(RadixSeparator.Root, { ref: ref, orientation: orientation, decorative: decorative, className: cn('shrink-0 bg-border', orientation === 'horizontal' ? 'h-px w-full' : 'h-full w-px', className), ...rest })));
Separator$4.displayName = 'Separator';

const inputStyles = cva([
    'flex w-full rounded-lg border border-border bg-surface text-surface-foreground transition-colors',
    'placeholder:text-muted-foreground',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:cursor-not-allowed disabled:opacity-50',
    'aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:ring-destructive',
    'file:mr-3 file:cursor-pointer file:rounded-md file:border-0 file:bg-muted',
    'file:px-2.5 file:py-1 file:text-sm file:font-medium file:text-foreground',
    'file:transition-colors hover:file:bg-muted/80',
], {
    variants: {
        size: {
            sm: 'h-8 px-2.5 text-sm',
            md: 'h-9 px-3   text-sm',
            lg: 'h-10 px-3.5 text-base',
        },
    },
    defaultVariants: { size: 'md' },
});
const Input$1 = React.forwardRef(({ className, size, startContent, endContent, isInvalid, type = 'text', ...rest }, ref) => {
    const hasAffix = startContent || endContent;
    const input = (jsx("input", { ref: ref, type: type, "aria-invalid": isInvalid || undefined, className: cn(inputStyles({ size }), startContent && 'pl-9', endContent && 'pr-9', !hasAffix && className), ...rest }));
    if (!hasAffix)
        return input;
    return (jsxs("div", { className: cn('relative inline-flex w-full items-center', className), children: [startContent && jsx("span", { className: "pointer-events-none absolute left-3 inline-flex text-muted-foreground", children: startContent }), input, endContent && jsx("span", { className: "pointer-events-none absolute right-3 inline-flex text-muted-foreground", children: endContent })] }));
});
Input$1.displayName = 'Input';

const Textarea = React.forwardRef(({ className, rows = 3, isInvalid, ...rest }, ref) => (jsx("textarea", { ref: ref, rows: rows, "aria-invalid": isInvalid || undefined, className: cn('flex w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-surface-foreground transition-colors', 'placeholder:text-muted-foreground resize-y', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background', 'disabled:cursor-not-allowed disabled:opacity-50', 'aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:ring-destructive', className), ...rest })));
Textarea.displayName = 'Textarea';

const Label$2 = React.forwardRef(({ className, children, isRequired, ...rest }, ref) => (jsxs(LabelPrimitive.Root, { ref: ref, className: cn('text-sm font-medium text-foreground', className), ...rest, children: [children, isRequired && jsx("span", { className: "ml-0.5 text-destructive", "aria-hidden": true, children: "*" })] })));
Label$2.displayName = 'Label';

const SurfaceThemeContext = React.createContext(undefined);
function SurfaceThemeProvider({ children, ...theme }) {
    return jsx(SurfaceThemeContext.Provider, { value: theme, children: children });
}
function useSurfaceTheme() {
    const theme = React.useContext(SurfaceThemeContext);
    if (!theme)
        return {};
    const { backgroundColor: _background, backgroundImage: _image, ...style } = theme.style;
    return { ...(theme.className !== undefined ? { className: theme.className } : {}), style };
}

const MultiCtx = React.createContext(null);
const useMulti = () => {
    const c = React.useContext(MultiCtx);
    if (!c)
        throw new Error('Select.* (multi) must be inside <Select.Root multiple>');
    return c;
};
function MultiRoot({ value: controlled, defaultValue = [], onValueChange, disabled, children }) {
    const [internal, setInternal] = React.useState(defaultValue);
    const value = controlled ?? internal;
    const [options, setOptions] = React.useState(new Map());
    const registerOption = React.useCallback((v, label) => {
        setOptions(prev => {
            if (prev.get(v) === label)
                return prev;
            const next = new Map(prev);
            next.set(v, label);
            return next;
        });
    }, []);
    const commit = React.useCallback((next) => {
        if (controlled === undefined)
            setInternal(next);
        onValueChange?.(next);
    }, [controlled, onValueChange]);
    const toggle = React.useCallback((v) => {
        commit(value.includes(v) ? value.filter(x => x !== v) : [...value, v]);
    }, [value, commit]);
    const remove = React.useCallback((v) => commit(value.filter(x => x !== v)), [value, commit]);
    const ctx = React.useMemo(() => ({ value, toggle, remove, options, registerOption, disabled: Boolean(disabled) }), [value, toggle, remove, options, registerOption, disabled]);
    return (jsx(MultiCtx.Provider, { value: ctx, children: jsx(Popover$1.Root, { ...(disabled ? { open: false } : {}), children: children }) }));
}
const MultiTrigger = React.forwardRef(({ className, children, disabled, ...rest }, ref) => {
    const { disabled: rootDisabled } = useMulti();
    return (jsxs(Popover$1.Trigger, { ref: ref, disabled: disabled ?? rootDisabled, className: cn('flex min-h-9 w-full items-center justify-between gap-2 rounded-lg border border-border bg-surface px-2 py-1 text-sm text-surface-foreground', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background', 'disabled:cursor-not-allowed disabled:opacity-50', className), ...rest, children: [children, jsx(Icon$1, { icon: CaretDown, size: 14, className: "text-muted-foreground" })] }));
});
MultiTrigger.displayName = 'Select.Trigger';
function MultiValue() {
    const { value, options, remove } = useMulti();
    if (value.length === 0)
        return jsx("span", { className: "text-muted-foreground", children: "Select\u2026" });
    return (jsx("div", { className: "flex flex-wrap gap-1", children: value.map(v => (jsxs("span", { className: "inline-flex items-center gap-1 rounded-md bg-primary-subtle px-1.5 py-0.5 text-xs text-primary", children: [options.get(v) ?? v, jsx("button", { type: "button", onClick: (e) => { e.stopPropagation(); remove(v); }, "aria-label": `Remove ${v}`, children: jsx(Icon$1, { icon: X, size: 12 }) })] }, v))) }));
}
const MultiContent = React.forwardRef(({ className, children, ...rest }, _ref) => {
    const highlight = useSlidingHighlight();
    const { ref, ...handlers } = highlight.containerProps;
    return (jsxs(Fragment, { children: [jsx("div", { "aria-hidden": "true", className: "hidden", children: children }), jsx(Popover$1.Portal, { children: jsxs(Popover$1.Content, { ref: ref, sideOffset: 4, className: cn('relative z-popover min-w-[var(--radix-popover-trigger-width)] overflow-y-auto rounded-lg border border-border bg-surface p-1 shadow-md', 'max-h-[min(24rem,var(--radix-popover-content-available-height))]', className), ...rest, ...handlers, children: [jsx("span", { "aria-hidden": true, "data-slot": "select-multi-highlight", className: "pointer-events-none absolute inset-x-1 top-0 rounded-md bg-muted duration-slow ease-move", style: highlight.style }), children] }) })] }));
});
MultiContent.displayName = 'Select.Content';
function MultiItem({ value, children, className }) {
    const { value: selected, toggle, registerOption } = useMulti();
    React.useEffect(() => registerOption(value, children), [value, children, registerOption]);
    const checked = selected.includes(value);
    return (jsxs("button", { type: "button", role: "menuitemcheckbox", "aria-checked": checked, onClick: () => toggle(value), className: cn('relative flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-foreground', className), children: [jsx("span", { className: "inline-flex h-4 w-4 items-center justify-center rounded border border-border bg-surface", children: checked && jsx(Icon$1, { icon: Check, size: 12 }) }), children] }));
}

const Group$3 = RadixSelect.Group;
const Value = RadixSelect.Value;
const SingleTrigger = React.forwardRef(({ className, children, ...rest }, ref) => (jsxs(RadixSelect.Trigger, { ref: ref, className: cn('flex h-9 w-full items-center justify-between gap-2 rounded-lg border border-border bg-surface px-3 text-sm text-surface-foreground transition-colors', 'placeholder:text-muted-foreground', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background', 'disabled:cursor-not-allowed disabled:opacity-50', 'data-[placeholder]:text-muted-foreground', className), ...rest, children: [children, jsx(RadixSelect.Icon, { asChild: true, children: jsx(Icon$1, { icon: CaretDown, size: 14, className: "text-muted-foreground" }) })] })));
SingleTrigger.displayName = 'Select.Trigger';
const SingleContent = React.forwardRef(({ className, style, children, position = 'popper', ...rest }, ref) => {
    const scope = useSurfaceTheme();
    return (jsx(RadixSelect.Portal, { children: jsxs(RadixSelect.Content, { ref: ref, style: { ...scope.style, ...style }, position: position, sideOffset: 4, className: cn(scope.className, 'z-popover overflow-hidden rounded-lg border border-border bg-surface text-surface-foreground shadow-md', 'data-[state=open]:animate-fade-in-up', 'min-w-[var(--radix-select-trigger-width)]', className), ...rest, children: [jsx(RadixSelect.ScrollUpButton, { className: "flex items-center justify-center py-1 text-muted-foreground", children: jsx(Icon$1, { icon: CaretUp, size: 14 }) }), jsx(RadixSelect.Viewport, { className: "p-1", style: { maxHeight: 300, overflowY: 'auto' }, children: children }), jsx(RadixSelect.ScrollDownButton, { className: "flex items-center justify-center py-1 text-muted-foreground", children: jsx(Icon$1, { icon: CaretDown, size: 14 }) })] }) }));
});
SingleContent.displayName = 'Select.Content';
const SingleItem = React.forwardRef(({ className, children, ...rest }, ref) => (jsxs(RadixSelect.Item, { ref: ref, className: cn('relative flex cursor-default select-none items-center rounded-md py-1.5 pl-8 pr-2 text-sm text-foreground outline-none', 'data-[highlighted]:bg-muted data-[disabled]:pointer-events-none data-[disabled]:opacity-50', className), ...rest, children: [jsx("span", { className: "absolute left-2 inline-flex h-4 w-4 items-center justify-center", children: jsx(RadixSelect.ItemIndicator, { children: jsx(Icon$1, { icon: Check, size: 12 }) }) }), jsx(RadixSelect.ItemText, { children: children })] })));
SingleItem.displayName = 'Select.Item';
function SelectRoot(props) {
    if (props.multiple) {
        const multiValue = props.value;
        const multiDefault = props.defaultValue ?? [];
        const multiChange = props.onValueChange;
        return (jsx(MultiRoot, { ...(multiValue !== undefined ? { value: multiValue } : {}), defaultValue: multiDefault, ...(multiChange !== undefined ? { onValueChange: multiChange } : {}), ...(props.disabled ? { disabled: true } : {}), children: props.children }));
    }
    const { multiple: _multiple, value, defaultValue, onValueChange, children, ...rootProps } = props;
    const singleValue = value;
    const singleDefault = defaultValue;
    const singleChange = onValueChange;
    return (jsx(RadixSelect.Root, { ...rootProps, ...(singleValue !== undefined ? { value: singleValue } : {}), ...(singleDefault !== undefined ? { defaultValue: singleDefault } : {}), ...(singleChange !== undefined ? { onValueChange: singleChange } : {}), children: children }));
}
const TriggerDispatch = (props) => props.multiple ? jsx(MultiTrigger, { ...props }) : jsx(SingleTrigger, { ...props });
const ContentDispatch = (props) => props.multiple ? jsx(MultiContent, { ...props }) : jsx(SingleContent, { ...props });
const ItemDispatch = (props) => props.multiple
    ? jsx(MultiItem, { value: props.value, className: props.className, children: props.children })
    : jsx(SingleItem, { ...props });
const SelectRootWrapper = Object.assign(SelectRoot, {
    Root: SelectRoot,
    Group: Group$3,
    Value,
    MultiValue,
    Trigger: TriggerDispatch,
    Content: ContentDispatch,
    Item: ItemDispatch,
});
const Select = SelectRootWrapper;
const SelectValue = Value;
const SelectTrigger = TriggerDispatch;
const SelectContent = ContentDispatch;
const SelectItem = ItemDispatch;
const SelectGroup = Group$3;
const SelectMultiValue = MultiValue;

const Checkbox = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixCheckbox.Root, { ref: ref, className: cn('peer inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border border-border bg-surface shadow-xs transition-colors', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background', 'data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-primary-foreground', 'data-[state=indeterminate]:bg-primary data-[state=indeterminate]:border-primary data-[state=indeterminate]:text-primary-foreground', 'disabled:cursor-not-allowed disabled:opacity-50', className), ...rest, children: jsx(RadixCheckbox.Indicator, { className: "flex items-center justify-center data-[state=checked]:animate-in data-[state=checked]:zoom-in-50 data-[state=checked]:duration-fast data-[state=checked]:ease-out", children: jsx(Icon$1, { icon: rest.checked === 'indeterminate' ? Minus : Check, size: 12 }) }) })));
Checkbox.displayName = 'Checkbox';

const Root$9 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixRadio.Root, { ref: ref, className: cn('grid gap-2', className), ...rest })));
Root$9.displayName = 'RadioGroup.Root';
const Item$6 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixRadio.Item, { ref: ref, className: cn('inline-flex h-4 w-4 items-center justify-center rounded-full border border-border bg-surface shadow-xs', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background', 'disabled:cursor-not-allowed disabled:opacity-50', 'data-[state=checked]:border-primary', className), ...rest, children: jsx(RadixRadio.Indicator, { className: "h-2 w-2 rounded-full bg-primary" }) })));
Item$6.displayName = 'RadioGroup.Item';
const RadioGroup$2 = Object.assign(Root$9, {
    Root: Root$9,
    Item: Item$6,
});
const RadioGroupItem = Item$6;

const Switch = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixSwitch.Root, { ref: ref, className: cn('peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background', 'disabled:cursor-not-allowed disabled:opacity-50', 'data-[state=checked]:bg-primary data-[state=unchecked]:bg-muted-foreground/40', className), ...rest, children: jsx(RadixSwitch.Thumb, { className: "pointer-events-none block h-4 w-4 rounded-full bg-background shadow-md ring-0 transition-transform duration-slow ease-move data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0" }) })));
Switch.displayName = 'Switch';

const Slider = React.forwardRef(({ className, ...rest }, ref) => (jsxs(RadixSlider.Root, { ref: ref, className: cn('relative flex w-full touch-none select-none items-center', className), ...rest, children: [jsx(RadixSlider.Track, { className: "relative h-1.5 w-full grow overflow-hidden rounded-full bg-muted", children: jsx(RadixSlider.Range, { className: "absolute h-full bg-primary" }) }), jsx(RadixSlider.Thumb, { className: "block h-4 w-4 rounded-full border-2 border-primary bg-surface shadow transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50" })] })));
Slider.displayName = 'Slider';

const COLS$1 = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-3',
};
const FormSection = React.forwardRef(({ className, title, description, columns = 1, action, children, ...rest }, ref) => (jsxs("section", { ref: ref, className: cn('mb-6 last:mb-0', className), ...rest, children: [(title || action) && (jsxs("div", { className: cn('flex items-center justify-between gap-3', description ? 'mb-1' : 'mb-4'), children: [title && jsx("h3", { className: "text-base font-semibold leading-tight text-foreground", children: title }), action && jsx("div", { className: "flex shrink-0 items-center gap-2", children: action })] })), description && jsx("p", { className: "mb-4 text-sm text-muted-foreground", children: description }), jsx("div", { className: cn('grid gap-4', COLS$1[columns]), children: children })] })));
FormSection.displayName = 'Form.Section';

const FieldContext = React.createContext(null);
const useField = () => {
    const c = React.useContext(FieldContext);
    if (!c)
        throw new Error('Form.* must be used inside Form.Field');
    return c;
};
function Field$1({ name, children, className }) {
    const methods = useFormContext();
    const error = methods.formState.errors[name]?.message;
    const id = React.useId();
    return (jsx(FieldContext.Provider, { value: { name, error, describedById: `${id}-desc`, controlId: `${id}-control` }, children: jsx("div", { className: cn('space-y-1.5', className), children: children }) }));
}
function LegacyField({ name, control, render }) {
    const id = React.useId();
    return (jsx(Controller, { name: name, control: control, render: (rp) => (jsx(FieldContext.Provider, { value: {
                name: String(name),
                error: rp.fieldState.error?.message,
                describedById: `${id}-desc`,
                controlId: `${id}-control`,
                legacy: true,
            }, children: render(rp) })) }));
}
function SmartFormField(props) {
    if ('render' in props) {
        return jsx(LegacyField, { ...props });
    }
    return jsx(Field$1, { ...props });
}
function FieldLabel(props) {
    const { controlId } = useField();
    return jsx(Label$2, { htmlFor: controlId, ...props });
}
function FieldControl({ children, asChild, ...rest }) {
    const { name, error, controlId, describedById, legacy } = useField();
    const methods = useFormContext();
    if (legacy) {
        return jsx(Fragment, { children: children });
    }
    const Comp = asChild ? Slot : 'div';
    return (jsx(Controller, { name: name, control: methods.control, render: ({ field }) => (jsx(Comp, { id: controlId, "aria-invalid": !!error || undefined, "aria-describedby": describedById, ...field, ...rest, children: children })) }));
}
function FieldDescription({ className, children, ...rest }) {
    const { describedById } = useField();
    return (jsx("p", { id: describedById, className: cn('text-xs text-muted-foreground', className), ...rest, children: children }));
}
function FieldMessage({ className, ...rest }) {
    const { error } = useField();
    if (!error)
        return null;
    return (jsx("div", { className: "grid animate-slide-down duration-fast ease-out", children: jsx("p", { className: cn('overflow-hidden text-xs text-destructive', className), ...rest, children: error }) }));
}
function FormRoot({ children, ...methods }) {
    return jsx(FormProvider, { ...methods, children: children });
}
FormRoot.displayName = 'Form';
const FormItem = React.forwardRef(({ className, ...rest }, ref) => (jsx("div", { ref: ref, className: cn('space-y-2', className), ...rest })));
FormItem.displayName = 'FormItem';
const Form = Object.assign(FormRoot, {
    Root: FormRoot,
    Field: Field$1,
    Section: FormSection,
    Item: FormItem,
    Label: FieldLabel,
    Control: FieldControl,
    Description: FieldDescription,
    Message: FieldMessage,
});
const FormField = SmartFormField;
const FormLabel = FieldLabel;
const FormControl = FieldControl;
const FormDescription = FieldDescription;
const FormMessage = FieldMessage;

const Calendar = React.forwardRef(({ className, classNames, showOutsideDays = true, ...rest }, ref) => (jsx("div", { ref: ref, children: jsx(DayPicker, { showOutsideDays: showOutsideDays, className: cn('p-3', className), classNames: {
            months: 'flex flex-col sm:flex-row gap-4',
            month: 'space-y-3',
            caption: 'flex justify-between items-center px-1',
            caption_label: 'text-sm font-medium',
            nav: 'flex items-center gap-1',
            nav_button: 'inline-flex h-7 w-7 items-center justify-center rounded-md border border-border hover:bg-muted',
            nav_button_previous: '',
            nav_button_next: '',
            table: 'w-full border-collapse space-y-1',
            head_row: 'flex',
            head_cell: 'w-8 text-center text-xs font-medium text-muted-foreground',
            row: 'flex w-full mt-1',
            cell: 'h-8 w-8 text-center text-sm',
            day: 'inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-muted',
            day_selected: 'bg-primary text-primary-foreground hover:bg-primary',
            day_today: 'ring-1 ring-primary',
            day_outside: 'text-muted-foreground opacity-50',
            day_disabled: 'opacity-40 pointer-events-none',
            ...classNames,
        }, components: {
            IconLeft: () => jsx(CaretLeft, { className: "h-4 w-4" }),
            IconRight: () => jsx(CaretRight, { className: "h-4 w-4" }),
        }, ...rest }) })));
Calendar.displayName = 'Calendar';

/**
 * @name toDate
 * @category Common Helpers
 * @summary Convert the given argument to an instance of Date.
 *
 * @description
 * Convert the given argument to an instance of Date.
 *
 * If the argument is an instance of Date, the function returns its clone.
 *
 * If the argument is a number, it is treated as a timestamp.
 *
 * If the argument is none of the above, the function returns Invalid Date.
 *
 * **Note**: *all* Date arguments passed to any *date-fns* function is processed by `toDate`.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param argument - The value to convert
 *
 * @returns The parsed date in the local time zone
 *
 * @example
 * // Clone the date:
 * const result = toDate(new Date(2014, 1, 11, 11, 30, 30))
 * //=> Tue Feb 11 2014 11:30:30
 *
 * @example
 * // Convert the timestamp to date:
 * const result = toDate(1392098430000)
 * //=> Tue Feb 11 2014 11:30:30
 */
function toDate(argument) {
  const argStr = Object.prototype.toString.call(argument);

  // Clone the date
  if (
    argument instanceof Date ||
    (typeof argument === "object" && argStr === "[object Date]")
  ) {
    // Prevent the date to lose the milliseconds when passed to new Date() in IE10
    return new argument.constructor(+argument);
  } else if (
    typeof argument === "number" ||
    argStr === "[object Number]" ||
    typeof argument === "string" ||
    argStr === "[object String]"
  ) {
    // TODO: Can we get rid of as?
    return new Date(argument);
  } else {
    // TODO: Can we get rid of as?
    return new Date(NaN);
  }
}

/**
 * @name constructFrom
 * @category Generic Helpers
 * @summary Constructs a date using the reference date and the value
 *
 * @description
 * The function constructs a new date using the constructor from the reference
 * date and the given value. It helps to build generic functions that accept
 * date extensions.
 *
 * It defaults to `Date` if the passed reference date is a number or a string.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The reference date to take constructor from
 * @param value - The value to create the date
 *
 * @returns Date initialized using the given date and value
 *
 * @example
 * import { constructFrom } from 'date-fns'
 *
 * // A function that clones a date preserving the original type
 * function cloneDate<DateType extends Date(date: DateType): DateType {
 *   return constructFrom(
 *     date, // Use contrustor from the given date
 *     date.getTime() // Use the date value to create a new date
 *   )
 * }
 */
function constructFrom(date, value) {
  if (date instanceof Date) {
    return new date.constructor(value);
  } else {
    return new Date(value);
  }
}

/**
 * @module constants
 * @summary Useful constants
 * @description
 * Collection of useful date constants.
 *
 * The constants could be imported from `date-fns/constants`:
 *
 * ```ts
 * import { maxTime, minTime } from "./constants/date-fns/constants";
 *
 * function isAllowedTime(time) {
 *   return time <= maxTime && time >= minTime;
 * }
 * ```
 */


/**
 * @constant
 * @name millisecondsInWeek
 * @summary Milliseconds in 1 week.
 */
const millisecondsInWeek = 604800000;

/**
 * @constant
 * @name millisecondsInDay
 * @summary Milliseconds in 1 day.
 */
const millisecondsInDay = 86400000;

let defaultOptions = {};

function getDefaultOptions() {
  return defaultOptions;
}

/**
 * The {@link startOfWeek} function options.
 */

/**
 * @name startOfWeek
 * @category Week Helpers
 * @summary Return the start of a week for the given date.
 *
 * @description
 * Return the start of a week for the given date.
 * The result will be in the local timezone.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The original date
 * @param options - An object with options
 *
 * @returns The start of a week
 *
 * @example
 * // The start of a week for 2 September 2014 11:55:00:
 * const result = startOfWeek(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Sun Aug 31 2014 00:00:00
 *
 * @example
 * // If the week starts on Monday, the start of the week for 2 September 2014 11:55:00:
 * const result = startOfWeek(new Date(2014, 8, 2, 11, 55, 0), { weekStartsOn: 1 })
 * //=> Mon Sep 01 2014 00:00:00
 */
function startOfWeek(date, options) {
  const defaultOptions = getDefaultOptions();
  const weekStartsOn =
    options?.weekStartsOn ??
    options?.locale?.options?.weekStartsOn ??
    defaultOptions.weekStartsOn ??
    defaultOptions.locale?.options?.weekStartsOn ??
    0;

  const _date = toDate(date);
  const day = _date.getDay();
  const diff = (day < weekStartsOn ? 7 : 0) + day - weekStartsOn;

  _date.setDate(_date.getDate() - diff);
  _date.setHours(0, 0, 0, 0);
  return _date;
}

/**
 * @name startOfISOWeek
 * @category ISO Week Helpers
 * @summary Return the start of an ISO week for the given date.
 *
 * @description
 * Return the start of an ISO week for the given date.
 * The result will be in the local timezone.
 *
 * ISO week-numbering year: http://en.wikipedia.org/wiki/ISO_week_date
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The original date
 *
 * @returns The start of an ISO week
 *
 * @example
 * // The start of an ISO week for 2 September 2014 11:55:00:
 * const result = startOfISOWeek(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Mon Sep 01 2014 00:00:00
 */
function startOfISOWeek(date) {
  return startOfWeek(date, { weekStartsOn: 1 });
}

/**
 * @name getISOWeekYear
 * @category ISO Week-Numbering Year Helpers
 * @summary Get the ISO week-numbering year of the given date.
 *
 * @description
 * Get the ISO week-numbering year of the given date,
 * which always starts 3 days before the year's first Thursday.
 *
 * ISO week-numbering year: http://en.wikipedia.org/wiki/ISO_week_date
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The given date
 *
 * @returns The ISO week-numbering year
 *
 * @example
 * // Which ISO-week numbering year is 2 January 2005?
 * const result = getISOWeekYear(new Date(2005, 0, 2))
 * //=> 2004
 */
function getISOWeekYear(date) {
  const _date = toDate(date);
  const year = _date.getFullYear();

  const fourthOfJanuaryOfNextYear = constructFrom(date, 0);
  fourthOfJanuaryOfNextYear.setFullYear(year + 1, 0, 4);
  fourthOfJanuaryOfNextYear.setHours(0, 0, 0, 0);
  const startOfNextYear = startOfISOWeek(fourthOfJanuaryOfNextYear);

  const fourthOfJanuaryOfThisYear = constructFrom(date, 0);
  fourthOfJanuaryOfThisYear.setFullYear(year, 0, 4);
  fourthOfJanuaryOfThisYear.setHours(0, 0, 0, 0);
  const startOfThisYear = startOfISOWeek(fourthOfJanuaryOfThisYear);

  if (_date.getTime() >= startOfNextYear.getTime()) {
    return year + 1;
  } else if (_date.getTime() >= startOfThisYear.getTime()) {
    return year;
  } else {
    return year - 1;
  }
}

/**
 * @name startOfDay
 * @category Day Helpers
 * @summary Return the start of a day for the given date.
 *
 * @description
 * Return the start of a day for the given date.
 * The result will be in the local timezone.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The original date
 *
 * @returns The start of a day
 *
 * @example
 * // The start of a day for 2 September 2014 11:55:00:
 * const result = startOfDay(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Tue Sep 02 2014 00:00:00
 */
function startOfDay(date) {
  const _date = toDate(date);
  _date.setHours(0, 0, 0, 0);
  return _date;
}

/**
 * Google Chrome as of 67.0.3396.87 introduced timezones with offset that includes seconds.
 * They usually appear for dates that denote time before the timezones were introduced
 * (e.g. for 'Europe/Prague' timezone the offset is GMT+00:57:44 before 1 October 1891
 * and GMT+01:00:00 after that date)
 *
 * Date#getTimezoneOffset returns the offset in minutes and would return 57 for the example above,
 * which would lead to incorrect calculations.
 *
 * This function returns the timezone offset in milliseconds that takes seconds in account.
 */
function getTimezoneOffsetInMilliseconds(date) {
  const _date = toDate(date);
  const utcDate = new Date(
    Date.UTC(
      _date.getFullYear(),
      _date.getMonth(),
      _date.getDate(),
      _date.getHours(),
      _date.getMinutes(),
      _date.getSeconds(),
      _date.getMilliseconds(),
    ),
  );
  utcDate.setUTCFullYear(_date.getFullYear());
  return +date - +utcDate;
}

/**
 * @name differenceInCalendarDays
 * @category Day Helpers
 * @summary Get the number of calendar days between the given dates.
 *
 * @description
 * Get the number of calendar days between the given dates. This means that the times are removed
 * from the dates and then the difference in days is calculated.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param dateLeft - The later date
 * @param dateRight - The earlier date
 *
 * @returns The number of calendar days
 *
 * @example
 * // How many calendar days are between
 * // 2 July 2011 23:00:00 and 2 July 2012 00:00:00?
 * const result = differenceInCalendarDays(
 *   new Date(2012, 6, 2, 0, 0),
 *   new Date(2011, 6, 2, 23, 0)
 * )
 * //=> 366
 * // How many calendar days are between
 * // 2 July 2011 23:59:00 and 3 July 2011 00:01:00?
 * const result = differenceInCalendarDays(
 *   new Date(2011, 6, 3, 0, 1),
 *   new Date(2011, 6, 2, 23, 59)
 * )
 * //=> 1
 */
function differenceInCalendarDays(dateLeft, dateRight) {
  const startOfDayLeft = startOfDay(dateLeft);
  const startOfDayRight = startOfDay(dateRight);

  const timestampLeft =
    +startOfDayLeft - getTimezoneOffsetInMilliseconds(startOfDayLeft);
  const timestampRight =
    +startOfDayRight - getTimezoneOffsetInMilliseconds(startOfDayRight);

  // Round the number of days to the nearest integer because the number of
  // milliseconds in a day is not constant (e.g. it's different in the week of
  // the daylight saving time clock shift).
  return Math.round((timestampLeft - timestampRight) / millisecondsInDay);
}

/**
 * @name startOfISOWeekYear
 * @category ISO Week-Numbering Year Helpers
 * @summary Return the start of an ISO week-numbering year for the given date.
 *
 * @description
 * Return the start of an ISO week-numbering year,
 * which always starts 3 days before the year's first Thursday.
 * The result will be in the local timezone.
 *
 * ISO week-numbering year: http://en.wikipedia.org/wiki/ISO_week_date
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The original date
 *
 * @returns The start of an ISO week-numbering year
 *
 * @example
 * // The start of an ISO week-numbering year for 2 July 2005:
 * const result = startOfISOWeekYear(new Date(2005, 6, 2))
 * //=> Mon Jan 03 2005 00:00:00
 */
function startOfISOWeekYear(date) {
  const year = getISOWeekYear(date);
  const fourthOfJanuary = constructFrom(date, 0);
  fourthOfJanuary.setFullYear(year, 0, 4);
  fourthOfJanuary.setHours(0, 0, 0, 0);
  return startOfISOWeek(fourthOfJanuary);
}

/**
 * @name isDate
 * @category Common Helpers
 * @summary Is the given value a date?
 *
 * @description
 * Returns true if the given value is an instance of Date. The function works for dates transferred across iframes.
 *
 * @param value - The value to check
 *
 * @returns True if the given value is a date
 *
 * @example
 * // For a valid date:
 * const result = isDate(new Date())
 * //=> true
 *
 * @example
 * // For an invalid date:
 * const result = isDate(new Date(NaN))
 * //=> true
 *
 * @example
 * // For some value:
 * const result = isDate('2014-02-31')
 * //=> false
 *
 * @example
 * // For an object:
 * const result = isDate({})
 * //=> false
 */
function isDate(value) {
  return (
    value instanceof Date ||
    (typeof value === "object" &&
      Object.prototype.toString.call(value) === "[object Date]")
  );
}

/**
 * @name isValid
 * @category Common Helpers
 * @summary Is the given date valid?
 *
 * @description
 * Returns false if argument is Invalid Date and true otherwise.
 * Argument is converted to Date using `toDate`. See [toDate](https://date-fns.org/docs/toDate)
 * Invalid Date is a Date, whose time value is NaN.
 *
 * Time value of Date: http://es5.github.io/#x15.9.1.1
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The date to check
 *
 * @returns The date is valid
 *
 * @example
 * // For the valid date:
 * const result = isValid(new Date(2014, 1, 31))
 * //=> true
 *
 * @example
 * // For the value, convertable into a date:
 * const result = isValid(1393804800000)
 * //=> true
 *
 * @example
 * // For the invalid date:
 * const result = isValid(new Date(''))
 * //=> false
 */
function isValid(date) {
  if (!isDate(date) && typeof date !== "number") {
    return false;
  }
  const _date = toDate(date);
  return !isNaN(Number(_date));
}

/**
 * @name startOfYear
 * @category Year Helpers
 * @summary Return the start of a year for the given date.
 *
 * @description
 * Return the start of a year for the given date.
 * The result will be in the local timezone.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The original date
 *
 * @returns The start of a year
 *
 * @example
 * // The start of a year for 2 September 2014 11:55:00:
 * const result = startOfYear(new Date(2014, 8, 2, 11, 55, 00))
 * //=> Wed Jan 01 2014 00:00:00
 */
function startOfYear(date) {
  const cleanDate = toDate(date);
  const _date = constructFrom(date, 0);
  _date.setFullYear(cleanDate.getFullYear(), 0, 1);
  _date.setHours(0, 0, 0, 0);
  return _date;
}

const formatDistanceLocale = {
  lessThanXSeconds: {
    one: "less than a second",
    other: "less than {{count}} seconds",
  },

  xSeconds: {
    one: "1 second",
    other: "{{count}} seconds",
  },

  halfAMinute: "half a minute",

  lessThanXMinutes: {
    one: "less than a minute",
    other: "less than {{count}} minutes",
  },

  xMinutes: {
    one: "1 minute",
    other: "{{count}} minutes",
  },

  aboutXHours: {
    one: "about 1 hour",
    other: "about {{count}} hours",
  },

  xHours: {
    one: "1 hour",
    other: "{{count}} hours",
  },

  xDays: {
    one: "1 day",
    other: "{{count}} days",
  },

  aboutXWeeks: {
    one: "about 1 week",
    other: "about {{count}} weeks",
  },

  xWeeks: {
    one: "1 week",
    other: "{{count}} weeks",
  },

  aboutXMonths: {
    one: "about 1 month",
    other: "about {{count}} months",
  },

  xMonths: {
    one: "1 month",
    other: "{{count}} months",
  },

  aboutXYears: {
    one: "about 1 year",
    other: "about {{count}} years",
  },

  xYears: {
    one: "1 year",
    other: "{{count}} years",
  },

  overXYears: {
    one: "over 1 year",
    other: "over {{count}} years",
  },

  almostXYears: {
    one: "almost 1 year",
    other: "almost {{count}} years",
  },
};

const formatDistance = (token, count, options) => {
  let result;

  const tokenValue = formatDistanceLocale[token];
  if (typeof tokenValue === "string") {
    result = tokenValue;
  } else if (count === 1) {
    result = tokenValue.one;
  } else {
    result = tokenValue.other.replace("{{count}}", count.toString());
  }

  if (options?.addSuffix) {
    if (options.comparison && options.comparison > 0) {
      return "in " + result;
    } else {
      return result + " ago";
    }
  }

  return result;
};

function buildFormatLongFn(args) {
  return (options = {}) => {
    // TODO: Remove String()
    const width = options.width ? String(options.width) : args.defaultWidth;
    const format = args.formats[width] || args.formats[args.defaultWidth];
    return format;
  };
}

const dateFormats = {
  full: "EEEE, MMMM do, y",
  long: "MMMM do, y",
  medium: "MMM d, y",
  short: "MM/dd/yyyy",
};

const timeFormats = {
  full: "h:mm:ss a zzzz",
  long: "h:mm:ss a z",
  medium: "h:mm:ss a",
  short: "h:mm a",
};

const dateTimeFormats = {
  full: "{{date}} 'at' {{time}}",
  long: "{{date}} 'at' {{time}}",
  medium: "{{date}}, {{time}}",
  short: "{{date}}, {{time}}",
};

const formatLong = {
  date: buildFormatLongFn({
    formats: dateFormats,
    defaultWidth: "full",
  }),

  time: buildFormatLongFn({
    formats: timeFormats,
    defaultWidth: "full",
  }),

  dateTime: buildFormatLongFn({
    formats: dateTimeFormats,
    defaultWidth: "full",
  }),
};

const formatRelativeLocale = {
  lastWeek: "'last' eeee 'at' p",
  yesterday: "'yesterday at' p",
  today: "'today at' p",
  tomorrow: "'tomorrow at' p",
  nextWeek: "eeee 'at' p",
  other: "P",
};

const formatRelative = (token, _date, _baseDate, _options) =>
  formatRelativeLocale[token];

/* eslint-disable no-unused-vars */

/**
 * The localize function argument callback which allows to convert raw value to
 * the actual type.
 *
 * @param value - The value to convert
 *
 * @returns The converted value
 */

/**
 * The map of localized values for each width.
 */

/**
 * The index type of the locale unit value. It types conversion of units of
 * values that don't start at 0 (i.e. quarters).
 */

/**
 * Converts the unit value to the tuple of values.
 */

/**
 * The tuple of localized era values. The first element represents BC,
 * the second element represents AD.
 */

/**
 * The tuple of localized quarter values. The first element represents Q1.
 */

/**
 * The tuple of localized day values. The first element represents Sunday.
 */

/**
 * The tuple of localized month values. The first element represents January.
 */

function buildLocalizeFn(args) {
  return (value, options) => {
    const context = options?.context ? String(options.context) : "standalone";

    let valuesArray;
    if (context === "formatting" && args.formattingValues) {
      const defaultWidth = args.defaultFormattingWidth || args.defaultWidth;
      const width = options?.width ? String(options.width) : defaultWidth;

      valuesArray =
        args.formattingValues[width] || args.formattingValues[defaultWidth];
    } else {
      const defaultWidth = args.defaultWidth;
      const width = options?.width ? String(options.width) : args.defaultWidth;

      valuesArray = args.values[width] || args.values[defaultWidth];
    }
    const index = args.argumentCallback ? args.argumentCallback(value) : value;

    // @ts-expect-error - For some reason TypeScript just don't want to match it, no matter how hard we try. I challenge you to try to remove it!
    return valuesArray[index];
  };
}

const eraValues = {
  narrow: ["B", "A"],
  abbreviated: ["BC", "AD"],
  wide: ["Before Christ", "Anno Domini"],
};

const quarterValues = {
  narrow: ["1", "2", "3", "4"],
  abbreviated: ["Q1", "Q2", "Q3", "Q4"],
  wide: ["1st quarter", "2nd quarter", "3rd quarter", "4th quarter"],
};

// Note: in English, the names of days of the week and months are capitalized.
// If you are making a new locale based on this one, check if the same is true for the language you're working on.
// Generally, formatted dates should look like they are in the middle of a sentence,
// e.g. in Spanish language the weekdays and months should be in the lowercase.
const monthValues = {
  narrow: ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"],
  abbreviated: [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ],

  wide: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ],
};

const dayValues = {
  narrow: ["S", "M", "T", "W", "T", "F", "S"],
  short: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
  abbreviated: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
  wide: [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ],
};

const dayPeriodValues = {
  narrow: {
    am: "a",
    pm: "p",
    midnight: "mi",
    noon: "n",
    morning: "morning",
    afternoon: "afternoon",
    evening: "evening",
    night: "night",
  },
  abbreviated: {
    am: "AM",
    pm: "PM",
    midnight: "midnight",
    noon: "noon",
    morning: "morning",
    afternoon: "afternoon",
    evening: "evening",
    night: "night",
  },
  wide: {
    am: "a.m.",
    pm: "p.m.",
    midnight: "midnight",
    noon: "noon",
    morning: "morning",
    afternoon: "afternoon",
    evening: "evening",
    night: "night",
  },
};

const formattingDayPeriodValues = {
  narrow: {
    am: "a",
    pm: "p",
    midnight: "mi",
    noon: "n",
    morning: "in the morning",
    afternoon: "in the afternoon",
    evening: "in the evening",
    night: "at night",
  },
  abbreviated: {
    am: "AM",
    pm: "PM",
    midnight: "midnight",
    noon: "noon",
    morning: "in the morning",
    afternoon: "in the afternoon",
    evening: "in the evening",
    night: "at night",
  },
  wide: {
    am: "a.m.",
    pm: "p.m.",
    midnight: "midnight",
    noon: "noon",
    morning: "in the morning",
    afternoon: "in the afternoon",
    evening: "in the evening",
    night: "at night",
  },
};

const ordinalNumber = (dirtyNumber, _options) => {
  const number = Number(dirtyNumber);

  // If ordinal numbers depend on context, for example,
  // if they are different for different grammatical genders,
  // use `options.unit`.
  //
  // `unit` can be 'year', 'quarter', 'month', 'week', 'date', 'dayOfYear',
  // 'day', 'hour', 'minute', 'second'.

  const rem100 = number % 100;
  if (rem100 > 20 || rem100 < 10) {
    switch (rem100 % 10) {
      case 1:
        return number + "st";
      case 2:
        return number + "nd";
      case 3:
        return number + "rd";
    }
  }
  return number + "th";
};

const localize = {
  ordinalNumber,

  era: buildLocalizeFn({
    values: eraValues,
    defaultWidth: "wide",
  }),

  quarter: buildLocalizeFn({
    values: quarterValues,
    defaultWidth: "wide",
    argumentCallback: (quarter) => quarter - 1,
  }),

  month: buildLocalizeFn({
    values: monthValues,
    defaultWidth: "wide",
  }),

  day: buildLocalizeFn({
    values: dayValues,
    defaultWidth: "wide",
  }),

  dayPeriod: buildLocalizeFn({
    values: dayPeriodValues,
    defaultWidth: "wide",
    formattingValues: formattingDayPeriodValues,
    defaultFormattingWidth: "wide",
  }),
};

function buildMatchFn(args) {
  return (string, options = {}) => {
    const width = options.width;

    const matchPattern =
      (width && args.matchPatterns[width]) ||
      args.matchPatterns[args.defaultMatchWidth];
    const matchResult = string.match(matchPattern);

    if (!matchResult) {
      return null;
    }
    const matchedString = matchResult[0];

    const parsePatterns =
      (width && args.parsePatterns[width]) ||
      args.parsePatterns[args.defaultParseWidth];

    const key = Array.isArray(parsePatterns)
      ? findIndex(parsePatterns, (pattern) => pattern.test(matchedString))
      : // eslint-disable-next-line @typescript-eslint/no-explicit-any -- I challange you to fix the type
        findKey(parsePatterns, (pattern) => pattern.test(matchedString));

    let value;

    value = args.valueCallback ? args.valueCallback(key) : key;
    value = options.valueCallback
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any -- I challange you to fix the type
        options.valueCallback(value)
      : value;

    const rest = string.slice(matchedString.length);

    return { value, rest };
  };
}

function findKey(object, predicate) {
  for (const key in object) {
    if (
      Object.prototype.hasOwnProperty.call(object, key) &&
      predicate(object[key])
    ) {
      return key;
    }
  }
  return undefined;
}

function findIndex(array, predicate) {
  for (let key = 0; key < array.length; key++) {
    if (predicate(array[key])) {
      return key;
    }
  }
  return undefined;
}

function buildMatchPatternFn(args) {
  return (string, options = {}) => {
    const matchResult = string.match(args.matchPattern);
    if (!matchResult) return null;
    const matchedString = matchResult[0];

    const parseResult = string.match(args.parsePattern);
    if (!parseResult) return null;
    let value = args.valueCallback
      ? args.valueCallback(parseResult[0])
      : parseResult[0];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- I challange you to fix the type
    value = options.valueCallback ? options.valueCallback(value) : value;

    const rest = string.slice(matchedString.length);

    return { value, rest };
  };
}

const matchOrdinalNumberPattern = /^(\d+)(th|st|nd|rd)?/i;
const parseOrdinalNumberPattern = /\d+/i;

const matchEraPatterns = {
  narrow: /^(b|a)/i,
  abbreviated: /^(b\.?\s?c\.?|b\.?\s?c\.?\s?e\.?|a\.?\s?d\.?|c\.?\s?e\.?)/i,
  wide: /^(before christ|before common era|anno domini|common era)/i,
};
const parseEraPatterns = {
  any: [/^b/i, /^(a|c)/i],
};

const matchQuarterPatterns = {
  narrow: /^[1234]/i,
  abbreviated: /^q[1234]/i,
  wide: /^[1234](th|st|nd|rd)? quarter/i,
};
const parseQuarterPatterns = {
  any: [/1/i, /2/i, /3/i, /4/i],
};

const matchMonthPatterns = {
  narrow: /^[jfmasond]/i,
  abbreviated: /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i,
  wide: /^(january|february|march|april|may|june|july|august|september|october|november|december)/i,
};
const parseMonthPatterns = {
  narrow: [
    /^j/i,
    /^f/i,
    /^m/i,
    /^a/i,
    /^m/i,
    /^j/i,
    /^j/i,
    /^a/i,
    /^s/i,
    /^o/i,
    /^n/i,
    /^d/i,
  ],

  any: [
    /^ja/i,
    /^f/i,
    /^mar/i,
    /^ap/i,
    /^may/i,
    /^jun/i,
    /^jul/i,
    /^au/i,
    /^s/i,
    /^o/i,
    /^n/i,
    /^d/i,
  ],
};

const matchDayPatterns = {
  narrow: /^[smtwf]/i,
  short: /^(su|mo|tu|we|th|fr|sa)/i,
  abbreviated: /^(sun|mon|tue|wed|thu|fri|sat)/i,
  wide: /^(sunday|monday|tuesday|wednesday|thursday|friday|saturday)/i,
};
const parseDayPatterns = {
  narrow: [/^s/i, /^m/i, /^t/i, /^w/i, /^t/i, /^f/i, /^s/i],
  any: [/^su/i, /^m/i, /^tu/i, /^w/i, /^th/i, /^f/i, /^sa/i],
};

const matchDayPeriodPatterns = {
  narrow: /^(a|p|mi|n|(in the|at) (morning|afternoon|evening|night))/i,
  any: /^([ap]\.?\s?m\.?|midnight|noon|(in the|at) (morning|afternoon|evening|night))/i,
};
const parseDayPeriodPatterns = {
  any: {
    am: /^a/i,
    pm: /^p/i,
    midnight: /^mi/i,
    noon: /^no/i,
    morning: /morning/i,
    afternoon: /afternoon/i,
    evening: /evening/i,
    night: /night/i,
  },
};

const match = {
  ordinalNumber: buildMatchPatternFn({
    matchPattern: matchOrdinalNumberPattern,
    parsePattern: parseOrdinalNumberPattern,
    valueCallback: (value) => parseInt(value, 10),
  }),

  era: buildMatchFn({
    matchPatterns: matchEraPatterns,
    defaultMatchWidth: "wide",
    parsePatterns: parseEraPatterns,
    defaultParseWidth: "any",
  }),

  quarter: buildMatchFn({
    matchPatterns: matchQuarterPatterns,
    defaultMatchWidth: "wide",
    parsePatterns: parseQuarterPatterns,
    defaultParseWidth: "any",
    valueCallback: (index) => index + 1,
  }),

  month: buildMatchFn({
    matchPatterns: matchMonthPatterns,
    defaultMatchWidth: "wide",
    parsePatterns: parseMonthPatterns,
    defaultParseWidth: "any",
  }),

  day: buildMatchFn({
    matchPatterns: matchDayPatterns,
    defaultMatchWidth: "wide",
    parsePatterns: parseDayPatterns,
    defaultParseWidth: "any",
  }),

  dayPeriod: buildMatchFn({
    matchPatterns: matchDayPeriodPatterns,
    defaultMatchWidth: "any",
    parsePatterns: parseDayPeriodPatterns,
    defaultParseWidth: "any",
  }),
};

/**
 * @category Locales
 * @summary English locale (United States).
 * @language English
 * @iso-639-2 eng
 * @author Sasha Koss [@kossnocorp](https://github.com/kossnocorp)
 * @author Lesha Koss [@leshakoss](https://github.com/leshakoss)
 */
const enUS = {
  code: "en-US",
  formatDistance: formatDistance,
  formatLong: formatLong,
  formatRelative: formatRelative,
  localize: localize,
  match: match,
  options: {
    weekStartsOn: 0 /* Sunday */,
    firstWeekContainsDate: 1,
  },
};

/**
 * @name getDayOfYear
 * @category Day Helpers
 * @summary Get the day of the year of the given date.
 *
 * @description
 * Get the day of the year of the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The given date
 *
 * @returns The day of year
 *
 * @example
 * // Which day of the year is 2 July 2014?
 * const result = getDayOfYear(new Date(2014, 6, 2))
 * //=> 183
 */
function getDayOfYear(date) {
  const _date = toDate(date);
  const diff = differenceInCalendarDays(_date, startOfYear(_date));
  const dayOfYear = diff + 1;
  return dayOfYear;
}

/**
 * @name getISOWeek
 * @category ISO Week Helpers
 * @summary Get the ISO week of the given date.
 *
 * @description
 * Get the ISO week of the given date.
 *
 * ISO week-numbering year: http://en.wikipedia.org/wiki/ISO_week_date
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The given date
 *
 * @returns The ISO week
 *
 * @example
 * // Which week of the ISO-week numbering year is 2 January 2005?
 * const result = getISOWeek(new Date(2005, 0, 2))
 * //=> 53
 */
function getISOWeek(date) {
  const _date = toDate(date);
  const diff = +startOfISOWeek(_date) - +startOfISOWeekYear(_date);

  // Round the number of weeks to the nearest integer because the number of
  // milliseconds in a week is not constant (e.g. it's different in the week of
  // the daylight saving time clock shift).
  return Math.round(diff / millisecondsInWeek) + 1;
}

/**
 * The {@link getWeekYear} function options.
 */

/**
 * @name getWeekYear
 * @category Week-Numbering Year Helpers
 * @summary Get the local week-numbering year of the given date.
 *
 * @description
 * Get the local week-numbering year of the given date.
 * The exact calculation depends on the values of
 * `options.weekStartsOn` (which is the index of the first day of the week)
 * and `options.firstWeekContainsDate` (which is the day of January, which is always in
 * the first week of the week-numbering year)
 *
 * Week numbering: https://en.wikipedia.org/wiki/Week#The_ISO_week_date_system
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The given date
 * @param options - An object with options.
 *
 * @returns The local week-numbering year
 *
 * @example
 * // Which week numbering year is 26 December 2004 with the default settings?
 * const result = getWeekYear(new Date(2004, 11, 26))
 * //=> 2005
 *
 * @example
 * // Which week numbering year is 26 December 2004 if week starts on Saturday?
 * const result = getWeekYear(new Date(2004, 11, 26), { weekStartsOn: 6 })
 * //=> 2004
 *
 * @example
 * // Which week numbering year is 26 December 2004 if the first week contains 4 January?
 * const result = getWeekYear(new Date(2004, 11, 26), { firstWeekContainsDate: 4 })
 * //=> 2004
 */
function getWeekYear(date, options) {
  const _date = toDate(date);
  const year = _date.getFullYear();

  const defaultOptions = getDefaultOptions();
  const firstWeekContainsDate =
    options?.firstWeekContainsDate ??
    options?.locale?.options?.firstWeekContainsDate ??
    defaultOptions.firstWeekContainsDate ??
    defaultOptions.locale?.options?.firstWeekContainsDate ??
    1;

  const firstWeekOfNextYear = constructFrom(date, 0);
  firstWeekOfNextYear.setFullYear(year + 1, 0, firstWeekContainsDate);
  firstWeekOfNextYear.setHours(0, 0, 0, 0);
  const startOfNextYear = startOfWeek(firstWeekOfNextYear, options);

  const firstWeekOfThisYear = constructFrom(date, 0);
  firstWeekOfThisYear.setFullYear(year, 0, firstWeekContainsDate);
  firstWeekOfThisYear.setHours(0, 0, 0, 0);
  const startOfThisYear = startOfWeek(firstWeekOfThisYear, options);

  if (_date.getTime() >= startOfNextYear.getTime()) {
    return year + 1;
  } else if (_date.getTime() >= startOfThisYear.getTime()) {
    return year;
  } else {
    return year - 1;
  }
}

/**
 * The {@link startOfWeekYear} function options.
 */

/**
 * @name startOfWeekYear
 * @category Week-Numbering Year Helpers
 * @summary Return the start of a local week-numbering year for the given date.
 *
 * @description
 * Return the start of a local week-numbering year.
 * The exact calculation depends on the values of
 * `options.weekStartsOn` (which is the index of the first day of the week)
 * and `options.firstWeekContainsDate` (which is the day of January, which is always in
 * the first week of the week-numbering year)
 *
 * Week numbering: https://en.wikipedia.org/wiki/Week#The_ISO_week_date_system
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The original date
 * @param options - An object with options
 *
 * @returns The start of a week-numbering year
 *
 * @example
 * // The start of an a week-numbering year for 2 July 2005 with default settings:
 * const result = startOfWeekYear(new Date(2005, 6, 2))
 * //=> Sun Dec 26 2004 00:00:00
 *
 * @example
 * // The start of a week-numbering year for 2 July 2005
 * // if Monday is the first day of week
 * // and 4 January is always in the first week of the year:
 * const result = startOfWeekYear(new Date(2005, 6, 2), {
 *   weekStartsOn: 1,
 *   firstWeekContainsDate: 4
 * })
 * //=> Mon Jan 03 2005 00:00:00
 */
function startOfWeekYear(date, options) {
  const defaultOptions = getDefaultOptions();
  const firstWeekContainsDate =
    options?.firstWeekContainsDate ??
    options?.locale?.options?.firstWeekContainsDate ??
    defaultOptions.firstWeekContainsDate ??
    defaultOptions.locale?.options?.firstWeekContainsDate ??
    1;

  const year = getWeekYear(date, options);
  const firstWeek = constructFrom(date, 0);
  firstWeek.setFullYear(year, 0, firstWeekContainsDate);
  firstWeek.setHours(0, 0, 0, 0);
  const _date = startOfWeek(firstWeek, options);
  return _date;
}

/**
 * The {@link getWeek} function options.
 */

/**
 * @name getWeek
 * @category Week Helpers
 * @summary Get the local week index of the given date.
 *
 * @description
 * Get the local week index of the given date.
 * The exact calculation depends on the values of
 * `options.weekStartsOn` (which is the index of the first day of the week)
 * and `options.firstWeekContainsDate` (which is the day of January, which is always in
 * the first week of the week-numbering year)
 *
 * Week numbering: https://en.wikipedia.org/wiki/Week#The_ISO_week_date_system
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The given date
 * @param options - An object with options
 *
 * @returns The week
 *
 * @example
 * // Which week of the local week numbering year is 2 January 2005 with default options?
 * const result = getWeek(new Date(2005, 0, 2))
 * //=> 2
 *
 * @example
 * // Which week of the local week numbering year is 2 January 2005,
 * // if Monday is the first day of the week,
 * // and the first week of the year always contains 4 January?
 * const result = getWeek(new Date(2005, 0, 2), {
 *   weekStartsOn: 1,
 *   firstWeekContainsDate: 4
 * })
 * //=> 53
 */

function getWeek(date, options) {
  const _date = toDate(date);
  const diff = +startOfWeek(_date, options) - +startOfWeekYear(_date, options);

  // Round the number of weeks to the nearest integer because the number of
  // milliseconds in a week is not constant (e.g. it's different in the week of
  // the daylight saving time clock shift).
  return Math.round(diff / millisecondsInWeek) + 1;
}

function addLeadingZeros(number, targetLength) {
  const sign = number < 0 ? "-" : "";
  const output = Math.abs(number).toString().padStart(targetLength, "0");
  return sign + output;
}

/*
 * |     | Unit                           |     | Unit                           |
 * |-----|--------------------------------|-----|--------------------------------|
 * |  a  | AM, PM                         |  A* |                                |
 * |  d  | Day of month                   |  D  |                                |
 * |  h  | Hour [1-12]                    |  H  | Hour [0-23]                    |
 * |  m  | Minute                         |  M  | Month                          |
 * |  s  | Second                         |  S  | Fraction of second             |
 * |  y  | Year (abs)                     |  Y  |                                |
 *
 * Letters marked by * are not implemented but reserved by Unicode standard.
 */

const lightFormatters = {
  // Year
  y(date, token) {
    // From http://www.unicode.org/reports/tr35/tr35-31/tr35-dates.html#Date_Format_tokens
    // | Year     |     y | yy |   yyy |  yyyy | yyyyy |
    // |----------|-------|----|-------|-------|-------|
    // | AD 1     |     1 | 01 |   001 |  0001 | 00001 |
    // | AD 12    |    12 | 12 |   012 |  0012 | 00012 |
    // | AD 123   |   123 | 23 |   123 |  0123 | 00123 |
    // | AD 1234  |  1234 | 34 |  1234 |  1234 | 01234 |
    // | AD 12345 | 12345 | 45 | 12345 | 12345 | 12345 |

    const signedYear = date.getFullYear();
    // Returns 1 for 1 BC (which is year 0 in JavaScript)
    const year = signedYear > 0 ? signedYear : 1 - signedYear;
    return addLeadingZeros(token === "yy" ? year % 100 : year, token.length);
  },

  // Month
  M(date, token) {
    const month = date.getMonth();
    return token === "M" ? String(month + 1) : addLeadingZeros(month + 1, 2);
  },

  // Day of the month
  d(date, token) {
    return addLeadingZeros(date.getDate(), token.length);
  },

  // AM or PM
  a(date, token) {
    const dayPeriodEnumValue = date.getHours() / 12 >= 1 ? "pm" : "am";

    switch (token) {
      case "a":
      case "aa":
        return dayPeriodEnumValue.toUpperCase();
      case "aaa":
        return dayPeriodEnumValue;
      case "aaaaa":
        return dayPeriodEnumValue[0];
      case "aaaa":
      default:
        return dayPeriodEnumValue === "am" ? "a.m." : "p.m.";
    }
  },

  // Hour [1-12]
  h(date, token) {
    return addLeadingZeros(date.getHours() % 12 || 12, token.length);
  },

  // Hour [0-23]
  H(date, token) {
    return addLeadingZeros(date.getHours(), token.length);
  },

  // Minute
  m(date, token) {
    return addLeadingZeros(date.getMinutes(), token.length);
  },

  // Second
  s(date, token) {
    return addLeadingZeros(date.getSeconds(), token.length);
  },

  // Fraction of second
  S(date, token) {
    const numberOfDigits = token.length;
    const milliseconds = date.getMilliseconds();
    const fractionalSeconds = Math.trunc(
      milliseconds * Math.pow(10, numberOfDigits - 3),
    );
    return addLeadingZeros(fractionalSeconds, token.length);
  },
};

const dayPeriodEnum = {
  midnight: "midnight",
  noon: "noon",
  morning: "morning",
  afternoon: "afternoon",
  evening: "evening",
  night: "night",
};

/*
 * |     | Unit                           |     | Unit                           |
 * |-----|--------------------------------|-----|--------------------------------|
 * |  a  | AM, PM                         |  A* | Milliseconds in day            |
 * |  b  | AM, PM, noon, midnight         |  B  | Flexible day period            |
 * |  c  | Stand-alone local day of week  |  C* | Localized hour w/ day period   |
 * |  d  | Day of month                   |  D  | Day of year                    |
 * |  e  | Local day of week              |  E  | Day of week                    |
 * |  f  |                                |  F* | Day of week in month           |
 * |  g* | Modified Julian day            |  G  | Era                            |
 * |  h  | Hour [1-12]                    |  H  | Hour [0-23]                    |
 * |  i! | ISO day of week                |  I! | ISO week of year               |
 * |  j* | Localized hour w/ day period   |  J* | Localized hour w/o day period  |
 * |  k  | Hour [1-24]                    |  K  | Hour [0-11]                    |
 * |  l* | (deprecated)                   |  L  | Stand-alone month              |
 * |  m  | Minute                         |  M  | Month                          |
 * |  n  |                                |  N  |                                |
 * |  o! | Ordinal number modifier        |  O  | Timezone (GMT)                 |
 * |  p! | Long localized time            |  P! | Long localized date            |
 * |  q  | Stand-alone quarter            |  Q  | Quarter                        |
 * |  r* | Related Gregorian year         |  R! | ISO week-numbering year        |
 * |  s  | Second                         |  S  | Fraction of second             |
 * |  t! | Seconds timestamp              |  T! | Milliseconds timestamp         |
 * |  u  | Extended year                  |  U* | Cyclic year                    |
 * |  v* | Timezone (generic non-locat.)  |  V* | Timezone (location)            |
 * |  w  | Local week of year             |  W* | Week of month                  |
 * |  x  | Timezone (ISO-8601 w/o Z)      |  X  | Timezone (ISO-8601)            |
 * |  y  | Year (abs)                     |  Y  | Local week-numbering year      |
 * |  z  | Timezone (specific non-locat.) |  Z* | Timezone (aliases)             |
 *
 * Letters marked by * are not implemented but reserved by Unicode standard.
 *
 * Letters marked by ! are non-standard, but implemented by date-fns:
 * - `o` modifies the previous token to turn it into an ordinal (see `format` docs)
 * - `i` is ISO day of week. For `i` and `ii` is returns numeric ISO week days,
 *   i.e. 7 for Sunday, 1 for Monday, etc.
 * - `I` is ISO week of year, as opposed to `w` which is local week of year.
 * - `R` is ISO week-numbering year, as opposed to `Y` which is local week-numbering year.
 *   `R` is supposed to be used in conjunction with `I` and `i`
 *   for universal ISO week-numbering date, whereas
 *   `Y` is supposed to be used in conjunction with `w` and `e`
 *   for week-numbering date specific to the locale.
 * - `P` is long localized date format
 * - `p` is long localized time format
 */

const formatters = {
  // Era
  G: function (date, token, localize) {
    const era = date.getFullYear() > 0 ? 1 : 0;
    switch (token) {
      // AD, BC
      case "G":
      case "GG":
      case "GGG":
        return localize.era(era, { width: "abbreviated" });
      // A, B
      case "GGGGG":
        return localize.era(era, { width: "narrow" });
      // Anno Domini, Before Christ
      case "GGGG":
      default:
        return localize.era(era, { width: "wide" });
    }
  },

  // Year
  y: function (date, token, localize) {
    // Ordinal number
    if (token === "yo") {
      const signedYear = date.getFullYear();
      // Returns 1 for 1 BC (which is year 0 in JavaScript)
      const year = signedYear > 0 ? signedYear : 1 - signedYear;
      return localize.ordinalNumber(year, { unit: "year" });
    }

    return lightFormatters.y(date, token);
  },

  // Local week-numbering year
  Y: function (date, token, localize, options) {
    const signedWeekYear = getWeekYear(date, options);
    // Returns 1 for 1 BC (which is year 0 in JavaScript)
    const weekYear = signedWeekYear > 0 ? signedWeekYear : 1 - signedWeekYear;

    // Two digit year
    if (token === "YY") {
      const twoDigitYear = weekYear % 100;
      return addLeadingZeros(twoDigitYear, 2);
    }

    // Ordinal number
    if (token === "Yo") {
      return localize.ordinalNumber(weekYear, { unit: "year" });
    }

    // Padding
    return addLeadingZeros(weekYear, token.length);
  },

  // ISO week-numbering year
  R: function (date, token) {
    const isoWeekYear = getISOWeekYear(date);

    // Padding
    return addLeadingZeros(isoWeekYear, token.length);
  },

  // Extended year. This is a single number designating the year of this calendar system.
  // The main difference between `y` and `u` localizers are B.C. years:
  // | Year | `y` | `u` |
  // |------|-----|-----|
  // | AC 1 |   1 |   1 |
  // | BC 1 |   1 |   0 |
  // | BC 2 |   2 |  -1 |
  // Also `yy` always returns the last two digits of a year,
  // while `uu` pads single digit years to 2 characters and returns other years unchanged.
  u: function (date, token) {
    const year = date.getFullYear();
    return addLeadingZeros(year, token.length);
  },

  // Quarter
  Q: function (date, token, localize) {
    const quarter = Math.ceil((date.getMonth() + 1) / 3);
    switch (token) {
      // 1, 2, 3, 4
      case "Q":
        return String(quarter);
      // 01, 02, 03, 04
      case "QQ":
        return addLeadingZeros(quarter, 2);
      // 1st, 2nd, 3rd, 4th
      case "Qo":
        return localize.ordinalNumber(quarter, { unit: "quarter" });
      // Q1, Q2, Q3, Q4
      case "QQQ":
        return localize.quarter(quarter, {
          width: "abbreviated",
          context: "formatting",
        });
      // 1, 2, 3, 4 (narrow quarter; could be not numerical)
      case "QQQQQ":
        return localize.quarter(quarter, {
          width: "narrow",
          context: "formatting",
        });
      // 1st quarter, 2nd quarter, ...
      case "QQQQ":
      default:
        return localize.quarter(quarter, {
          width: "wide",
          context: "formatting",
        });
    }
  },

  // Stand-alone quarter
  q: function (date, token, localize) {
    const quarter = Math.ceil((date.getMonth() + 1) / 3);
    switch (token) {
      // 1, 2, 3, 4
      case "q":
        return String(quarter);
      // 01, 02, 03, 04
      case "qq":
        return addLeadingZeros(quarter, 2);
      // 1st, 2nd, 3rd, 4th
      case "qo":
        return localize.ordinalNumber(quarter, { unit: "quarter" });
      // Q1, Q2, Q3, Q4
      case "qqq":
        return localize.quarter(quarter, {
          width: "abbreviated",
          context: "standalone",
        });
      // 1, 2, 3, 4 (narrow quarter; could be not numerical)
      case "qqqqq":
        return localize.quarter(quarter, {
          width: "narrow",
          context: "standalone",
        });
      // 1st quarter, 2nd quarter, ...
      case "qqqq":
      default:
        return localize.quarter(quarter, {
          width: "wide",
          context: "standalone",
        });
    }
  },

  // Month
  M: function (date, token, localize) {
    const month = date.getMonth();
    switch (token) {
      case "M":
      case "MM":
        return lightFormatters.M(date, token);
      // 1st, 2nd, ..., 12th
      case "Mo":
        return localize.ordinalNumber(month + 1, { unit: "month" });
      // Jan, Feb, ..., Dec
      case "MMM":
        return localize.month(month, {
          width: "abbreviated",
          context: "formatting",
        });
      // J, F, ..., D
      case "MMMMM":
        return localize.month(month, {
          width: "narrow",
          context: "formatting",
        });
      // January, February, ..., December
      case "MMMM":
      default:
        return localize.month(month, { width: "wide", context: "formatting" });
    }
  },

  // Stand-alone month
  L: function (date, token, localize) {
    const month = date.getMonth();
    switch (token) {
      // 1, 2, ..., 12
      case "L":
        return String(month + 1);
      // 01, 02, ..., 12
      case "LL":
        return addLeadingZeros(month + 1, 2);
      // 1st, 2nd, ..., 12th
      case "Lo":
        return localize.ordinalNumber(month + 1, { unit: "month" });
      // Jan, Feb, ..., Dec
      case "LLL":
        return localize.month(month, {
          width: "abbreviated",
          context: "standalone",
        });
      // J, F, ..., D
      case "LLLLL":
        return localize.month(month, {
          width: "narrow",
          context: "standalone",
        });
      // January, February, ..., December
      case "LLLL":
      default:
        return localize.month(month, { width: "wide", context: "standalone" });
    }
  },

  // Local week of year
  w: function (date, token, localize, options) {
    const week = getWeek(date, options);

    if (token === "wo") {
      return localize.ordinalNumber(week, { unit: "week" });
    }

    return addLeadingZeros(week, token.length);
  },

  // ISO week of year
  I: function (date, token, localize) {
    const isoWeek = getISOWeek(date);

    if (token === "Io") {
      return localize.ordinalNumber(isoWeek, { unit: "week" });
    }

    return addLeadingZeros(isoWeek, token.length);
  },

  // Day of the month
  d: function (date, token, localize) {
    if (token === "do") {
      return localize.ordinalNumber(date.getDate(), { unit: "date" });
    }

    return lightFormatters.d(date, token);
  },

  // Day of year
  D: function (date, token, localize) {
    const dayOfYear = getDayOfYear(date);

    if (token === "Do") {
      return localize.ordinalNumber(dayOfYear, { unit: "dayOfYear" });
    }

    return addLeadingZeros(dayOfYear, token.length);
  },

  // Day of week
  E: function (date, token, localize) {
    const dayOfWeek = date.getDay();
    switch (token) {
      // Tue
      case "E":
      case "EE":
      case "EEE":
        return localize.day(dayOfWeek, {
          width: "abbreviated",
          context: "formatting",
        });
      // T
      case "EEEEE":
        return localize.day(dayOfWeek, {
          width: "narrow",
          context: "formatting",
        });
      // Tu
      case "EEEEEE":
        return localize.day(dayOfWeek, {
          width: "short",
          context: "formatting",
        });
      // Tuesday
      case "EEEE":
      default:
        return localize.day(dayOfWeek, {
          width: "wide",
          context: "formatting",
        });
    }
  },

  // Local day of week
  e: function (date, token, localize, options) {
    const dayOfWeek = date.getDay();
    const localDayOfWeek = (dayOfWeek - options.weekStartsOn + 8) % 7 || 7;
    switch (token) {
      // Numerical value (Nth day of week with current locale or weekStartsOn)
      case "e":
        return String(localDayOfWeek);
      // Padded numerical value
      case "ee":
        return addLeadingZeros(localDayOfWeek, 2);
      // 1st, 2nd, ..., 7th
      case "eo":
        return localize.ordinalNumber(localDayOfWeek, { unit: "day" });
      case "eee":
        return localize.day(dayOfWeek, {
          width: "abbreviated",
          context: "formatting",
        });
      // T
      case "eeeee":
        return localize.day(dayOfWeek, {
          width: "narrow",
          context: "formatting",
        });
      // Tu
      case "eeeeee":
        return localize.day(dayOfWeek, {
          width: "short",
          context: "formatting",
        });
      // Tuesday
      case "eeee":
      default:
        return localize.day(dayOfWeek, {
          width: "wide",
          context: "formatting",
        });
    }
  },

  // Stand-alone local day of week
  c: function (date, token, localize, options) {
    const dayOfWeek = date.getDay();
    const localDayOfWeek = (dayOfWeek - options.weekStartsOn + 8) % 7 || 7;
    switch (token) {
      // Numerical value (same as in `e`)
      case "c":
        return String(localDayOfWeek);
      // Padded numerical value
      case "cc":
        return addLeadingZeros(localDayOfWeek, token.length);
      // 1st, 2nd, ..., 7th
      case "co":
        return localize.ordinalNumber(localDayOfWeek, { unit: "day" });
      case "ccc":
        return localize.day(dayOfWeek, {
          width: "abbreviated",
          context: "standalone",
        });
      // T
      case "ccccc":
        return localize.day(dayOfWeek, {
          width: "narrow",
          context: "standalone",
        });
      // Tu
      case "cccccc":
        return localize.day(dayOfWeek, {
          width: "short",
          context: "standalone",
        });
      // Tuesday
      case "cccc":
      default:
        return localize.day(dayOfWeek, {
          width: "wide",
          context: "standalone",
        });
    }
  },

  // ISO day of week
  i: function (date, token, localize) {
    const dayOfWeek = date.getDay();
    const isoDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;
    switch (token) {
      // 2
      case "i":
        return String(isoDayOfWeek);
      // 02
      case "ii":
        return addLeadingZeros(isoDayOfWeek, token.length);
      // 2nd
      case "io":
        return localize.ordinalNumber(isoDayOfWeek, { unit: "day" });
      // Tue
      case "iii":
        return localize.day(dayOfWeek, {
          width: "abbreviated",
          context: "formatting",
        });
      // T
      case "iiiii":
        return localize.day(dayOfWeek, {
          width: "narrow",
          context: "formatting",
        });
      // Tu
      case "iiiiii":
        return localize.day(dayOfWeek, {
          width: "short",
          context: "formatting",
        });
      // Tuesday
      case "iiii":
      default:
        return localize.day(dayOfWeek, {
          width: "wide",
          context: "formatting",
        });
    }
  },

  // AM or PM
  a: function (date, token, localize) {
    const hours = date.getHours();
    const dayPeriodEnumValue = hours / 12 >= 1 ? "pm" : "am";

    switch (token) {
      case "a":
      case "aa":
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "abbreviated",
          context: "formatting",
        });
      case "aaa":
        return localize
          .dayPeriod(dayPeriodEnumValue, {
            width: "abbreviated",
            context: "formatting",
          })
          .toLowerCase();
      case "aaaaa":
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "narrow",
          context: "formatting",
        });
      case "aaaa":
      default:
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "wide",
          context: "formatting",
        });
    }
  },

  // AM, PM, midnight, noon
  b: function (date, token, localize) {
    const hours = date.getHours();
    let dayPeriodEnumValue;
    if (hours === 12) {
      dayPeriodEnumValue = dayPeriodEnum.noon;
    } else if (hours === 0) {
      dayPeriodEnumValue = dayPeriodEnum.midnight;
    } else {
      dayPeriodEnumValue = hours / 12 >= 1 ? "pm" : "am";
    }

    switch (token) {
      case "b":
      case "bb":
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "abbreviated",
          context: "formatting",
        });
      case "bbb":
        return localize
          .dayPeriod(dayPeriodEnumValue, {
            width: "abbreviated",
            context: "formatting",
          })
          .toLowerCase();
      case "bbbbb":
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "narrow",
          context: "formatting",
        });
      case "bbbb":
      default:
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "wide",
          context: "formatting",
        });
    }
  },

  // in the morning, in the afternoon, in the evening, at night
  B: function (date, token, localize) {
    const hours = date.getHours();
    let dayPeriodEnumValue;
    if (hours >= 17) {
      dayPeriodEnumValue = dayPeriodEnum.evening;
    } else if (hours >= 12) {
      dayPeriodEnumValue = dayPeriodEnum.afternoon;
    } else if (hours >= 4) {
      dayPeriodEnumValue = dayPeriodEnum.morning;
    } else {
      dayPeriodEnumValue = dayPeriodEnum.night;
    }

    switch (token) {
      case "B":
      case "BB":
      case "BBB":
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "abbreviated",
          context: "formatting",
        });
      case "BBBBB":
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "narrow",
          context: "formatting",
        });
      case "BBBB":
      default:
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: "wide",
          context: "formatting",
        });
    }
  },

  // Hour [1-12]
  h: function (date, token, localize) {
    if (token === "ho") {
      let hours = date.getHours() % 12;
      if (hours === 0) hours = 12;
      return localize.ordinalNumber(hours, { unit: "hour" });
    }

    return lightFormatters.h(date, token);
  },

  // Hour [0-23]
  H: function (date, token, localize) {
    if (token === "Ho") {
      return localize.ordinalNumber(date.getHours(), { unit: "hour" });
    }

    return lightFormatters.H(date, token);
  },

  // Hour [0-11]
  K: function (date, token, localize) {
    const hours = date.getHours() % 12;

    if (token === "Ko") {
      return localize.ordinalNumber(hours, { unit: "hour" });
    }

    return addLeadingZeros(hours, token.length);
  },

  // Hour [1-24]
  k: function (date, token, localize) {
    let hours = date.getHours();
    if (hours === 0) hours = 24;

    if (token === "ko") {
      return localize.ordinalNumber(hours, { unit: "hour" });
    }

    return addLeadingZeros(hours, token.length);
  },

  // Minute
  m: function (date, token, localize) {
    if (token === "mo") {
      return localize.ordinalNumber(date.getMinutes(), { unit: "minute" });
    }

    return lightFormatters.m(date, token);
  },

  // Second
  s: function (date, token, localize) {
    if (token === "so") {
      return localize.ordinalNumber(date.getSeconds(), { unit: "second" });
    }

    return lightFormatters.s(date, token);
  },

  // Fraction of second
  S: function (date, token) {
    return lightFormatters.S(date, token);
  },

  // Timezone (ISO-8601. If offset is 0, output is always `'Z'`)
  X: function (date, token, _localize) {
    const timezoneOffset = date.getTimezoneOffset();

    if (timezoneOffset === 0) {
      return "Z";
    }

    switch (token) {
      // Hours and optional minutes
      case "X":
        return formatTimezoneWithOptionalMinutes(timezoneOffset);

      // Hours, minutes and optional seconds without `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `XX`
      case "XXXX":
      case "XX": // Hours and minutes without `:` delimiter
        return formatTimezone(timezoneOffset);

      // Hours, minutes and optional seconds with `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `XXX`
      case "XXXXX":
      case "XXX": // Hours and minutes with `:` delimiter
      default:
        return formatTimezone(timezoneOffset, ":");
    }
  },

  // Timezone (ISO-8601. If offset is 0, output is `'+00:00'` or equivalent)
  x: function (date, token, _localize) {
    const timezoneOffset = date.getTimezoneOffset();

    switch (token) {
      // Hours and optional minutes
      case "x":
        return formatTimezoneWithOptionalMinutes(timezoneOffset);

      // Hours, minutes and optional seconds without `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `xx`
      case "xxxx":
      case "xx": // Hours and minutes without `:` delimiter
        return formatTimezone(timezoneOffset);

      // Hours, minutes and optional seconds with `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `xxx`
      case "xxxxx":
      case "xxx": // Hours and minutes with `:` delimiter
      default:
        return formatTimezone(timezoneOffset, ":");
    }
  },

  // Timezone (GMT)
  O: function (date, token, _localize) {
    const timezoneOffset = date.getTimezoneOffset();

    switch (token) {
      // Short
      case "O":
      case "OO":
      case "OOO":
        return "GMT" + formatTimezoneShort(timezoneOffset, ":");
      // Long
      case "OOOO":
      default:
        return "GMT" + formatTimezone(timezoneOffset, ":");
    }
  },

  // Timezone (specific non-location)
  z: function (date, token, _localize) {
    const timezoneOffset = date.getTimezoneOffset();

    switch (token) {
      // Short
      case "z":
      case "zz":
      case "zzz":
        return "GMT" + formatTimezoneShort(timezoneOffset, ":");
      // Long
      case "zzzz":
      default:
        return "GMT" + formatTimezone(timezoneOffset, ":");
    }
  },

  // Seconds timestamp
  t: function (date, token, _localize) {
    const timestamp = Math.trunc(date.getTime() / 1000);
    return addLeadingZeros(timestamp, token.length);
  },

  // Milliseconds timestamp
  T: function (date, token, _localize) {
    const timestamp = date.getTime();
    return addLeadingZeros(timestamp, token.length);
  },
};

function formatTimezoneShort(offset, delimiter = "") {
  const sign = offset > 0 ? "-" : "+";
  const absOffset = Math.abs(offset);
  const hours = Math.trunc(absOffset / 60);
  const minutes = absOffset % 60;
  if (minutes === 0) {
    return sign + String(hours);
  }
  return sign + String(hours) + delimiter + addLeadingZeros(minutes, 2);
}

function formatTimezoneWithOptionalMinutes(offset, delimiter) {
  if (offset % 60 === 0) {
    const sign = offset > 0 ? "-" : "+";
    return sign + addLeadingZeros(Math.abs(offset) / 60, 2);
  }
  return formatTimezone(offset, delimiter);
}

function formatTimezone(offset, delimiter = "") {
  const sign = offset > 0 ? "-" : "+";
  const absOffset = Math.abs(offset);
  const hours = addLeadingZeros(Math.trunc(absOffset / 60), 2);
  const minutes = addLeadingZeros(absOffset % 60, 2);
  return sign + hours + delimiter + minutes;
}

const dateLongFormatter = (pattern, formatLong) => {
  switch (pattern) {
    case "P":
      return formatLong.date({ width: "short" });
    case "PP":
      return formatLong.date({ width: "medium" });
    case "PPP":
      return formatLong.date({ width: "long" });
    case "PPPP":
    default:
      return formatLong.date({ width: "full" });
  }
};

const timeLongFormatter = (pattern, formatLong) => {
  switch (pattern) {
    case "p":
      return formatLong.time({ width: "short" });
    case "pp":
      return formatLong.time({ width: "medium" });
    case "ppp":
      return formatLong.time({ width: "long" });
    case "pppp":
    default:
      return formatLong.time({ width: "full" });
  }
};

const dateTimeLongFormatter = (pattern, formatLong) => {
  const matchResult = pattern.match(/(P+)(p+)?/) || [];
  const datePattern = matchResult[1];
  const timePattern = matchResult[2];

  if (!timePattern) {
    return dateLongFormatter(pattern, formatLong);
  }

  let dateTimeFormat;

  switch (datePattern) {
    case "P":
      dateTimeFormat = formatLong.dateTime({ width: "short" });
      break;
    case "PP":
      dateTimeFormat = formatLong.dateTime({ width: "medium" });
      break;
    case "PPP":
      dateTimeFormat = formatLong.dateTime({ width: "long" });
      break;
    case "PPPP":
    default:
      dateTimeFormat = formatLong.dateTime({ width: "full" });
      break;
  }

  return dateTimeFormat
    .replace("{{date}}", dateLongFormatter(datePattern, formatLong))
    .replace("{{time}}", timeLongFormatter(timePattern, formatLong));
};

const longFormatters = {
  p: timeLongFormatter,
  P: dateTimeLongFormatter,
};

const dayOfYearTokenRE = /^D+$/;
const weekYearTokenRE = /^Y+$/;

const throwTokens = ["D", "DD", "YY", "YYYY"];

function isProtectedDayOfYearToken(token) {
  return dayOfYearTokenRE.test(token);
}

function isProtectedWeekYearToken(token) {
  return weekYearTokenRE.test(token);
}

function warnOrThrowProtectedError(token, format, input) {
  const _message = message(token, format, input);
  console.warn(_message);
  if (throwTokens.includes(token)) throw new RangeError(_message);
}

function message(token, format, input) {
  const subject = token[0] === "Y" ? "years" : "days of the month";
  return `Use \`${token.toLowerCase()}\` instead of \`${token}\` (in \`${format}\`) for formatting ${subject} to the input \`${input}\`; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md`;
}

// This RegExp consists of three parts separated by `|`:
// - [yYQqMLwIdDecihHKkms]o matches any available ordinal number token
//   (one of the certain letters followed by `o`)
// - (\w)\1* matches any sequences of the same letter
// - '' matches two quote characters in a row
// - '(''|[^'])+('|$) matches anything surrounded by two quote characters ('),
//   except a single quote symbol, which ends the sequence.
//   Two quote characters do not end the sequence.
//   If there is no matching single quote
//   then the sequence will continue until the end of the string.
// - . matches any single character unmatched by previous parts of the RegExps
const formattingTokensRegExp =
  /[yYQqMLwIdDecihHKkms]o|(\w)\1*|''|'(''|[^'])+('|$)|./g;

// This RegExp catches symbols escaped by quotes, and also
// sequences of symbols P, p, and the combinations like `PPPPPPPppppp`
const longFormattingTokensRegExp = /P+p+|P+|p+|''|'(''|[^'])+('|$)|./g;

const escapedStringRegExp = /^'([^]*?)'?$/;
const doubleQuoteRegExp = /''/g;
const unescapedLatinCharacterRegExp = /[a-zA-Z]/;

/**
 * The {@link format} function options.
 */

/**
 * @name format
 * @alias formatDate
 * @category Common Helpers
 * @summary Format the date.
 *
 * @description
 * Return the formatted date string in the given format. The result may vary by locale.
 *
 * > ⚠️ Please note that the `format` tokens differ from Moment.js and other libraries.
 * > See: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 *
 * The characters wrapped between two single quotes characters (') are escaped.
 * Two single quotes in a row, whether inside or outside a quoted sequence, represent a 'real' single quote.
 * (see the last example)
 *
 * Format of the string is based on Unicode Technical Standard #35:
 * https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table
 * with a few additions (see note 7 below the table).
 *
 * Accepted patterns:
 * | Unit                            | Pattern | Result examples                   | Notes |
 * |---------------------------------|---------|-----------------------------------|-------|
 * | Era                             | G..GGG  | AD, BC                            |       |
 * |                                 | GGGG    | Anno Domini, Before Christ        | 2     |
 * |                                 | GGGGG   | A, B                              |       |
 * | Calendar year                   | y       | 44, 1, 1900, 2017                 | 5     |
 * |                                 | yo      | 44th, 1st, 0th, 17th              | 5,7   |
 * |                                 | yy      | 44, 01, 00, 17                    | 5     |
 * |                                 | yyy     | 044, 001, 1900, 2017              | 5     |
 * |                                 | yyyy    | 0044, 0001, 1900, 2017            | 5     |
 * |                                 | yyyyy   | ...                               | 3,5   |
 * | Local week-numbering year       | Y       | 44, 1, 1900, 2017                 | 5     |
 * |                                 | Yo      | 44th, 1st, 1900th, 2017th         | 5,7   |
 * |                                 | YY      | 44, 01, 00, 17                    | 5,8   |
 * |                                 | YYY     | 044, 001, 1900, 2017              | 5     |
 * |                                 | YYYY    | 0044, 0001, 1900, 2017            | 5,8   |
 * |                                 | YYYYY   | ...                               | 3,5   |
 * | ISO week-numbering year         | R       | -43, 0, 1, 1900, 2017             | 5,7   |
 * |                                 | RR      | -43, 00, 01, 1900, 2017           | 5,7   |
 * |                                 | RRR     | -043, 000, 001, 1900, 2017        | 5,7   |
 * |                                 | RRRR    | -0043, 0000, 0001, 1900, 2017     | 5,7   |
 * |                                 | RRRRR   | ...                               | 3,5,7 |
 * | Extended year                   | u       | -43, 0, 1, 1900, 2017             | 5     |
 * |                                 | uu      | -43, 01, 1900, 2017               | 5     |
 * |                                 | uuu     | -043, 001, 1900, 2017             | 5     |
 * |                                 | uuuu    | -0043, 0001, 1900, 2017           | 5     |
 * |                                 | uuuuu   | ...                               | 3,5   |
 * | Quarter (formatting)            | Q       | 1, 2, 3, 4                        |       |
 * |                                 | Qo      | 1st, 2nd, 3rd, 4th                | 7     |
 * |                                 | QQ      | 01, 02, 03, 04                    |       |
 * |                                 | QQQ     | Q1, Q2, Q3, Q4                    |       |
 * |                                 | QQQQ    | 1st quarter, 2nd quarter, ...     | 2     |
 * |                                 | QQQQQ   | 1, 2, 3, 4                        | 4     |
 * | Quarter (stand-alone)           | q       | 1, 2, 3, 4                        |       |
 * |                                 | qo      | 1st, 2nd, 3rd, 4th                | 7     |
 * |                                 | qq      | 01, 02, 03, 04                    |       |
 * |                                 | qqq     | Q1, Q2, Q3, Q4                    |       |
 * |                                 | qqqq    | 1st quarter, 2nd quarter, ...     | 2     |
 * |                                 | qqqqq   | 1, 2, 3, 4                        | 4     |
 * | Month (formatting)              | M       | 1, 2, ..., 12                     |       |
 * |                                 | Mo      | 1st, 2nd, ..., 12th               | 7     |
 * |                                 | MM      | 01, 02, ..., 12                   |       |
 * |                                 | MMM     | Jan, Feb, ..., Dec                |       |
 * |                                 | MMMM    | January, February, ..., December  | 2     |
 * |                                 | MMMMM   | J, F, ..., D                      |       |
 * | Month (stand-alone)             | L       | 1, 2, ..., 12                     |       |
 * |                                 | Lo      | 1st, 2nd, ..., 12th               | 7     |
 * |                                 | LL      | 01, 02, ..., 12                   |       |
 * |                                 | LLL     | Jan, Feb, ..., Dec                |       |
 * |                                 | LLLL    | January, February, ..., December  | 2     |
 * |                                 | LLLLL   | J, F, ..., D                      |       |
 * | Local week of year              | w       | 1, 2, ..., 53                     |       |
 * |                                 | wo      | 1st, 2nd, ..., 53th               | 7     |
 * |                                 | ww      | 01, 02, ..., 53                   |       |
 * | ISO week of year                | I       | 1, 2, ..., 53                     | 7     |
 * |                                 | Io      | 1st, 2nd, ..., 53th               | 7     |
 * |                                 | II      | 01, 02, ..., 53                   | 7     |
 * | Day of month                    | d       | 1, 2, ..., 31                     |       |
 * |                                 | do      | 1st, 2nd, ..., 31st               | 7     |
 * |                                 | dd      | 01, 02, ..., 31                   |       |
 * | Day of year                     | D       | 1, 2, ..., 365, 366               | 9     |
 * |                                 | Do      | 1st, 2nd, ..., 365th, 366th       | 7     |
 * |                                 | DD      | 01, 02, ..., 365, 366             | 9     |
 * |                                 | DDD     | 001, 002, ..., 365, 366           |       |
 * |                                 | DDDD    | ...                               | 3     |
 * | Day of week (formatting)        | E..EEE  | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 | EEEE    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 | EEEEE   | M, T, W, T, F, S, S               |       |
 * |                                 | EEEEEE  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
 * | ISO day of week (formatting)    | i       | 1, 2, 3, ..., 7                   | 7     |
 * |                                 | io      | 1st, 2nd, ..., 7th                | 7     |
 * |                                 | ii      | 01, 02, ..., 07                   | 7     |
 * |                                 | iii     | Mon, Tue, Wed, ..., Sun           | 7     |
 * |                                 | iiii    | Monday, Tuesday, ..., Sunday      | 2,7   |
 * |                                 | iiiii   | M, T, W, T, F, S, S               | 7     |
 * |                                 | iiiiii  | Mo, Tu, We, Th, Fr, Sa, Su        | 7     |
 * | Local day of week (formatting)  | e       | 2, 3, 4, ..., 1                   |       |
 * |                                 | eo      | 2nd, 3rd, ..., 1st                | 7     |
 * |                                 | ee      | 02, 03, ..., 01                   |       |
 * |                                 | eee     | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 | eeee    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 | eeeee   | M, T, W, T, F, S, S               |       |
 * |                                 | eeeeee  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
 * | Local day of week (stand-alone) | c       | 2, 3, 4, ..., 1                   |       |
 * |                                 | co      | 2nd, 3rd, ..., 1st                | 7     |
 * |                                 | cc      | 02, 03, ..., 01                   |       |
 * |                                 | ccc     | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 | cccc    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 | ccccc   | M, T, W, T, F, S, S               |       |
 * |                                 | cccccc  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
 * | AM, PM                          | a..aa   | AM, PM                            |       |
 * |                                 | aaa     | am, pm                            |       |
 * |                                 | aaaa    | a.m., p.m.                        | 2     |
 * |                                 | aaaaa   | a, p                              |       |
 * | AM, PM, noon, midnight          | b..bb   | AM, PM, noon, midnight            |       |
 * |                                 | bbb     | am, pm, noon, midnight            |       |
 * |                                 | bbbb    | a.m., p.m., noon, midnight        | 2     |
 * |                                 | bbbbb   | a, p, n, mi                       |       |
 * | Flexible day period             | B..BBB  | at night, in the morning, ...     |       |
 * |                                 | BBBB    | at night, in the morning, ...     | 2     |
 * |                                 | BBBBB   | at night, in the morning, ...     |       |
 * | Hour [1-12]                     | h       | 1, 2, ..., 11, 12                 |       |
 * |                                 | ho      | 1st, 2nd, ..., 11th, 12th         | 7     |
 * |                                 | hh      | 01, 02, ..., 11, 12               |       |
 * | Hour [0-23]                     | H       | 0, 1, 2, ..., 23                  |       |
 * |                                 | Ho      | 0th, 1st, 2nd, ..., 23rd          | 7     |
 * |                                 | HH      | 00, 01, 02, ..., 23               |       |
 * | Hour [0-11]                     | K       | 1, 2, ..., 11, 0                  |       |
 * |                                 | Ko      | 1st, 2nd, ..., 11th, 0th          | 7     |
 * |                                 | KK      | 01, 02, ..., 11, 00               |       |
 * | Hour [1-24]                     | k       | 24, 1, 2, ..., 23                 |       |
 * |                                 | ko      | 24th, 1st, 2nd, ..., 23rd         | 7     |
 * |                                 | kk      | 24, 01, 02, ..., 23               |       |
 * | Minute                          | m       | 0, 1, ..., 59                     |       |
 * |                                 | mo      | 0th, 1st, ..., 59th               | 7     |
 * |                                 | mm      | 00, 01, ..., 59                   |       |
 * | Second                          | s       | 0, 1, ..., 59                     |       |
 * |                                 | so      | 0th, 1st, ..., 59th               | 7     |
 * |                                 | ss      | 00, 01, ..., 59                   |       |
 * | Fraction of second              | S       | 0, 1, ..., 9                      |       |
 * |                                 | SS      | 00, 01, ..., 99                   |       |
 * |                                 | SSS     | 000, 001, ..., 999                |       |
 * |                                 | SSSS    | ...                               | 3     |
 * | Timezone (ISO-8601 w/ Z)        | X       | -08, +0530, Z                     |       |
 * |                                 | XX      | -0800, +0530, Z                   |       |
 * |                                 | XXX     | -08:00, +05:30, Z                 |       |
 * |                                 | XXXX    | -0800, +0530, Z, +123456          | 2     |
 * |                                 | XXXXX   | -08:00, +05:30, Z, +12:34:56      |       |
 * | Timezone (ISO-8601 w/o Z)       | x       | -08, +0530, +00                   |       |
 * |                                 | xx      | -0800, +0530, +0000               |       |
 * |                                 | xxx     | -08:00, +05:30, +00:00            | 2     |
 * |                                 | xxxx    | -0800, +0530, +0000, +123456      |       |
 * |                                 | xxxxx   | -08:00, +05:30, +00:00, +12:34:56 |       |
 * | Timezone (GMT)                  | O...OOO | GMT-8, GMT+5:30, GMT+0            |       |
 * |                                 | OOOO    | GMT-08:00, GMT+05:30, GMT+00:00   | 2     |
 * | Timezone (specific non-locat.)  | z...zzz | GMT-8, GMT+5:30, GMT+0            | 6     |
 * |                                 | zzzz    | GMT-08:00, GMT+05:30, GMT+00:00   | 2,6   |
 * | Seconds timestamp               | t       | 512969520                         | 7     |
 * |                                 | tt      | ...                               | 3,7   |
 * | Milliseconds timestamp          | T       | 512969520900                      | 7     |
 * |                                 | TT      | ...                               | 3,7   |
 * | Long localized date             | P       | 04/29/1453                        | 7     |
 * |                                 | PP      | Apr 29, 1453                      | 7     |
 * |                                 | PPP     | April 29th, 1453                  | 7     |
 * |                                 | PPPP    | Friday, April 29th, 1453          | 2,7   |
 * | Long localized time             | p       | 12:00 AM                          | 7     |
 * |                                 | pp      | 12:00:00 AM                       | 7     |
 * |                                 | ppp     | 12:00:00 AM GMT+2                 | 7     |
 * |                                 | pppp    | 12:00:00 AM GMT+02:00             | 2,7   |
 * | Combination of date and time    | Pp      | 04/29/1453, 12:00 AM              | 7     |
 * |                                 | PPpp    | Apr 29, 1453, 12:00:00 AM         | 7     |
 * |                                 | PPPppp  | April 29th, 1453 at ...           | 7     |
 * |                                 | PPPPpppp| Friday, April 29th, 1453 at ...   | 2,7   |
 * Notes:
 * 1. "Formatting" units (e.g. formatting quarter) in the default en-US locale
 *    are the same as "stand-alone" units, but are different in some languages.
 *    "Formatting" units are declined according to the rules of the language
 *    in the context of a date. "Stand-alone" units are always nominative singular:
 *
 *    `format(new Date(2017, 10, 6), 'do LLLL', {locale: cs}) //=> '6. listopad'`
 *
 *    `format(new Date(2017, 10, 6), 'do MMMM', {locale: cs}) //=> '6. listopadu'`
 *
 * 2. Any sequence of the identical letters is a pattern, unless it is escaped by
 *    the single quote characters (see below).
 *    If the sequence is longer than listed in table (e.g. `EEEEEEEEEEE`)
 *    the output will be the same as default pattern for this unit, usually
 *    the longest one (in case of ISO weekdays, `EEEE`). Default patterns for units
 *    are marked with "2" in the last column of the table.
 *
 *    `format(new Date(2017, 10, 6), 'MMM') //=> 'Nov'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMM') //=> 'November'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMMM') //=> 'N'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMMMM') //=> 'November'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMMMMM') //=> 'November'`
 *
 * 3. Some patterns could be unlimited length (such as `yyyyyyyy`).
 *    The output will be padded with zeros to match the length of the pattern.
 *
 *    `format(new Date(2017, 10, 6), 'yyyyyyyy') //=> '00002017'`
 *
 * 4. `QQQQQ` and `qqqqq` could be not strictly numerical in some locales.
 *    These tokens represent the shortest form of the quarter.
 *
 * 5. The main difference between `y` and `u` patterns are B.C. years:
 *
 *    | Year | `y` | `u` |
 *    |------|-----|-----|
 *    | AC 1 |   1 |   1 |
 *    | BC 1 |   1 |   0 |
 *    | BC 2 |   2 |  -1 |
 *
 *    Also `yy` always returns the last two digits of a year,
 *    while `uu` pads single digit years to 2 characters and returns other years unchanged:
 *
 *    | Year | `yy` | `uu` |
 *    |------|------|------|
 *    | 1    |   01 |   01 |
 *    | 14   |   14 |   14 |
 *    | 376  |   76 |  376 |
 *    | 1453 |   53 | 1453 |
 *
 *    The same difference is true for local and ISO week-numbering years (`Y` and `R`),
 *    except local week-numbering years are dependent on `options.weekStartsOn`
 *    and `options.firstWeekContainsDate` (compare [getISOWeekYear](https://date-fns.org/docs/getISOWeekYear)
 *    and [getWeekYear](https://date-fns.org/docs/getWeekYear)).
 *
 * 6. Specific non-location timezones are currently unavailable in `date-fns`,
 *    so right now these tokens fall back to GMT timezones.
 *
 * 7. These patterns are not in the Unicode Technical Standard #35:
 *    - `i`: ISO day of week
 *    - `I`: ISO week of year
 *    - `R`: ISO week-numbering year
 *    - `t`: seconds timestamp
 *    - `T`: milliseconds timestamp
 *    - `o`: ordinal number modifier
 *    - `P`: long localized date
 *    - `p`: long localized time
 *
 * 8. `YY` and `YYYY` tokens represent week-numbering years but they are often confused with years.
 *    You should enable `options.useAdditionalWeekYearTokens` to use them. See: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 *
 * 9. `D` and `DD` tokens represent days of the year but they are often confused with days of the month.
 *    You should enable `options.useAdditionalDayOfYearTokens` to use them. See: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 *
 * @param date - The original date
 * @param format - The string of tokens
 * @param options - An object with options
 *
 * @returns The formatted date string
 *
 * @throws `date` must not be Invalid Date
 * @throws `options.locale` must contain `localize` property
 * @throws `options.locale` must contain `formatLong` property
 * @throws use `yyyy` instead of `YYYY` for formatting years using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @throws use `yy` instead of `YY` for formatting years using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @throws use `d` instead of `D` for formatting days of the month using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @throws use `dd` instead of `DD` for formatting days of the month using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @throws format string contains an unescaped latin alphabet character
 *
 * @example
 * // Represent 11 February 2014 in middle-endian format:
 * const result = format(new Date(2014, 1, 11), 'MM/dd/yyyy')
 * //=> '02/11/2014'
 *
 * @example
 * // Represent 2 July 2014 in Esperanto:
 * import { eoLocale } from 'date-fns/locale/eo'
 * const result = format(new Date(2014, 6, 2), "do 'de' MMMM yyyy", {
 *   locale: eoLocale
 * })
 * //=> '2-a de julio 2014'
 *
 * @example
 * // Escape string by single quote characters:
 * const result = format(new Date(2014, 6, 2, 15), "h 'o''clock'")
 * //=> "3 o'clock"
 */
function format(date, formatStr, options) {
  const defaultOptions = getDefaultOptions();
  const locale = defaultOptions.locale ?? enUS;

  const firstWeekContainsDate =
    defaultOptions.firstWeekContainsDate ??
    defaultOptions.locale?.options?.firstWeekContainsDate ??
    1;

  const weekStartsOn =
    defaultOptions.weekStartsOn ??
    defaultOptions.locale?.options?.weekStartsOn ??
    0;

  const originalDate = toDate(date);

  if (!isValid(originalDate)) {
    throw new RangeError("Invalid time value");
  }

  let parts = formatStr
    .match(longFormattingTokensRegExp)
    .map((substring) => {
      const firstCharacter = substring[0];
      if (firstCharacter === "p" || firstCharacter === "P") {
        const longFormatter = longFormatters[firstCharacter];
        return longFormatter(substring, locale.formatLong);
      }
      return substring;
    })
    .join("")
    .match(formattingTokensRegExp)
    .map((substring) => {
      // Replace two single quote characters with one single quote character
      if (substring === "''") {
        return { isToken: false, value: "'" };
      }

      const firstCharacter = substring[0];
      if (firstCharacter === "'") {
        return { isToken: false, value: cleanEscapedString(substring) };
      }

      if (formatters[firstCharacter]) {
        return { isToken: true, value: substring };
      }

      if (firstCharacter.match(unescapedLatinCharacterRegExp)) {
        throw new RangeError(
          "Format string contains an unescaped latin alphabet character `" +
            firstCharacter +
            "`",
        );
      }

      return { isToken: false, value: substring };
    });

  // invoke localize preprocessor (only for french locales at the moment)
  if (locale.localize.preprocessor) {
    parts = locale.localize.preprocessor(originalDate, parts);
  }

  const formatterOptions = {
    firstWeekContainsDate,
    weekStartsOn,
    locale,
  };

  return parts
    .map((part) => {
      if (!part.isToken) return part.value;

      const token = part.value;

      if (
        (isProtectedWeekYearToken(token)) ||
        (isProtectedDayOfYearToken(token))
      ) {
        warnOrThrowProtectedError(token, formatStr, String(date));
      }

      const formatter = formatters[token[0]];
      return formatter(originalDate, token, locale.localize, formatterOptions);
    })
    .join("");
}

function cleanEscapedString(input) {
  const matched = input.match(escapedStringRegExp);

  if (!matched) {
    return input;
  }

  return matched[1].replace(doubleQuoteRegExp, "'");
}

const DatePicker = React.forwardRef(({ value: controlled, defaultValue, onValueChange, placeholder = 'Pick date', className, disabled, minDate, ...rest }, ref) => {
    const [internal, setInternal] = React.useState(defaultValue);
    const value = controlled ?? internal;
    const commit = (d) => {
        if (controlled === undefined)
            setInternal(d);
        onValueChange?.(d);
    };
    return (jsxs(Popover$1.Root, { children: [jsxs(Popover$1.Trigger, { ref: ref, disabled: disabled, className: cn('flex h-9 w-full items-center justify-between gap-2 rounded-lg border border-border bg-surface px-3 text-sm text-surface-foreground', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background', 'disabled:cursor-not-allowed disabled:opacity-50', className), ...rest, children: [jsx("span", { className: cn(!value && 'text-muted-foreground'), children: value ? format(value, 'MMM d, yyyy') : placeholder }), jsx(Icon$1, { icon: Calendar$1, size: 14, className: "text-muted-foreground" })] }), jsx(Popover$1.Portal, { children: jsx(Popover$1.Content, { sideOffset: 4, className: "z-popover rounded-lg border border-border bg-surface shadow-md", children: jsx(Calendar, { mode: "single", selected: value, onSelect: commit, disabled: minDate ? { before: minDate } : undefined, initialFocus: true }) }) })] }));
});
DatePicker.displayName = 'DatePicker';

const Item$5 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixAccordion.Item, { ref: ref, className: cn('border-b border-border', className), ...rest })));
Item$5.displayName = 'Accordion.Item';
const Trigger$9 = React.forwardRef(({ className, children, ...rest }, ref) => (jsx(RadixAccordion.Header, { className: "flex", children: jsxs(RadixAccordion.Trigger, { ref: ref, className: cn('flex flex-1 items-center justify-between gap-2 py-3 text-sm font-medium text-foreground transition-all', 'hover:underline data-[state=open]:[&>svg]:rotate-180', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background', className), ...rest, children: [children, jsx(Icon$1, { icon: CaretDown, size: 16, className: "shrink-0 text-muted-foreground transition-transform" })] }) })));
Trigger$9.displayName = 'Accordion.Trigger';
const Content$9 = React.forwardRef(({ className, children, ...rest }, ref) => (jsx(RadixAccordion.Content, { ref: ref, className: cn('overflow-hidden text-sm text-muted-foreground data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up', className), ...rest, children: jsx("div", { className: "pb-3", children: children }) })));
Content$9.displayName = 'Accordion.Content';
const AccordionRootWrapper = ((props) => jsx(RadixAccordion.Root, { ...props }));
AccordionRootWrapper.displayName = 'Accordion';
const Accordion = Object.assign(AccordionRootWrapper, {
    Root: AccordionRootWrapper,
    Item: Item$5,
    Trigger: Trigger$9,
    Content: Content$9,
});
const AccordionItem = Item$5;
const AccordionTrigger = Trigger$9;
const AccordionContent = Content$9;

const Root$8 = React.forwardRef(({ className, ...rest }, ref) => (jsx(Command$1, { ref: ref, className: cn('flex h-full w-full flex-col overflow-hidden rounded-lg bg-surface text-surface-foreground', className), ...rest })));
Root$8.displayName = 'Command.Root';
const Input = React.forwardRef(({ className, wrapperClassName, ...rest }, ref) => (jsxs("div", { className: cn('flex items-center gap-2 border-b border-border px-3', wrapperClassName), children: [jsx(Icon$1, { icon: MagnifyingGlass, size: 14, className: "text-muted-foreground" }), jsx(Command$1.Input, { ref: ref, className: cn('flex h-10 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground', className), ...rest })] })));
Input.displayName = 'Command.Input';
const List$1 = React.forwardRef(({ className, children, ...rest }, forwardedRef) => {
    const listRef = React.useRef(null);
    const highlight = useSlidingMarker(listRef, '[data-selected="true"]', [], {
        observeAttributes: ['data-selected'],
    });
    React.useImperativeHandle(forwardedRef, () => listRef.current);
    return (jsxs(Command$1.List, { ref: listRef, className: cn('relative max-h-[300px] overflow-y-auto p-1', className), ...rest, children: [jsx("span", { "aria-hidden": true, "data-slot": "command-highlight", className: "pointer-events-none absolute inset-x-1 top-0 rounded-md bg-muted duration-slow ease-move", style: highlight.style }), children] }));
});
List$1.displayName = 'Command.List';
const Empty = React.forwardRef(({ className, ...rest }, ref) => (jsx(Command$1.Empty, { ref: ref, className: cn('py-6 text-center text-sm text-muted-foreground', className), ...rest })));
Empty.displayName = 'Command.Empty';
const Group$2 = React.forwardRef(({ className, ...rest }, ref) => (jsx(Command$1.Group, { ref: ref, className: cn('text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.08em] [&_[cmdk-group-heading]]:text-muted-foreground', className), ...rest })));
Group$2.displayName = 'Command.Group';
const Item$4 = React.forwardRef(({ className, ...rest }, ref) => (jsx(Command$1.Item, { ref: ref, className: cn('relative flex cursor-default select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none transition-colors', 'text-muted-foreground data-[selected=true]:text-foreground', 'data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50', className), ...rest })));
Item$4.displayName = 'Command.Item';
const Separator$3 = React.forwardRef(({ className, ...rest }, ref) => (jsx(Command$1.Separator, { ref: ref, className: cn('-mx-1 my-1 h-px bg-border', className), ...rest })));
Separator$3.displayName = 'Command.Separator';
const Command = Object.assign(Root$8, {
    Root: Root$8,
    Input,
    List: List$1,
    Empty,
    Group: Group$2,
    Item: Item$4,
    Separator: Separator$3,
});
const CommandInput = Input;
const CommandList = List$1;
const CommandEmpty = Empty;
const CommandGroup = Group$2;
const CommandItem = Item$4;
const CommandSeparator = Separator$3;

const Root$7 = React.forwardRef(({ className, children, ...rest }, forwardedRef) => {
    const highlight = useSlidingHighlight({ axis: 'horizontal' });
    const openMarker = useSlidingMarker(highlight.containerProps.ref, '[data-state="open"]', [], {
        axis: 'horizontal',
        observeAttributes: ['data-state'],
    });
    React.useImperativeHandle(forwardedRef, () => highlight.containerProps.ref.current);
    const { ref, ...highlightHandlers } = highlight.containerProps;
    const compose = (theirs, ours) => (event) => {
        theirs?.(event);
        ours(event);
    };
    return (jsxs(RadixMenubar.Root, { ref: ref, className: cn('relative flex h-10 items-center gap-1 rounded-lg border border-border bg-surface p-1', className), ...rest, onMouseOver: compose(rest.onMouseOver, highlightHandlers.onMouseOver), onMouseLeave: compose(rest.onMouseLeave, highlightHandlers.onMouseLeave), onFocus: compose(rest.onFocus, highlightHandlers.onFocus), onBlur: compose(rest.onBlur, highlightHandlers.onBlur), children: [jsx("span", { "aria-hidden": true, "data-slot": "menubar-indicator", className: "pointer-events-none absolute inset-y-1 left-0 rounded-md bg-primary-subtle duration-slow ease-move", style: openMarker.style }), jsx("span", { "aria-hidden": true, "data-slot": "menubar-highlight", className: "pointer-events-none absolute inset-y-1 left-0 rounded-md bg-muted duration-slow ease-move", style: highlight.style }), children] }));
});
Root$7.displayName = 'Menubar.Root';
const Menu = RadixMenubar.Menu;
const Trigger$8 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixMenubar.Trigger, { ref: ref, className: cn('relative flex cursor-default select-none items-center rounded-md px-3 py-1 text-sm font-medium outline-none transition-colors', 'text-muted-foreground data-[state=open]:text-foreground', className), ...rest })));
Trigger$8.displayName = 'Menubar.Trigger';
const itemBase$2 = 'flex cursor-default select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none data-[highlighted]:bg-muted data-[disabled]:pointer-events-none data-[disabled]:opacity-50';
const Content$8 = React.forwardRef(({ className, align = 'start', sideOffset = 8, ...rest }, ref) => (jsx(RadixMenubar.Portal, { children: jsx(RadixMenubar.Content, { ref: ref, align: align, sideOffset: sideOffset, className: cn('z-popover min-w-[12rem] overflow-hidden rounded-lg border border-border bg-surface p-1 shadow-md', 'origin-[var(--radix-menubar-content-transform-origin)]', 'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=open]:duration-default data-[state=open]:ease-out', 'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=closed]:duration-fast data-[state=closed]:ease-in', className), ...rest }) })));
Content$8.displayName = 'Menubar.Content';
const Item$3 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixMenubar.Item, { ref: ref, className: cn(itemBase$2, className), ...rest })));
Item$3.displayName = 'Menubar.Item';
const Separator$2 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixMenubar.Separator, { ref: ref, className: cn('-mx-1 my-1 h-px bg-border', className), ...rest })));
Separator$2.displayName = 'Menubar.Separator';
const Shortcut$2 = ({ className, ...rest }) => (jsx("span", { className: cn('ml-auto text-xs text-muted-foreground', className), ...rest }));
const Menubar = Object.assign(Root$7, {
    Root: Root$7,
    Menu,
    Trigger: Trigger$8,
    Content: Content$8,
    Item: Item$3,
    Separator: Separator$2,
    Shortcut: Shortcut$2,
});
const MenubarMenu = Menu;
const MenubarTrigger = Trigger$8;
const MenubarContent = Content$8;
const MenubarItem = Item$3;
const MenubarSeparator = Separator$2;
const MenubarShortcut = Shortcut$2;

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50];
function PageReadout({ page, pageCount }) {
    const [seen, setSeen] = React.useState({ page, direction: 0 });
    if (seen.page !== page)
        setSeen({ page, direction: page > seen.page ? 1 : -1 });
    const direction = seen.page === page ? seen.direction : 0;
    return (jsxs("span", { className: "flex items-baseline gap-1 px-2 text-sm text-muted-foreground", children: ["Page", jsx("span", { className: "inline-block overflow-hidden text-center tabular-nums", style: { width: `${String(pageCount).length}ch` }, children: jsx("span", { "data-slot": "pagination-page", className: direction === 0 ? undefined : direction > 0 ? 'block animate-fade-in-up' : 'block animate-fade-in-down', children: page }, page) }), "of ", pageCount] }));
}
function Pagination(props) {
    const normalized = React.useMemo(() => {
        if ('pagination' in props || 'onLimitChange' in props) {
            const v1 = props;
            const pageCount = v1.pagination?.totalPages ?? 1;
            const page = v1.pagination?.page ?? 1;
            const pageSize = v1.pagination?.limit ?? 20;
            const totalRecords = v1.pagination?.total ?? 0;
            return {
                page,
                pageCount,
                pageSize,
                pageSizeOptions: v1.pageSizeOptions ?? [...DEFAULT_PAGE_SIZE_OPTIONS],
                totalRecords,
                onPageChange: v1.onPageChange,
                onPageSizeChange: v1.onLimitChange,
                hasControls: true,
                isLoading: v1.isLoading === true,
                className: v1.className,
            };
        }
        if (props.variant === 'controls') {
            const v2 = props;
            return {
                page: v2.page,
                pageCount: v2.pageCount,
                pageSize: v2.pageSize,
                pageSizeOptions: v2.pageSizeOptions ?? [...DEFAULT_PAGE_SIZE_OPTIONS],
                totalRecords: v2.totalRecords,
                onPageChange: v2.onPageChange,
                onPageSizeChange: v2.onPageSizeChange,
                hasControls: true,
                isLoading: false,
                className: v2.className,
            };
        }
        const simple = props;
        return {
            page: simple.page,
            pageCount: simple.pageCount,
            pageSize: 0,
            pageSizeOptions: [],
            totalRecords: 0,
            onPageChange: simple.onPageChange,
            onPageSizeChange: (_size) => { },
            hasControls: false,
            isLoading: false,
            className: simple.className,
        };
    }, [props]);
    const { page, pageCount, pageSize, pageSizeOptions, totalRecords, onPageChange, onPageSizeChange, hasControls, isLoading, className } = normalized;
    const canPrev = page > 1 && !isLoading;
    const canNext = page < pageCount && !isLoading;
    const prev = () => canPrev && onPageChange(page - 1);
    const next = () => canNext && onPageChange(page + 1);
    const first = () => canPrev && onPageChange(1);
    const last = () => canNext && onPageChange(pageCount);
    const nav = (jsxs("div", { className: "flex items-center gap-1", children: [jsx(IconButton, { icon: CaretLineLeft, "aria-label": "First page", variant: "ghost", size: "sm", onClick: first, disabled: !canPrev }), jsx(IconButton, { icon: CaretLeft, "aria-label": "Previous page", variant: "ghost", size: "sm", onClick: prev, disabled: !canPrev }), jsx(PageReadout, { page: page, pageCount: pageCount }), jsx(IconButton, { icon: CaretRight, "aria-label": "Next page", variant: "ghost", size: "sm", onClick: next, disabled: !canNext }), jsx(IconButton, { icon: CaretLineRight, "aria-label": "Last page", variant: "ghost", size: "sm", onClick: last, disabled: !canNext })] }));
    if (!hasControls) {
        return jsx("nav", { className: cn('flex justify-end', className), children: nav });
    }
    const from = totalRecords === 0 ? 0 : (page - 1) * pageSize + 1;
    const to = Math.min(page * pageSize, totalRecords);
    return (jsxs("nav", { className: cn('flex flex-wrap items-center justify-between gap-3', className), children: [jsxs("div", { className: "flex items-center gap-2 text-sm text-muted-foreground", children: [jsxs(Select.Root, { value: String(pageSize), onValueChange: (v) => onPageSizeChange(Number(v)), children: [jsx(Select.Trigger, { "aria-label": "Rows per page", className: "w-28 whitespace-nowrap", children: jsx(Select.Value, {}) }), jsx(Select.Content, { children: pageSizeOptions.map(opt => (jsxs(Select.Item, { value: String(opt), className: "whitespace-nowrap", children: [opt, " / page"] }, opt))) })] }), jsx("span", { children: totalRecords === 0
                            ? 'No items'
                            : jsxs(Fragment, { children: ["Showing ", jsxs("strong", { className: "text-foreground", children: [from, "\u2013", to] }), " of ", jsx("strong", { className: "text-foreground", children: totalRecords })] }) })] }), nav] }));
}

const List = React.forwardRef(({ className, children, ...rest }, forwardedRef) => {
    const listRef = React.useRef(null);
    const underline = useSlidingMarker(listRef, '[data-state="active"]', [], {
        axis: 'horizontal',
        observeAttributes: ['data-state'],
    });
    React.useImperativeHandle(forwardedRef, () => listRef.current);
    return (jsxs(RadixTabs.List, { ref: listRef, className: cn('relative flex items-center gap-1 border-b border-border', className), ...rest, children: [children, jsx("span", { "aria-hidden": true, "data-slot": "tabs-indicator", className: "pointer-events-none absolute -bottom-px left-0 h-0.5 rounded-full bg-primary duration-slow ease-move", style: underline.style })] }));
});
List.displayName = 'Tabs.List';
const Trigger$7 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixTabs.Trigger, { ref: ref, className: cn('inline-flex items-center gap-2 border-b-2 border-transparent px-3 py-2 text-sm font-medium text-muted-foreground transition-colors', 'hover:text-foreground', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background', 'data-[state=active]:text-foreground', 'disabled:pointer-events-none disabled:opacity-50', className), ...rest })));
Trigger$7.displayName = 'Tabs.Trigger';
const Content$7 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixTabs.Content, { ref: ref, className: cn('mt-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', className), ...rest })));
Content$7.displayName = 'Tabs.Content';
const TabsRootWrapper = ((props) => jsx(RadixTabs.Root, { ...props }));
TabsRootWrapper.displayName = 'Tabs';
const Tabs = Object.assign(TabsRootWrapper, {
    Root: TabsRootWrapper,
    List,
    Trigger: Trigger$7,
    Content: Content$7,
});
const TabsList = List;
const TabsTrigger = Trigger$7;
const TabsContent = Content$7;

const cardStyles$1 = cva('rounded-xl bg-surface text-surface-foreground', {
    variants: {
        variant: {
            default: 'border border-border shadow-none',
            elevated: 'border-0 shadow-md',
            summary: 'border border-border shadow-none',
            flat: 'border border-border shadow-none',
        },
        tone: {
            neutral: '',
            success: '',
            warning: '',
            destructive: '',
            info: '',
        },
    },
    defaultVariants: { variant: 'default', tone: 'neutral' },
});
const iconToneText = {
    primary: 'text-primary',
    accent: 'text-primary',
    success: 'text-success-foreground',
    warning: 'text-warning-foreground',
    destructive: 'text-destructive-foreground',
    info: 'text-info-foreground',
    neutral: 'text-muted-foreground',
};
function renderLegacySummary(props) {
    const { title, value, icon, iconTone = 'primary', description, valueClassName, trend } = props;
    return (jsxs("div", { className: "p-5", children: [jsxs("div", { className: "flex items-center justify-between gap-3", children: [jsx("div", { className: "truncate text-sm font-medium text-muted-foreground", children: title }), icon && (jsx("div", { className: cn('shrink-0 [&_svg]:h-4 [&_svg]:w-4', iconToneText[iconTone]), children: icon }))] }), jsx("div", { className: cn('mt-3 text-3xl font-semibold tracking-tight tabular-nums text-foreground', valueClassName), children: value }), description && jsx("p", { className: "mt-1 text-xs text-muted-foreground", children: description }), trend && (jsxs("p", { className: cn('mt-1 text-xs font-medium', trend.isPositive ? 'text-success-foreground' : 'text-destructive-foreground'), children: [trend.isPositive ? '↑' : '↓', " ", Math.abs(trend.value), "%"] }))] }));
}
const Root$6 = React.forwardRef(({ className, variant, tone, title, value, icon, iconTone, description, valueClassName, trend, children, ...rest }, ref) => {
    const isLegacySummary = title !== undefined && value !== undefined;
    return (jsx("div", { ref: ref, className: cn(cardStyles$1({ variant, tone }), className), ...rest, children: isLegacySummary
            ? renderLegacySummary({ title, value, icon, iconTone, description, valueClassName, trend })
            : children }));
});
Root$6.displayName = 'Card.Root';
const Header$3 = React.forwardRef(({ className, ...rest }, ref) => (jsx("div", { ref: ref, className: cn('flex items-center justify-between gap-3 border-b border-border px-5 py-4', className), ...rest })));
Header$3.displayName = 'Card.Header';
const titleStyles = cva('font-semibold leading-tight', {
    variants: {
        size: {
            sm: 'text-sm',
            md: 'text-base',
            lg: 'text-lg',
        },
    },
    defaultVariants: { size: 'md' },
});
const Title$5 = React.forwardRef(({ className, size, ...rest }, ref) => (jsx("h3", { ref: ref, className: cn(titleStyles({ size }), className), ...rest })));
Title$5.displayName = 'Card.Title';
const Description$5 = React.forwardRef(({ className, ...rest }, ref) => (jsx("p", { ref: ref, className: cn('text-sm text-muted-foreground', className), ...rest })));
Description$5.displayName = 'Card.Description';
const Body$1 = React.forwardRef(({ className, ...rest }, ref) => (jsx("div", { ref: ref, className: cn('p-5', className), ...rest })));
Body$1.displayName = 'Card.Body';
const Footer$4 = React.forwardRef(({ className, ...rest }, ref) => (jsx("div", { ref: ref, className: cn('flex items-center justify-end gap-2 border-t border-border px-5 py-3', className), ...rest })));
Footer$4.displayName = 'Card.Footer';
const CardRootWrapper = React.forwardRef((props, ref) => (jsx(Root$6, { ref: ref, ...props })));
CardRootWrapper.displayName = 'Card';
const Card = Object.assign(CardRootWrapper, {
    Root: CardRootWrapper,
    Header: Header$3,
    Title: Title$5,
    Description: Description$5,
    Body: Body$1,
    Footer: Footer$4,
});
const CardHeader = Header$3;
const CardTitle = Title$5;
const CardDescription = Description$5;
const CardContent = Body$1;
const CardFooter = Footer$4;
const GRID_COLS = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
    5: 'grid-cols-5',
    6: 'grid-cols-6',
    7: 'grid-cols-7',
    8: 'grid-cols-8',
};
const colClass = (bp, cols) => {
    if (!cols)
        return '';
    const base = GRID_COLS[cols];
    return base ? `${bp}:${base}` : '';
};
const SummaryCardGrid = React.forwardRef(({ className, columns = { md: 2, lg: 4 }, ...rest }, ref) => (jsx("div", { ref: ref, className: cn('grid gap-4', colClass('sm', columns.sm), colClass('md', columns.md), colClass('lg', columns.lg), colClass('xl', columns.xl), className), ...rest })));
SummaryCardGrid.displayName = 'SummaryCardGrid';

const CollapsibleRoot = React.forwardRef(({ children, open, onOpenChange, onToggle, ...rest }, ref) => {
    const innerRef = React.useRef(null);
    React.useImperativeHandle(ref, () => innerRef.current);
    React.useEffect(() => {
        const el = innerRef.current;
        if (!el)
            return;
        if (open !== undefined && el.open !== open) {
            el.open = open;
        }
    }, [open]);
    const handleToggle = (e) => {
        if (onOpenChange) {
            onOpenChange(e.currentTarget.open);
        }
        onToggle?.(e);
    };
    return (jsx("details", { ref: innerRef, open: open, onToggle: handleToggle, ...rest, children: children }));
});
CollapsibleRoot.displayName = 'Collapsible';
const triggerBase = 'cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden';
const CollapsibleTrigger = React.forwardRef(({ children, asChild, className, ...rest }, ref) => {
    if (asChild && React.isValidElement(children)) {
        return React.cloneElement(children, { ...rest, ref });
    }
    return (jsx("summary", { ref: ref, className: cn(triggerBase, className), ...rest, children: children }));
});
CollapsibleTrigger.displayName = 'CollapsibleTrigger';
const CollapsibleContent = React.forwardRef(({ children, ...rest }, ref) => (jsx("div", { ref: ref, ...rest, children: children })));
CollapsibleContent.displayName = 'CollapsibleContent';
const Collapsible = Object.assign(CollapsibleRoot, {
    Root: CollapsibleRoot,
    Trigger: CollapsibleTrigger,
    Content: CollapsibleContent,
});

const Trigger$6 = RadixDialog.Trigger;
const Close$3 = RadixDialog.Close;
const Portal$3 = RadixDialog.Portal;
const Overlay$2 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixDialog.Overlay, { ref: ref, className: cn('fixed inset-0 z-overlay bg-neutral-900/60 backdrop-blur-sm', 'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:duration-default data-[state=open]:ease-out', 'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:duration-fast data-[state=closed]:ease-in', className), ...rest })));
Overlay$2.displayName = 'Dialog.Overlay';
const Content$6 = React.forwardRef(({ className, style, children, ...rest }, ref) => {
    const scope = useSurfaceTheme();
    return (jsxs(Portal$3, { children: [jsx(Overlay$2, {}), jsxs(RadixDialog.Content, { ref: ref, style: { ...scope.style, ...style }, className: cn(scope.className, 'fixed left-[50%] top-[50%] z-dialog flex max-h-[calc(100dvh-2rem)] w-full max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col', 'rounded-lg border border-border bg-surface p-6 text-surface-foreground shadow-lg', 'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:duration-default data-[state=open]:ease-out', 'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:duration-fast data-[state=closed]:ease-in', className), ...rest, children: [jsx("div", { className: "grid gap-4 overflow-y-auto", children: children }), jsx(RadixDialog.Close, { className: "absolute right-4 top-4 rounded-md p-1 text-muted-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring", "aria-label": "Close", children: jsx(Icon$1, { icon: X, size: 16 }) })] })] }));
});
Content$6.displayName = 'Dialog.Content';
const Header$2 = ({ className, ...rest }) => (jsx("div", { className: cn('flex flex-col gap-1.5 text-left', className), ...rest }));
const Footer$3 = ({ className, ...rest }) => (jsx("div", { className: cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className), ...rest }));
const Title$4 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixDialog.Title, { ref: ref, className: cn('text-lg font-semibold leading-tight', className), ...rest })));
Title$4.displayName = 'Dialog.Title';
const Description$4 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixDialog.Description, { ref: ref, className: cn('text-sm text-muted-foreground', className), ...rest })));
Description$4.displayName = 'Dialog.Description';
const DialogRootWrapper = ((props) => jsx(RadixDialog.Root, { ...props }));
DialogRootWrapper.displayName = 'Dialog';
const Dialog = Object.assign(DialogRootWrapper, {
    Root: DialogRootWrapper,
    Trigger: Trigger$6,
    Close: Close$3,
    Portal: Portal$3,
    Overlay: Overlay$2,
    Content: Content$6,
    Header: Header$2,
    Footer: Footer$3,
    Title: Title$4,
    Description: Description$4,
});
const DialogTrigger = Trigger$6;
const DialogContent = Content$6;
const DialogTitle = Title$4;
const DialogDescription = Description$4;
const DialogHeader = Header$2;
const DialogFooter = Footer$3;
const DialogClose = Close$3;
const DialogOverlay = Overlay$2;
const DialogPortal = Portal$3;

const Trigger$5 = RadixAlertDialog.Trigger;
const Portal$2 = RadixAlertDialog.Portal;
const Overlay$1 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixAlertDialog.Overlay, { ref: ref, className: cn('fixed inset-0 z-overlay bg-neutral-900/60 backdrop-blur-sm', 'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:duration-default data-[state=open]:ease-out', 'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:duration-fast data-[state=closed]:ease-in', className), ...rest })));
Overlay$1.displayName = 'AlertDialog.Overlay';
const Content$5 = React.forwardRef(({ className, style, ...rest }, ref) => {
    const scope = useSurfaceTheme();
    return (jsxs(Portal$2, { children: [jsx(Overlay$1, {}), jsx(RadixAlertDialog.Content, { ref: ref, style: { ...scope.style, ...style }, className: cn(scope.className, 'fixed left-1/2 top-1/2 z-dialog grid max-h-[calc(100dvh-2rem)] w-full max-w-md -translate-x-1/2 -translate-y-1/2 gap-4 overflow-y-auto', 'rounded-lg border border-border bg-surface p-6 text-surface-foreground shadow-lg', 'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:duration-default data-[state=open]:ease-out', 'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:duration-fast data-[state=closed]:ease-in', className), ...rest })] }));
});
Content$5.displayName = 'AlertDialog.Content';
const Title$3 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixAlertDialog.Title, { ref: ref, className: cn('text-lg font-semibold', className), ...rest })));
Title$3.displayName = 'AlertDialog.Title';
const Description$3 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixAlertDialog.Description, { ref: ref, className: cn('text-sm text-muted-foreground', className), ...rest })));
Description$3.displayName = 'AlertDialog.Description';
const Footer$2 = ({ className, ...rest }) => (jsx("div", { className: cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className), ...rest }));
const Action$1 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixAlertDialog.Action, { ref: ref, className: cn(buttonStyles({ variant: 'destructive', size: 'md' }), className), ...rest })));
Action$1.displayName = 'AlertDialog.Action';
const Cancel = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixAlertDialog.Cancel, { ref: ref, className: cn(buttonStyles({ variant: 'secondary', size: 'md' }), className), ...rest })));
Cancel.displayName = 'AlertDialog.Cancel';
const AlertDialogHeader = ({ className, ...rest }) => (jsx("div", { className: cn('flex flex-col gap-1.5 text-left', className), ...rest }));
const AlertDialogRootWrapper = ((props) => jsx(RadixAlertDialog.Root, { ...props }));
AlertDialogRootWrapper.displayName = 'AlertDialog';
const AlertDialog = Object.assign(AlertDialogRootWrapper, {
    Root: AlertDialogRootWrapper,
    Trigger: Trigger$5,
    Portal: Portal$2,
    Overlay: Overlay$1,
    Content: Content$5,
    Header: AlertDialogHeader,
    Title: Title$3,
    Description: Description$3,
    Footer: Footer$2,
    Action: Action$1,
    Cancel,
});
const AlertDialogTrigger = Trigger$5;
const AlertDialogContent = Content$5;
const AlertDialogTitle = Title$3;
const AlertDialogDescription = Description$3;
const AlertDialogFooter = Footer$2;
const AlertDialogAction = Action$1;
const AlertDialogCancel = Cancel;
const AlertDialogOverlay = Overlay$1;
const AlertDialogPortal = Portal$2;

const sheetVariants = cva([
    'fixed z-dialog flex flex-col gap-4 bg-surface text-surface-foreground p-6 shadow-lg border border-border',
    'data-[state=open]:animate-in data-[state=open]:duration-slow data-[state=open]:ease-out',
    'data-[state=closed]:animate-out data-[state=closed]:duration-fast data-[state=closed]:ease-in',
], {
    variants: {
        side: {
            right: 'right-0 top-0 h-full w-full sm:max-w-md data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right',
            left: 'left-0  top-0 h-full w-full sm:max-w-md data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left',
            top: 'top-0    left-0 w-full h-auto data-[state=open]:slide-in-from-top data-[state=closed]:slide-out-to-top',
            bottom: 'bottom-0 left-0 w-full h-auto data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom',
        },
    },
    defaultVariants: { side: 'right' },
});
const Trigger$4 = RadixDialog.Trigger;
const Close$2 = RadixDialog.Close;
const Overlay = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixDialog.Overlay, { ref: ref, className: cn('fixed inset-0 z-overlay bg-neutral-900/50 backdrop-blur-sm', className), ...rest })));
Overlay.displayName = 'Sheet.Overlay';
const Content$4 = React.forwardRef(({ side, className, children, viewportOffset = 'none', overlayClassName, style, ...rest }, ref) => {
    const scope = useSurfaceTheme();
    return (jsxs(RadixDialog.Portal, { children: [jsx(Overlay, { className: overlayClassName, style: viewportOffset === 'app-header' ? { top: '3.5rem' } : undefined }), jsxs(RadixDialog.Content, { ref: ref, className: cn(scope.className, sheetVariants({ side }), className), style: { ...scope.style, ...style, ...(viewportOffset === 'app-header' ? { top: '3.5rem', bottom: 0, height: 'auto' } : {}) }, ...rest, children: [children, jsx(RadixDialog.Close, { className: "absolute right-4 top-4 rounded-md p-1 text-muted-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring", "aria-label": "Close", children: jsx(Icon$1, { icon: X, size: 16 }) })] })] }));
});
Content$4.displayName = 'Sheet.Content';
const Title$2 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixDialog.Title, { ref: ref, className: cn('text-lg font-semibold', className), ...rest })));
Title$2.displayName = 'Sheet.Title';
const Description$2 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixDialog.Description, { ref: ref, className: cn('text-sm text-muted-foreground', className), ...rest })));
Description$2.displayName = 'Sheet.Description';
const Header$1 = ({ className, ...rest }) => (jsx("div", { className: cn('flex flex-col gap-1', className), ...rest }));
const Footer$1 = ({ className, ...rest }) => (jsx("div", { className: cn('mt-auto flex items-center justify-end gap-2', className), ...rest }));
const SheetRootWrapper = ((props) => jsx(RadixDialog.Root, { ...props }));
SheetRootWrapper.displayName = 'Sheet';
const Sheet = Object.assign(SheetRootWrapper, {
    Root: SheetRootWrapper,
    Trigger: Trigger$4,
    Close: Close$2,
    Overlay,
    Content: Content$4,
    Title: Title$2,
    Description: Description$2,
    Header: Header$1,
    Footer: Footer$1,
});
const SheetTrigger = Trigger$4;
const SheetContent = Content$4;
const SheetTitle = Title$2;
const SheetDescription = Description$2;
const SheetHeader = Header$1;
const SheetFooter = Footer$1;
const SheetClose = Close$2;
const SheetOverlay = Overlay;

const FormDrawer = ({ open, onOpenChange, title, description, children, footer, isDirty, widthClassName = 'max-w-xl', }) => {
    const [interacted, setInteracted] = React.useState(false);
    const [confirmDiscard, setConfirmDiscard] = React.useState(false);
    React.useEffect(() => {
        if (open)
            setInteracted(false);
    }, [open]);
    const dirty = isDirty ?? interacted;
    const requestClose = (next) => {
        if (next) {
            onOpenChange(true);
            return;
        }
        if (dirty)
            setConfirmDiscard(true);
        else
            onOpenChange(false);
    };
    return (jsxs(Fragment, { children: [jsx(Sheet, { open: open, onOpenChange: requestClose, children: jsxs(SheetContent, { side: "right", className: cn('flex w-full flex-col gap-0 p-0 sm:w-[90vw]', widthClassName), children: [jsxs(SheetHeader, { className: "border-b border-border px-6 py-4", children: [jsx(SheetTitle, { children: title }), description && jsx(SheetDescription, { children: description })] }), jsx("div", { className: "min-h-0 flex-1 overflow-y-auto px-6 py-5", onChangeCapture: () => setInteracted(true), onInput: () => setInteracted(true), children: children }), footer && (jsx("div", { className: "border-t border-border px-6 py-4", children: jsx("div", { className: "flex items-center justify-end gap-3", children: footer }) }))] }) }), jsx(AlertDialog, { open: confirmDiscard, onOpenChange: setConfirmDiscard, children: jsxs(AlertDialogContent, { children: [jsxs(AlertDialogHeader, { children: [jsx(AlertDialogTitle, { children: "Discard unsaved changes?" }), jsx(AlertDialogDescription, { children: "You have unsaved changes. If you close now, they will be lost." })] }), jsxs(AlertDialogFooter, { children: [jsx(AlertDialogCancel, { children: "Keep editing" }), jsx(AlertDialogAction, { className: "bg-destructive text-destructive-foreground hover:bg-destructive/90", onClick: () => {
                                        setConfirmDiscard(false);
                                        onOpenChange(false);
                                    }, children: "Discard" })] })] }) })] }));
};

const Trigger$3 = Popover$1.Trigger;
const Anchor = Popover$1.Anchor;
const Close$1 = Popover$1.Close;
const Content$3 = React.forwardRef(({ className, style, align = 'center', sideOffset = 6, ...rest }, ref) => {
    const scope = useSurfaceTheme();
    return (jsx(Popover$1.Portal, { children: jsx(Popover$1.Content, { ref: ref, style: { ...scope.style, ...style }, align: align, sideOffset: sideOffset, className: cn(scope.className, 'z-popover w-72 rounded-lg border border-border bg-surface p-4 text-surface-foreground shadow-md outline-none', 'origin-[var(--radix-popover-content-transform-origin)]', 'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=open]:duration-default data-[state=open]:ease-out', 'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=closed]:duration-fast data-[state=closed]:ease-in', className), ...rest }) }));
});
Content$3.displayName = 'Popover.Content';
const PopoverRootWrapper = ((props) => jsx(Popover$1.Root, { ...props }));
PopoverRootWrapper.displayName = 'Popover';
const Popover = Object.assign(PopoverRootWrapper, {
    Root: PopoverRootWrapper,
    Trigger: Trigger$3,
    Anchor,
    Close: Close$1,
    Content: Content$3,
});
const PopoverTrigger = Trigger$3;
const PopoverContent = Content$3;
const PopoverAnchor = Anchor;
const PopoverClose = Close$1;

const Provider$1 = (props) => (jsx(RadixTooltip.Provider, { delayDuration: 200, ...props }));
const Trigger$2 = RadixTooltip.Trigger;
const Content$2 = React.forwardRef(({ className, style, sideOffset = 6, ...rest }, ref) => {
    const scope = useSurfaceTheme();
    return (jsx(RadixTooltip.Portal, { children: jsx(RadixTooltip.Content, { ref: ref, style: { ...scope.style, ...style }, sideOffset: sideOffset, className: cn(scope.className, 'z-popover rounded-md bg-neutral-900 px-2 py-1 text-xs text-white shadow-md', 'origin-[var(--radix-tooltip-content-transform-origin)]', 'data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95 data-[state=delayed-open]:duration-fast data-[state=delayed-open]:ease-out', 'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:duration-fast data-[state=closed]:ease-in', className), ...rest }) }));
});
Content$2.displayName = 'Tooltip.Content';
const TooltipRootWrapper = ((props) => jsx(RadixTooltip.Root, { ...props }));
TooltipRootWrapper.displayName = 'Tooltip';
const Tooltip = Object.assign(TooltipRootWrapper, {
    Root: TooltipRootWrapper,
    Provider: Provider$1,
    Trigger: Trigger$2,
    Content: Content$2,
});
const TooltipProvider = Provider$1;
const TooltipTrigger = Trigger$2;
const TooltipContent = Content$2;

const Trigger$1 = RadixMenu.Trigger;
const Group$1 = RadixMenu.Group;
const Sub$1 = RadixMenu.Sub;
const RadioGroup$1 = RadixMenu.RadioGroup;
const Portal$1 = RadixMenu.Portal;
const Content$1 = React.forwardRef(({ className, style, sideOffset = 4, ...rest }, ref) => {
    const scope = useSurfaceTheme();
    return (jsx(Portal$1, { children: jsx(RadixMenu.Content, { ref: ref, style: { ...scope.style, ...style }, sideOffset: sideOffset, className: cn(scope.className, 'z-popover min-w-[10rem] overflow-hidden rounded-lg border border-border bg-surface p-1 text-surface-foreground shadow-md', 'origin-[var(--radix-dropdown-menu-content-transform-origin)]', 'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=open]:duration-default data-[state=open]:ease-out', 'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=closed]:duration-fast data-[state=closed]:ease-in', className), ...rest }) }));
});
Content$1.displayName = 'DropdownMenu.Content';
const itemBase$1 = 'flex cursor-default select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none data-[highlighted]:bg-muted data-[disabled]:pointer-events-none data-[disabled]:opacity-50';
const Item$2 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixMenu.Item, { ref: ref, className: cn(itemBase$1, className), ...rest })));
Item$2.displayName = 'DropdownMenu.Item';
const CheckboxItem$1 = React.forwardRef(({ className, children, ...rest }, ref) => (jsxs(RadixMenu.CheckboxItem, { ref: ref, className: cn(itemBase$1, 'pl-7 relative', className), ...rest, children: [jsx("span", { className: "absolute left-2 inline-flex h-3.5 w-3.5 items-center justify-center", children: jsx(RadixMenu.ItemIndicator, { children: jsx(Icon$1, { icon: Check, size: 12 }) }) }), children] })));
CheckboxItem$1.displayName = 'DropdownMenu.CheckboxItem';
const RadioItem$1 = React.forwardRef(({ className, children, ...rest }, ref) => (jsxs(RadixMenu.RadioItem, { ref: ref, className: cn(itemBase$1, 'pl-7 relative', className), ...rest, children: [jsx("span", { className: "absolute left-2 inline-flex h-3.5 w-3.5 items-center justify-center", children: jsx(RadixMenu.ItemIndicator, { children: jsx(Icon$1, { icon: Circle, size: 12, className: "fill-current" }) }) }), children] })));
RadioItem$1.displayName = 'DropdownMenu.RadioItem';
const Label$1 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixMenu.Label, { ref: ref, className: cn('px-2 py-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground', className), ...rest })));
Label$1.displayName = 'DropdownMenu.Label';
const Separator$1 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixMenu.Separator, { ref: ref, className: cn('-mx-1 my-1 h-px bg-border', className), ...rest })));
Separator$1.displayName = 'DropdownMenu.Separator';
const Shortcut$1 = ({ className, ...rest }) => (jsx("span", { className: cn('ml-auto text-xs text-muted-foreground', className), ...rest }));
const SubTrigger$1 = React.forwardRef(({ className, children, ...rest }, ref) => (jsxs(RadixMenu.SubTrigger, { ref: ref, className: cn(itemBase$1, className), ...rest, children: [children, jsx(Icon$1, { icon: CaretRight, size: 12, className: "ml-auto" })] })));
SubTrigger$1.displayName = 'DropdownMenu.SubTrigger';
const SubContent$1 = React.forwardRef(({ className, style, ...rest }, ref) => {
    const scope = useSurfaceTheme();
    return (jsx(RadixMenu.SubContent, { ref: ref, style: { ...scope.style, ...style }, className: cn(scope.className, 'z-popover min-w-[10rem] overflow-hidden rounded-lg border border-border bg-surface p-1 shadow-md', className), ...rest }));
});
SubContent$1.displayName = 'DropdownMenu.SubContent';
const DropdownMenuRootWrapper = ((props) => jsx(RadixMenu.Root, { ...props }));
DropdownMenuRootWrapper.displayName = 'DropdownMenu';
const DropdownMenu = Object.assign(DropdownMenuRootWrapper, {
    Root: DropdownMenuRootWrapper,
    Trigger: Trigger$1,
    Portal: Portal$1,
    Content: Content$1,
    Group: Group$1,
    Item: Item$2,
    CheckboxItem: CheckboxItem$1,
    RadioGroup: RadioGroup$1,
    RadioItem: RadioItem$1,
    Label: Label$1,
    Separator: Separator$1,
    Shortcut: Shortcut$1,
    Sub: Sub$1,
    SubTrigger: SubTrigger$1,
    SubContent: SubContent$1,
});
const DropdownMenuTrigger = Trigger$1;
const DropdownMenuPortal = Portal$1;
const DropdownMenuContent = Content$1;
const DropdownMenuGroup = Group$1;
const DropdownMenuItem = Item$2;
const DropdownMenuCheckboxItem = CheckboxItem$1;
const DropdownMenuRadioGroup = RadioGroup$1;
const DropdownMenuRadioItem = RadioItem$1;
const DropdownMenuLabel = Label$1;
const DropdownMenuSeparator = Separator$1;
const DropdownMenuShortcut = Shortcut$1;
const DropdownMenuSub = Sub$1;
const DropdownMenuSubTrigger = SubTrigger$1;
const DropdownMenuSubContent = SubContent$1;

const Trigger = RadixCtxMenu.Trigger;
const Group = RadixCtxMenu.Group;
const Sub = RadixCtxMenu.Sub;
const RadioGroup = RadixCtxMenu.RadioGroup;
const Portal = RadixCtxMenu.Portal;
const Content = React.forwardRef(({ className, ...rest }, ref) => (jsx(Portal, { children: jsx(RadixCtxMenu.Content, { ref: ref, className: cn('z-popover min-w-[10rem] overflow-hidden rounded-lg border border-border bg-surface p-1 text-surface-foreground shadow-md', className), ...rest }) })));
Content.displayName = 'ContextMenu.Content';
const itemBase = 'flex cursor-default select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none data-[highlighted]:bg-muted data-[disabled]:pointer-events-none data-[disabled]:opacity-50';
const Item$1 = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixCtxMenu.Item, { ref: ref, className: cn(itemBase, className), ...rest })));
Item$1.displayName = 'ContextMenu.Item';
const CheckboxItem = React.forwardRef(({ className, children, ...rest }, ref) => (jsxs(RadixCtxMenu.CheckboxItem, { ref: ref, className: cn(itemBase, 'pl-7 relative', className), ...rest, children: [jsx("span", { className: "absolute left-2 inline-flex h-3.5 w-3.5 items-center justify-center", children: jsx(RadixCtxMenu.ItemIndicator, { children: jsx(Icon$1, { icon: Check, size: 12 }) }) }), children] })));
CheckboxItem.displayName = 'ContextMenu.CheckboxItem';
const RadioItem = React.forwardRef(({ className, children, ...rest }, ref) => (jsxs(RadixCtxMenu.RadioItem, { ref: ref, className: cn(itemBase, 'pl-7 relative', className), ...rest, children: [jsx("span", { className: "absolute left-2 inline-flex h-3.5 w-3.5 items-center justify-center", children: jsx(RadixCtxMenu.ItemIndicator, { children: jsx(Icon$1, { icon: Circle, size: 12, className: "fill-current" }) }) }), children] })));
RadioItem.displayName = 'ContextMenu.RadioItem';
const Label = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixCtxMenu.Label, { ref: ref, className: cn('px-2 py-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground', className), ...rest })));
Label.displayName = 'ContextMenu.Label';
const Separator = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixCtxMenu.Separator, { ref: ref, className: cn('-mx-1 my-1 h-px bg-border', className), ...rest })));
Separator.displayName = 'ContextMenu.Separator';
const Shortcut = ({ className, ...rest }) => (jsx("span", { className: cn('ml-auto text-xs text-muted-foreground', className), ...rest }));
const SubTrigger = React.forwardRef(({ className, children, ...rest }, ref) => (jsxs(RadixCtxMenu.SubTrigger, { ref: ref, className: cn(itemBase, className), ...rest, children: [children, jsx(Icon$1, { icon: CaretRight, size: 12, className: "ml-auto" })] })));
SubTrigger.displayName = 'ContextMenu.SubTrigger';
const SubContent = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixCtxMenu.SubContent, { ref: ref, className: cn('z-popover min-w-[10rem] overflow-hidden rounded-lg border border-border bg-surface p-1 shadow-md', className), ...rest })));
SubContent.displayName = 'ContextMenu.SubContent';
const ContextMenuRootWrapper = ((props) => jsx(RadixCtxMenu.Root, { ...props }));
ContextMenuRootWrapper.displayName = 'ContextMenu';
const ContextMenu = Object.assign(ContextMenuRootWrapper, {
    Root: ContextMenuRootWrapper,
    Trigger,
    Portal,
    Content,
    Group,
    Item: Item$1,
    CheckboxItem,
    RadioGroup,
    RadioItem,
    Label,
    Separator,
    Shortcut,
    Sub,
    SubTrigger,
    SubContent,
});
const ContextMenuTrigger = Trigger;
const ContextMenuPortal = Portal;
const ContextMenuContent = Content;
const ContextMenuGroup = Group;
const ContextMenuItem = Item$1;
const ContextMenuCheckboxItem = CheckboxItem;
const ContextMenuRadioGroup = RadioGroup;
const ContextMenuRadioItem = RadioItem;
const ContextMenuLabel = Label;
const ContextMenuSeparator = Separator;
const ContextMenuShortcut = Shortcut;
const ContextMenuSub = Sub;
const ContextMenuSubTrigger = SubTrigger;
const ContextMenuSubContent = SubContent;

const badgeStyles$1 = cva('inline-flex items-center gap-1 rounded-sm font-semibold uppercase tracking-[0.04em] transition-colors duration-slow leading-none align-middle', {
    variants: {
        variant: {
            subtle: '',
            outline: 'bg-transparent border',
        },
        tone: {
            neutral: '',
            success: '',
            warning: '',
            destructive: '',
            info: '',
            accent: '',
        },
        size: {
            xs: 'px-1.5 py-0.5 text-[11px]',
            sm: 'px-2   py-0.5 text-[11px]',
            md: 'px-2.5 py-1   text-[11px]',
        },
    },
    compoundVariants: [
        { variant: 'subtle', tone: 'neutral', class: 'bg-neutral-tone-subtle text-neutral-tone-foreground border border-neutral-tone-border' },
        { variant: 'subtle', tone: 'success', class: 'bg-success-subtle text-success-foreground border border-success-border' },
        { variant: 'subtle', tone: 'warning', class: 'bg-warning-subtle text-warning-foreground border border-warning-border' },
        { variant: 'subtle', tone: 'destructive', class: 'bg-destructive-subtle text-destructive-foreground border border-destructive-border' },
        { variant: 'subtle', tone: 'info', class: 'bg-info-subtle text-info-foreground border border-info-border' },
        { variant: 'subtle', tone: 'accent', class: 'bg-accent-subtle text-accent-on-subtle border border-accent-border' },
        { variant: 'outline', tone: 'neutral', class: 'border-border text-muted-foreground' },
        { variant: 'outline', tone: 'success', class: 'border-success text-success-foreground' },
        { variant: 'outline', tone: 'warning', class: 'border-warning text-warning-foreground' },
        { variant: 'outline', tone: 'destructive', class: 'border-destructive text-destructive-foreground' },
        { variant: 'outline', tone: 'info', class: 'border-info text-info-foreground' },
        { variant: 'outline', tone: 'accent', class: 'border-accent text-accent-on-subtle' },
    ],
    defaultVariants: { variant: 'subtle', tone: 'neutral', size: 'sm' },
});
const V1_VARIANT_TO_V2_TONE = {
    default: 'neutral',
    secondary: 'neutral',
    destructive: 'destructive',
    success: 'success',
    warning: 'warning',
    outline: 'neutral',
};
const Badge$1 = React.forwardRef(({ className, variant, tone, size, ...rest }, ref) => {
    let v2Variant = undefined;
    let resolvedTone = tone;
    if (variant === 'subtle' || variant === 'outline' || variant === undefined || variant === null) {
        v2Variant = variant ?? undefined;
    }
    else {
        const v1 = variant;
        v2Variant = v1 === 'outline' ? 'outline' : 'subtle';
        if (!resolvedTone)
            resolvedTone = V1_VARIANT_TO_V2_TONE[v1];
    }
    return (jsx("span", { ref: ref, className: cn(badgeStyles$1({ variant: v2Variant, tone: resolvedTone, size }), className), ...rest }));
});
Badge$1.displayName = 'Badge';

const dotStyles = cva('inline-block h-1.5 w-1.5 shrink-0 rounded-full transition-colors duration-slow', {
    variants: {
        tone: {
            neutral: 'bg-neutral-tone-foreground',
            success: 'bg-success',
            warning: 'bg-warning',
            destructive: 'bg-destructive',
            info: 'bg-info',
        },
    },
    defaultVariants: { tone: 'neutral' },
});
const pillStyles = cva('inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium transition-colors duration-slow', {
    variants: {
        tone: {
            neutral: 'bg-neutral-tone-subtle text-neutral-tone-foreground border-neutral-tone-border',
            success: 'bg-success-subtle text-success-foreground border-success-border',
            warning: 'bg-warning-subtle text-warning-foreground border-warning-border',
            destructive: 'bg-destructive-subtle text-destructive-foreground border-destructive-border',
            info: 'bg-info-subtle text-info-foreground border-info-border',
        },
    },
    defaultVariants: { tone: 'neutral' },
});
const StatusPill = React.forwardRef(({ className, tone, isLive, children, ...rest }, ref) => {
    const [seen, setSeen] = React.useState({
        tone,
        changed: false,
    });
    if (seen.tone !== tone)
        setSeen({ tone, changed: true });
    return (jsxs("span", { ref: ref, "data-tone": tone ?? 'neutral', className: cn(pillStyles({ tone }), seen.changed && 'animate-status-change', className), ...rest, children: [jsx("span", { "data-testid": "dot", className: cn(dotStyles({ tone }), isLive && 'animate-pulse') }), children] }, String(tone)));
});
StatusPill.displayName = 'StatusPill';

const avatarStyles = cva('inline-flex shrink-0 overflow-hidden rounded-full bg-primary text-primary-foreground', {
    variants: {
        size: {
            sm: 'h-6 w-6 text-[10px]',
            md: 'h-8 w-8 text-xs',
            lg: 'h-10 w-10 text-sm',
        },
    },
    defaultVariants: { size: 'md' },
});
function initialsFrom(name) {
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(w => w[0].toUpperCase())
        .join('');
}
const Avatar = React.forwardRef(({ className, size, src, alt, children, ...rest }, ref) => {
    if (children !== undefined) {
        return (jsx(RadixAvatar.Root, { ref: ref, className: cn(avatarStyles({ size }), className), ...rest, children: children }));
    }
    return (jsxs(RadixAvatar.Root, { ref: ref, className: cn(avatarStyles({ size }), className), ...rest, children: [src && (jsx(RadixAvatar.Image, { src: src, alt: alt ?? '', className: "h-full w-full object-cover" })), jsx(RadixAvatar.Fallback, { className: "flex h-full w-full items-center justify-center font-semibold", children: alt ? initialsFrom(alt) : '' })] }));
});
Avatar.displayName = 'Avatar';
const AvatarImage = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixAvatar.Image, { ref: ref, className: cn('aspect-square h-full w-full object-cover', className), ...rest })));
AvatarImage.displayName = 'AvatarImage';
const AvatarFallback = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixAvatar.Fallback, { ref: ref, className: cn('flex h-full w-full items-center justify-center rounded-full bg-muted font-semibold', className), ...rest })));
AvatarFallback.displayName = 'AvatarFallback';

const Skeleton = React.forwardRef(({ className, ...rest }, ref) => (jsx("div", { ref: ref, className: cn('animate-pulse rounded-md bg-muted', className), ...rest })));
Skeleton.displayName = 'Skeleton';

const easeOut = (t) => 1 - Math.pow(1 - t, 3);
const prefersReducedMotion = () => typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const AnimatedNumber = React.forwardRef(({ value, format, durationMs = 400, showDirection = false, className, ...rest }, ref) => {
    const render = format ?? ((n) => Math.round(n).toLocaleString());
    const [shown, setShown] = React.useState(value);
    const [direction, setDirection] = React.useState(null);
    const from = React.useRef(value);
    const mounted = React.useRef(false);
    const frame = React.useRef();
    React.useEffect(() => {
        if (!mounted.current) {
            mounted.current = true;
            from.current = value;
            setShown(value);
            return;
        }
        if (value === from.current)
            return;
        const start = from.current;
        const delta = value - start;
        setDirection(delta > 0 ? 'up' : 'down');
        if (prefersReducedMotion() || typeof requestAnimationFrame === 'undefined') {
            from.current = value;
            setShown(value);
            return;
        }
        const t0 = performance.now();
        const step = (now) => {
            const t = Math.min(1, (now - t0) / durationMs);
            setShown(start + delta * easeOut(t));
            if (t < 1) {
                frame.current = requestAnimationFrame(step);
            }
            else {
                from.current = value;
            }
        };
        frame.current = requestAnimationFrame(step);
        return () => {
            if (frame.current !== undefined)
                cancelAnimationFrame(frame.current);
            from.current = value;
        };
    }, [value, durationMs]);
    React.useEffect(() => {
        if (!direction)
            return;
        const clear = setTimeout(() => setDirection(null), 900);
        return () => clearTimeout(clear);
    }, [direction, value]);
    return (jsx("span", { ref: ref, className: cn('tabular-nums transition-colors duration-slow', showDirection && direction === 'up' && 'text-success', showDirection && direction === 'down' && 'text-destructive', className), ...rest, children: render(shown) }));
});
AnimatedNumber.displayName = 'AnimatedNumber';

function ResponsiveStepper({ forwardedRef, ...props }) {
    const container = React.useRef(null);
    const [width, setWidth] = React.useState();
    React.useEffect(() => {
        const node = container.current;
        if (!node || props.compact !== 'auto')
            return;
        const measure = () => setWidth(node.getBoundingClientRect().width);
        measure();
        if (typeof ResizeObserver === 'undefined')
            return;
        const observer = new ResizeObserver(measure);
        observer.observe(node);
        return () => observer.disconnect();
    }, [props.compact]);
    const { compact, className, ...inner } = props;
    const collapsed = compact === true ||
        (width !== undefined && width < Math.min(900, Math.max(640, props.steps.length * 140)));
    const activeIndex = props.activeStepId !== undefined
        ? props.steps.findIndex((step) => step.id === props.activeStepId)
        : props.steps.findIndex((step) => step.state === 'current');
    const unresolvedIndex = props.steps.findIndex((step) => !connectorFilled(step.state));
    const index = activeIndex >= 0
        ? activeIndex
        : unresolvedIndex >= 0
            ? unresolvedIndex
            : Math.max(0, props.steps.length - 1);
    const active = props.steps[index];
    const progress = props.steps.length
        ? props.steps.reduce((sum, step) => sum +
            (typeof step.progress === 'number'
                ? panelFill(step.progress)
                : connectorFilled(step.state)
                    ? 100
                    : 0), 0) / props.steps.length
        : 0;
    return (jsx("div", { ref: container, className: cn('min-w-0', className), children: collapsed && active ? (jsxs("div", { className: "rounded-xl border border-border bg-surface p-3", children: [jsxs("p", { className: "text-xs text-muted-foreground", children: ["Step ", index + 1, " of ", props.steps.length] }), jsx("p", { className: "mt-1 text-sm font-semibold", children: active.label }), (active.blocker || active.detail) && (jsx("p", { className: cn('mt-1 text-xs', active.blocker ? 'text-warning-foreground' : 'text-muted-foreground'), children: active.blocker ?? active.detail })), props.variant !== 'form' && jsx("div", { role: "progressbar", "aria-label": "Completed steps", "aria-valuemin": 0, "aria-valuemax": 100, "aria-valuenow": Math.round(progress), className: "mt-3 h-1 overflow-hidden rounded-full bg-border", children: jsx("div", { className: "h-full bg-primary transition-[width] duration-slow", style: { width: `${progress}%` } }) }), jsxs("details", { className: "mt-3", children: [jsx("summary", { className: "cursor-pointer rounded text-xs font-medium text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", children: "View all steps" }), jsx(Stepper, { ...inner, ref: forwardedRef, activeStepId: active.id, orientation: "vertical", variant: props.variant === 'form' ? 'form' : 'panels', className: "mt-3" })] })] })) : (jsx(Stepper, { ...inner, ref: forwardedRef })) }));
}
const badgeStyles = {
    complete: 'border-primary bg-primary text-primary-foreground',
    current: 'border-primary bg-background text-primary',
    blocked: 'border-warning-border bg-warning-subtle text-warning-foreground',
    upcoming: 'border-border bg-background text-muted-foreground',
    skipped: 'border-dashed border-border bg-background text-muted-foreground',
    failed: 'border-destructive bg-destructive text-destructive-foreground',
};
const labelStyles = {
    complete: 'text-foreground',
    current: 'text-foreground font-semibold',
    blocked: 'text-foreground',
    upcoming: 'text-muted-foreground',
    skipped: 'text-muted-foreground',
    failed: 'text-foreground',
};
function StepGlyph({ state }) {
    if (state === 'complete')
        return jsx(Check, { className: "h-3 w-3" });
    if (state === 'failed')
        return jsx(X, { className: "h-3 w-3" });
    if (state === 'skipped')
        return jsx(Minus, { className: "h-3 w-3" });
    return jsx("span", { className: "h-1 w-1 rounded-full bg-current" });
}
const connectorFilled = (state) => state === 'complete' || state === 'skipped';
const panelFill = (progress) => Math.min(Math.max(progress ?? 0, 0), 100);
const PanelBadge = ({ state, index }) => (jsx("span", { className: cn('flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-semibold', 'transition-colors duration-slow', badgeStyles[state]), children: state === 'complete' ? (jsx(Check, { className: "h-3.5 w-3.5" })) : state === 'failed' ? (jsx(X, { className: "h-3.5 w-3.5" })) : state === 'skipped' ? (jsx(Minus, { className: "h-3.5 w-3.5" })) : (index + 1) }));
const Badge = ({ state }) => (jsx("span", { className: cn('flex h-5 w-5 shrink-0 items-center justify-center rounded-full border', 'transition-colors duration-slow', badgeStyles[state]), children: jsx(StepGlyph, { state: state }) }));
const Stepper = React.forwardRef(({ steps, orientation = 'horizontal', variant = 'track', activeStepId, onStepClick, compact = false, className, style, ...rest }, ref) => {
    const interactive = Boolean(onStepClick);
    const isActiveStep = (step) => activeStepId !== undefined ? step.id === activeStepId : step.state === 'current';
    const highlight = useSlidingHighlight({
        axis: orientation === 'vertical' ? 'vertical' : 'horizontal',
    });
    const { ref: highlightRef, ...highlightHandlers } = highlight.containerProps;
    const setPanelsRef = React.useCallback((node) => {
        highlightRef.current = node;
        if (typeof ref === 'function')
            ref(node);
        else if (ref)
            ref.current = node;
    }, [highlightRef, ref]);
    if (compact)
        return (jsx(ResponsiveStepper, { ...rest, steps: steps, orientation: orientation, variant: variant, ...(activeStepId !== undefined ? { activeStepId } : {}), ...(onStepClick ? { onStepClick } : {}), compact: compact, className: className, style: style, forwardedRef: ref }));
    if (variant === 'form') {
        return (jsx("ol", { ref: ref, ...rest, style: { ...style, ...(orientation === 'horizontal' ? { gridTemplateColumns: `repeat(${Math.max(1, steps.length)}, minmax(0, 1fr))` } : {}) }, className: cn('grid min-w-0 overflow-hidden rounded-xl border border-border bg-surface', orientation === 'vertical' && 'grid-cols-1 sm:grid-cols-2', className), children: steps.map((step, index) => {
                const active = isActiveStep(step);
                const inner = jsxs(Fragment, { children: [jsx("span", { className: cn('flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs', active || step.state === 'complete' ? 'bg-primary text-primary-foreground' : badgeStyles[step.state]), children: step.state === 'complete' ? jsx(Check, { className: "h-3.5 w-3.5" }) : step.state === 'failed' ? jsx(X, { className: "h-3.5 w-3.5" }) : step.state === 'skipped' ? jsx(Minus, { className: "h-3.5 w-3.5" }) : index + 1 }), jsxs("span", { className: "min-w-0", children: [jsx("span", { className: cn('block break-words text-sm font-medium', active ? 'text-primary' : 'text-muted-foreground'), children: step.label }), (step.blocker || step.detail) && jsx("span", { className: cn('mt-1 block break-words text-xs', step.blocker ? 'text-warning-foreground' : 'text-muted-foreground'), children: step.blocker ?? step.detail })] })] });
                const itemClass = 'flex h-full w-full items-start gap-2.5 p-3 text-left';
                return jsx("li", { "aria-current": active ? 'step' : undefined, className: cn('min-w-0 border-b-2', active ? 'border-primary' : 'border-transparent'), style: orientation === 'horizontal' ? { gridRow: 1, gridColumn: index + 1 } : undefined, children: onStepClick ? jsx("button", { type: "button", disabled: step.disabled, "aria-disabled": step.disabled || undefined, title: step.disabled ? step.disabledReason : step.title, onClick: () => onStepClick(step.id), className: cn(itemClass, 'transition-colors hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:cursor-not-allowed'), children: inner }) : jsx("span", { className: itemClass, title: step.title, children: inner }) }, step.id);
            }) }));
    }
    const content = (step) => {
        const inner = (jsxs(Fragment, { children: [jsx(Badge, { state: step.state }), jsxs("span", { className: cn('min-w-0', orientation === 'horizontal' && 'mt-1.5 w-full'), children: [jsx("span", { className: cn('block truncate text-xs font-medium leading-tight transition-colors duration-slow', labelStyles[step.state]), children: step.label }), (step.blocker || (step.disabled && step.disabledReason) || step.detail) && (jsx("span", { className: cn('mt-0.5 block truncate text-[11px]', step.blocker ? 'text-warning-foreground' : 'text-muted-foreground'), children: step.blocker ?? (step.disabled ? step.disabledReason : undefined) ?? step.detail }))] })] }));
        if (!interactive) {
            return (jsx("span", { title: step.disabled ? (step.disabledReason ?? step.title) : step.title, className: cn('relative z-10 flex min-w-0', orientation === 'horizontal'
                    ? 'w-full flex-col items-center px-1 text-center'
                    : 'gap-3'), children: inner }));
        }
        return (jsx("button", { type: "button", title: step.disabled ? (step.disabledReason ?? step.title) : step.title, disabled: step.disabled, "aria-disabled": step.disabled || undefined, onClick: () => onStepClick?.(step.id), className: cn('relative z-10 flex min-w-0 rounded-md transition-colors disabled:cursor-not-allowed disabled:opacity-60', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', orientation === 'horizontal'
                ? 'w-full flex-col items-center px-1 text-center hover:bg-muted'
                : 'w-full gap-3 p-1 text-left hover:bg-muted'), children: inner }));
    };
    if (orientation === 'vertical' && variant === 'panels') {
        const perStep = steps.some((step) => typeof step.progress === 'number');
        return (jsx("div", { className: cn('overflow-hidden rounded-lg border border-border bg-surface', className), children: jsxs("ol", { ref: setPanelsRef, className: "relative flex flex-col", style: style, ...(interactive ? highlightHandlers : {}), ...rest, children: [interactive && (jsx("span", { "aria-hidden": true, className: "pointer-events-none absolute inset-x-0 top-0 bg-neutral-tone-subtle duration-slow ease-move", style: highlight.style })), steps.map((step, index) => {
                        const isLast = index === steps.length - 1;
                        const previous = index > 0 ? steps[index - 1] : undefined;
                        const body = (jsxs(Fragment, { children: [jsxs("span", { className: "-my-3 flex shrink-0 flex-col items-center self-stretch", children: [jsx("span", { className: cn('h-3 w-px', previous
                                                ? connectorFilled(previous.state)
                                                    ? 'bg-primary'
                                                    : 'bg-border'
                                                : 'bg-transparent') }), jsx(PanelBadge, { state: step.state, index: index }), !isLast && (jsx("span", { className: "w-px flex-1 bg-border", children: jsx("span", { className: "block w-full bg-primary transition-[height] duration-slow ease-move", style: { height: connectorFilled(step.state) ? '100%' : '0%' } }) }))] }), jsxs("span", { className: "min-w-0 flex-1", children: [jsx("span", { className: "block text-[10px] font-medium uppercase tracking-wide text-muted-foreground", children: step.eyebrow ?? `Step ${index + 1}` }), jsx("span", { className: cn('block text-sm font-medium leading-snug transition-colors duration-slow', labelStyles[step.state]), children: step.label }), (step.blocker || (step.disabled && step.disabledReason) || step.detail) && (jsx("span", { className: cn('mt-0.5 block text-[11px]', step.blocker ? 'text-warning-foreground' : 'text-muted-foreground'), children: step.blocker ??
                                                (step.disabled ? step.disabledReason : undefined) ??
                                                step.detail })), perStep && (jsx("span", { "aria-hidden": true, className: "mt-2 block h-0.5 w-full bg-border", children: jsx("span", { className: "block h-full bg-primary transition-[width] duration-slow ease-move", style: { width: `${panelFill(step.progress)}%` } }) }))] })] }));
                        const shared = cn('flex w-full items-start gap-3 px-3 py-3 text-left transition-colors', isActiveStep(step) && 'bg-primary-subtle');
                        return (jsx("li", { "aria-current": isActiveStep(step) ? 'step' : undefined, className: cn('relative min-w-0', index > 0 && 'border-t border-border'), children: interactive ? (jsx("button", { type: "button", title: step.disabled ? (step.disabledReason ?? step.title) : step.title, disabled: step.disabled, "aria-disabled": step.disabled || undefined, onClick: () => onStepClick?.(step.id), className: cn(shared, 'relative focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60'), children: body })) : (jsx("span", { title: step.disabled ? (step.disabledReason ?? step.title) : step.title, className: shared, children: body })) }, step.id));
                    })] }) }));
    }
    if (orientation === 'vertical') {
        return (jsx("ol", { ref: ref, className: cn('flex flex-col', className), style: style, ...rest, children: steps.map((step, index) => {
                const isLast = index === steps.length - 1;
                return (jsxs("li", { "aria-current": isActiveStep(step) ? 'step' : undefined, className: "flex gap-3", children: [jsxs("div", { className: "flex flex-col items-center", children: [jsx(Badge, { state: step.state }), !isLast && (jsx("span", { className: "my-1 w-px flex-1 bg-border", children: jsx("span", { className: "block w-full bg-primary transition-[height] duration-slow ease-move", style: { height: connectorFilled(step.state) ? '100%' : '0%' } }) }))] }), jsxs("div", { className: cn('min-w-0 flex-1 text-left', isLast ? 'pb-0' : 'pb-6'), children: [jsxs("div", { className: "flex items-start justify-between gap-2", children: [jsxs("div", { className: "min-w-0", children: [jsx("span", { className: cn('block truncate text-sm font-medium transition-colors duration-slow', labelStyles[step.state]), children: step.label }), (step.blocker || (step.disabled && step.disabledReason) || step.detail) && (jsx("span", { className: cn('mt-0.5 block text-xs', step.blocker ? 'text-warning-foreground' : 'text-muted-foreground'), children: step.blocker ??
                                                        (step.disabled ? step.disabledReason : undefined) ??
                                                        step.detail }))] }), step.meta && (jsx("span", { className: "shrink-0 text-xs text-muted-foreground", children: step.meta }))] }), step.description && (jsx("div", { className: "mt-2 flex flex-col gap-0.5 text-left", children: step.description }))] })] }, step.id));
            }) }));
    }
    if (variant === 'panels') {
        const perStep = steps.some((step) => typeof step.progress === 'number');
        const resolved = steps.filter((step) => connectorFilled(step.state)).length;
        const progress = steps.length <= 1 ? 0 : resolved / (steps.length - 1);
        return (jsxs("div", { className: cn('overflow-hidden rounded-lg border border-border bg-surface', className), children: [!perStep && (jsx("div", { className: "h-0.5 w-full bg-border", children: jsx("div", { className: "h-full bg-primary transition-[width] duration-slow ease-move", style: { width: `${Math.min(progress, 1) * 100}%` } }) })), jsxs("ol", { ref: setPanelsRef, className: "relative flex items-stretch", style: style, ...(interactive ? highlightHandlers : {}), ...rest, children: [interactive && (jsx("span", { "aria-hidden": true, className: "pointer-events-none absolute inset-y-0 left-0 bg-neutral-tone-subtle duration-slow ease-move", style: highlight.style })), steps.map((step, index) => {
                            const isActive = isActiveStep(step);
                            const body = (jsxs(Fragment, { children: [jsx(PanelBadge, { state: step.state, index: index }), jsxs("span", { className: "min-w-0", children: [jsx("span", { className: "block text-[10px] font-medium uppercase tracking-wide text-muted-foreground", children: step.eyebrow ?? `Step ${index + 1}` }), jsx("span", { className: cn('block line-clamp-2 text-sm font-medium transition-colors duration-slow', labelStyles[step.state]), children: step.label }), (step.blocker || (step.disabled && step.disabledReason) || step.detail) && (jsx("span", { className: cn('mt-0.5 block truncate text-[11px]', step.blocker ? 'text-warning-foreground' : 'text-muted-foreground'), children: step.blocker ??
                                                    (step.disabled ? step.disabledReason : undefined) ??
                                                    step.detail }))] })] }));
                            const shared = cn('flex w-full items-center gap-3 px-4 py-3 text-left transition-colors', isActive && 'bg-primary-subtle');
                            return (jsxs("li", { "aria-current": isActive ? 'step' : undefined, className: cn('relative min-w-0 flex-1', index > 0 && 'border-l border-border'), children: [perStep && (jsx("span", { "aria-hidden": true, className: "absolute inset-x-0 top-0 z-10 block h-0.5 bg-border", children: jsx("span", { className: "block h-full bg-primary transition-[width] duration-slow ease-move", style: { width: `${panelFill(step.progress)}%` } }) })), interactive ? (jsx("button", { type: "button", title: step.disabled ? (step.disabledReason ?? step.title) : step.title, disabled: step.disabled, "aria-disabled": step.disabled || undefined, onClick: () => onStepClick?.(step.id), className: cn(shared, 'relative focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60'), children: body })) : (jsx("span", { title: step.disabled ? (step.disabledReason ?? step.title) : step.title, className: shared, children: body }))] }, step.id));
                        })] })] }));
    }
    return (jsx("ol", { ref: ref, className: cn('grid', className), ...rest, style: { gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))`, ...style }, children: steps.map((step, index) => (jsxs("li", { "aria-current": isActiveStep(step) ? 'step' : undefined, className: "relative flex min-w-0 flex-col items-center text-center", children: [index < steps.length - 1 && (jsx("span", { "aria-hidden": true, className: "absolute left-1/2 top-2.5 h-px w-full bg-border", children: jsx("span", { className: "block h-full bg-primary transition-[width] duration-slow ease-move", style: { width: connectorFilled(step.state) ? '100%' : '0%' } }) })), content(step)] }, step.id))) }));
});
Stepper.displayName = 'Stepper';

const Root$5 = React.forwardRef(({ className, density = 'default', ...rest }, ref) => (jsx("div", { className: "w-full overflow-x-auto", children: jsx("table", { ref: ref, "data-density": density, className: cn('w-full caption-bottom text-sm', '[&_td]:align-middle [&_th]:align-middle', '[&[data-density="comfortable"]_td]:py-4   [&[data-density="comfortable"]_td]:px-5', '[&[data-density="comfortable"]_th]:py-3.5 [&[data-density="comfortable"]_th]:px-5', '[&[data-density="default"]_td]:py-3      [&[data-density="default"]_td]:px-4', '[&[data-density="default"]_th]:py-3      [&[data-density="default"]_th]:px-4', '[&[data-density="compact"]_td]:py-2      [&[data-density="compact"]_td]:px-3', '[&[data-density="compact"]_th]:py-2      [&[data-density="compact"]_th]:px-3', className), ...rest }) })));
Root$5.displayName = 'Table.Root';
const Header = React.forwardRef(({ className, ...rest }, ref) => (jsx("thead", { ref: ref, className: cn('[&_tr]:border-b [&_tr]:border-border/60', className), ...rest })));
Header.displayName = 'Table.Header';
const Body = React.forwardRef(({ className, ...rest }, ref) => (jsx("tbody", { ref: ref, className: cn('[&_tr:last-child]:border-0', className), ...rest })));
Body.displayName = 'Table.Body';
const Footer = React.forwardRef(({ className, ...rest }, ref) => (jsx("tfoot", { ref: ref, className: cn('border-t border-border/60 font-medium [&_tr]:last:border-b-0', className), ...rest })));
Footer.displayName = 'Table.Footer';
const Row = React.forwardRef(({ className, ...rest }, ref) => (jsx("tr", { ref: ref, className: cn('border-b border-border/40 transition-colors hover:bg-muted/30', className), ...rest })));
Row.displayName = 'Table.Row';
const Head = React.forwardRef(({ className, ...rest }, ref) => (jsx("th", { ref: ref, className: cn('text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground', className), ...rest })));
Head.displayName = 'Table.Head';
const Cell = React.forwardRef(({ className, ...rest }, ref) => (jsx("td", { ref: ref, className: cn('text-foreground', className), ...rest })));
Cell.displayName = 'Table.Cell';
const Caption = React.forwardRef(({ className, ...rest }, ref) => (jsx("caption", { ref: ref, className: cn('mt-4 text-sm text-muted-foreground', className), ...rest })));
Caption.displayName = 'Table.Caption';
const Table = Object.assign(Root$5, {
    Root: Root$5,
    Header,
    Body,
    Footer,
    Row,
    Head,
    Cell,
    Caption,
});
const TableHeader = Header;
const TableBody = Body;
const TableRow = Row;
const TableHead = Head;
const TableCell = Cell;
const TableFooter = Footer;
const TableCaption = Caption;

const Root$4 = React.forwardRef(({ className, density = 'default', ...rest }, ref) => (jsx("div", { ref: ref, "data-density": density, className: cn('divide-y divide-border overflow-hidden rounded-lg border border-border bg-surface', '[&[data-density="comfortable"]_[data-slot=row]]:py-4 [&[data-density="comfortable"]_[data-slot=row]]:px-5', '[&[data-density="default"]_[data-slot=row]]:py-3    [&[data-density="default"]_[data-slot=row]]:px-4', '[&[data-density="compact"]_[data-slot=row]]:py-2    [&[data-density="compact"]_[data-slot=row]]:px-3', className), ...rest })));
Root$4.displayName = 'DataList.Root';
const Item = React.forwardRef(({ className, ...rest }, ref) => (jsx("div", { ref: ref, "data-slot": "row", className: cn('flex items-center gap-3 transition-colors hover:bg-muted/40', className), ...rest })));
Item.displayName = 'DataList.Item';
const DataList = Object.assign(Root$4, {
    Root: Root$4,
    Item,
});
const DataListItem = Item;

const EmptyState = ({ icon, title, description, action, className, compact, }) => (jsxs("div", { className: cn('flex animate-fade-in-up flex-col items-center justify-center text-center', compact ? 'py-10' : 'py-16', className), children: [icon && (jsx("div", { className: "mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground", children: icon })), jsx("p", { className: "text-base font-medium text-foreground", children: title }), description && jsx("p", { className: "mt-1 max-w-sm text-sm text-muted-foreground", children: description }), action && jsx("div", { className: "mt-4", children: action })] }));

const moveColumn = (order, id, delta) => {
    const from = order.indexOf(id);
    const to = from + delta;
    if (from === -1 || to < 0 || to >= order.length)
        return order;
    const next = [...order];
    next.splice(to, 0, ...next.splice(from, 1));
    return next;
};
const orderColumns = (columns, order) => {
    if (!order?.length)
        return columns;
    const byId = new Map(columns.map((c) => [c.id, c]));
    const ordered = order.map((id) => byId.get(id)).filter((c) => Boolean(c));
    const rest = columns.filter((c) => !order.includes(c.id));
    return [...ordered, ...rest];
};
const ColumnSettingsMenu = ({ columns, hidden, onChange, align = 'end', size = 'sm', order, onOrderChange, }) => {
    const reorderable = Boolean(order && onOrderChange);
    const ids = order?.length ? order.filter((id) => columns.some((c) => c.id === id)) : columns.map((c) => c.id);
    const shown = orderColumns(columns, ids);
    return (jsxs(DropdownMenu, { children: [jsx(DropdownMenuTrigger, { asChild: true, children: jsxs(Button, { variant: "outline", size: size, children: [jsx(SlidersHorizontal, { className: "mr-2 h-4 w-4" }), "Columns"] }) }), jsxs(DropdownMenuContent, { align: align, className: reorderable ? 'w-64' : 'w-48', children: [jsx(DropdownMenuLabel, { children: reorderable ? 'Show and order columns' : 'Toggle columns' }), jsx(DropdownMenuSeparator, {}), shown
                        .filter((c) => c.hideable !== false)
                        .map((c, index) => (jsxs("div", { className: "flex items-center", children: [jsx(DropdownMenuCheckboxItem, { className: "flex-1", checked: !hidden.has(c.id), onCheckedChange: (checked) => {
                                    const next = new Set(hidden);
                                    checked ? next.delete(c.id) : next.add(c.id);
                                    onChange(next);
                                }, children: c.menuLabel ?? (typeof c.header === 'string' ? c.header : c.id) }), reorderable && (jsxs("span", { className: "flex shrink-0 pr-1", children: [jsx("button", { type: "button", "aria-label": `Move ${c.menuLabel ?? c.id} earlier`, disabled: index === 0, className: "rounded p-1 text-muted-foreground hover:bg-muted disabled:opacity-30", onSelect: (event) => event.preventDefault(), onClick: (event) => {
                                            event.preventDefault();
                                            onOrderChange(moveColumn(ids, c.id, -1));
                                        }, children: jsx(ArrowUp, { className: "h-3 w-3" }) }), jsx("button", { type: "button", "aria-label": `Move ${c.menuLabel ?? c.id} later`, disabled: index === shown.length - 1, className: "rounded p-1 text-muted-foreground hover:bg-muted disabled:opacity-30", onSelect: (event) => event.preventDefault(), onClick: (event) => {
                                            event.preventDefault();
                                            onOrderChange(moveColumn(ids, c.id, 1));
                                        }, children: jsx(ArrowDown, { className: "h-3 w-3" }) })] }))] }, c.id)))] })] }));
};
const alignClass = (align) => align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
const isNumericColumn = (col) => col.numeric ?? col.align === 'right';
const numericClass = (col) => isNumericColumn(col) ? 'tabular-nums' : undefined;
const nextSort = (current, columnId) => {
    if (!current || current.columnId !== columnId)
        return { columnId, direction: 'asc' };
    if (current.direction === 'asc')
        return { columnId, direction: 'desc' };
    return null;
};
function DataTable({ data, columns, rowKey, onRowClick, isLoading, emptyMessage = 'No records found', density = 'default', stickyHeader = false, selectable = false, selectedIds, onSelectionChange, bulkActions, sort, onSortChange, enableColumnVisibility = true, hiddenColumns, onHiddenColumnsChange, columnOrder, onColumnOrderChange, toolbar, footer, tableClassName, }) {
    const [internalHidden, setInternalHidden] = React.useState(() => new Set(columns.filter((c) => c.defaultHidden).map((c) => c.id)));
    const isControlled = hiddenColumns !== undefined;
    const hidden = hiddenColumns ?? internalHidden;
    const setHidden = (next) => {
        if (isControlled)
            onHiddenColumnsChange?.(next);
        else
            setInternalHidden(next);
    };
    const visibleColumns = orderColumns(columns, columnOrder).filter((c) => !hidden.has(c.id));
    const selected = React.useMemo(() => new Set(selectedIds ?? []), [selectedIds]);
    const allKeys = React.useMemo(() => data.map(rowKey), [data, rowKey]);
    const allSelected = allKeys.length > 0 && allKeys.every((k) => selected.has(k));
    const someSelected = allKeys.some((k) => selected.has(k));
    const clearSelection = () => onSelectionChange?.([]);
    const toggleAll = () => onSelectionChange?.(allSelected ? [] : allKeys);
    const toggleRow = (key) => {
        const next = new Set(selected);
        next.has(key) ? next.delete(key) : next.add(key);
        onSelectionChange?.(Array.from(next));
    };
    const colSpan = visibleColumns.length + (selectable ? 1 : 0);
    const showToolbar = Boolean(toolbar) || enableColumnVisibility;
    const selectionCount = selected.size;
    const MAX_ANIMATED_ROWS = 30;
    const animateRows = !isLoading && density !== 'compact' && data.length <= MAX_ANIMATED_ROWS;
    const rowTransitions = useRowTransitions(allKeys, animateRows);
    return (jsxs("div", { className: "w-full space-y-6", children: [selectable && selectionCount > 0 ? (jsxs("div", { className: "flex items-center justify-between rounded-lg border border-border bg-surface px-3 py-2", children: [jsxs("div", { className: "flex items-center gap-2 text-sm", children: [jsx(Button, { variant: "ghost", size: "sm", onClick: clearSelection, "aria-label": "Clear selection", children: jsx(X, { className: "h-4 w-4" }) }), jsxs("span", { className: "font-medium", children: [selectionCount, " selected"] })] }), jsx("div", { className: "flex items-center gap-2", children: bulkActions?.({ selectedIds: Array.from(selected), clear: clearSelection }) })] })) : showToolbar ? (jsxs("div", { className: "flex items-center justify-between gap-3", children: [jsx("div", { className: "min-w-0 flex-1", children: toolbar }), enableColumnVisibility && (jsx(ColumnSettingsMenu, { columns: columns, hidden: hidden, onChange: setHidden }))] })) : null, jsx("div", { className: cn('w-full overflow-x-auto rounded-xl border border-border bg-surface', stickyHeader && 'max-h-[70vh] overflow-auto'), children: jsxs(Table, { density: density, className: tableClassName, children: [jsx(Table.Header, { className: cn(stickyHeader && '[&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-surface'), children: jsxs(Table.Row, { children: [selectable && (jsx(Table.Head, { className: "w-10", children: jsx(Checkbox, { className: "flex", "aria-label": "Select all rows", checked: allSelected ? true : someSelected ? 'indeterminate' : false, onCheckedChange: toggleAll }) })), visibleColumns.map((col) => {
                                        const active = sort?.columnId === col.id;
                                        const sortable = col.sortable && onSortChange;
                                        return (jsx(Table.Head, { className: cn(alignClass(col.align), numericClass(col), sortable && 'cursor-pointer select-none', col.headerClassName), style: col.width ? { width: col.width } : undefined, onClick: sortable ? () => onSortChange(nextSort(sort ?? null, col.id)) : undefined, "aria-sort": active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined, children: jsxs("span", { className: cn('inline-flex items-center gap-1', col.align === 'right' && 'flex-row-reverse'), children: [col.header, sortable && (active ? (sort.direction === 'asc' ? jsx(ArrowUp, { className: "h-3 w-3" }) : jsx(ArrowDown, { className: "h-3 w-3" })) : (jsx(CaretUpDown, { className: "h-3 w-3 opacity-40" })))] }) }, col.id));
                                    })] }) }), jsx(Table.Body, { children: isLoading ? (Array.from({ length: 5 }).map((_, i) => (jsx(Table.Row, { children: jsx(Table.Cell, { colSpan: colSpan, children: jsx("div", { className: "h-6 w-full animate-pulse rounded bg-muted" }) }) }, `sk-${i}`)))) : data.length === 0 ? (jsx(Table.Row, { children: jsx(Table.Cell, { colSpan: colSpan, className: "p-0", children: typeof emptyMessage === 'string' ? (jsx(EmptyState, { title: emptyMessage, compact: true })) : (emptyMessage) }) })) : (data.map((row, rowIndex) => {
                                const key = rowKey(row);
                                const isSelected = selected.has(key);
                                return (jsxs(Table.Row, { ref: animateRows ? rowTransitions.register(key) : undefined, "data-selected": isSelected || undefined, className: cn(onRowClick && 'cursor-pointer', isSelected && 'bg-primary/5', rowTransitions.isEntering(key) && 'animate-fade-in-up'), onClick: onRowClick ? () => onRowClick(row) : undefined, children: [selectable && (jsx(Table.Cell, { className: "w-10", onClick: (e) => e.stopPropagation(), children: jsx(Checkbox, { className: "flex", "aria-label": "Select row", checked: isSelected, onCheckedChange: () => toggleRow(key) }) })), visibleColumns.map((col) => (jsx(Table.Cell, { className: cn(alignClass(col.align), numericClass(col), col.className), children: col.cell(row, rowIndex) }, col.id)))] }, key));
                            })) })] }) }), footer] }));
}

const toneIcon = {
    primary: 'text-primary',
    success: 'text-success-foreground',
    warning: 'text-warning-foreground',
    destructive: 'text-destructive-foreground',
    info: 'text-info-foreground',
    neutral: 'text-muted-foreground',
};
const MIN_METRIC_WIDTH = 200;
const EDGE_FADE = 'pointer-events-none absolute inset-y-0 w-12 from-surface to-transparent transition-opacity duration-200 motion-reduce:transition-none';
const RibbonStrip = ({ className, children, ...rest }) => {
    const scroller = React.useRef(null);
    const [edges, setEdges] = React.useState({ left: false, right: false });
    const measure = React.useCallback(() => {
        const el = scroller.current;
        if (!el)
            return;
        const left = el.scrollLeft > 1;
        const right = el.scrollLeft < el.scrollWidth - el.clientWidth - 1;
        setEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
    }, []);
    React.useLayoutEffect(measure);
    React.useEffect(() => {
        const el = scroller.current;
        if (!el)
            return;
        el.addEventListener('scroll', measure, { passive: true });
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        ro?.observe(el);
        return () => {
            el.removeEventListener('scroll', measure);
            ro?.disconnect();
        };
    }, [measure]);
    return (jsxs("div", { className: cn('relative overflow-hidden rounded-xl border border-border bg-surface', className), ...rest, children: [jsx("div", { ref: scroller, className: "flex flex-nowrap items-stretch divide-x divide-border overflow-x-auto", children: children }), jsx("span", { "aria-hidden": "true", className: cn(EDGE_FADE, 'left-0 bg-gradient-to-r', edges.left ? 'opacity-100' : 'opacity-0') }), jsx("span", { "aria-hidden": "true", className: cn(EDGE_FADE, 'right-0 bg-gradient-to-l', edges.right ? 'opacity-100' : 'opacity-0') })] }));
};
const MetricRibbon = ({ items, isLoading, className, ...rest }) => {
    if (isLoading) {
        return (jsx(RibbonStrip, { className: className, ...rest, children: Array.from({ length: Math.max(items.length || 4, 4) }).map((_, i) => (jsx("div", { className: "flex min-h-[92px] flex-1 items-center gap-3 px-4 py-3", style: { minWidth: MIN_METRIC_WIDTH }, children: jsxs("div", { className: "min-w-0 flex-1", children: [jsx("div", { className: "h-2.5 w-1/2 animate-pulse rounded bg-muted" }), jsx("div", { className: "mt-2 h-6 w-3/4 animate-pulse rounded bg-muted" })] }) }, i))) }));
    }
    return (jsx(RibbonStrip, { className: className, ...rest, children: items.map((item) => {
            const tone = item.tone ?? 'neutral';
            const interactive = Boolean(item.onClick);
            const Comp = interactive ? 'button' : 'div';
            return (jsxs(Comp, { type: interactive ? 'button' : undefined, onClick: item.onClick, "aria-pressed": interactive ? item.active : undefined, className: cn('group flex min-h-[92px] flex-1 items-center gap-3 px-4 py-3 text-left transition-colors', interactive && 'cursor-pointer hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', item.active && 'bg-primary/5'), style: { minWidth: MIN_METRIC_WIDTH }, children: [jsxs("span", { className: "min-w-0 flex-1", children: [jsxs("span", { className: "block line-clamp-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground", title: typeof item.label === 'string' ? item.label : undefined, children: [item.icon && (jsx("span", { "aria-hidden": "true", className: cn('mr-1 inline-flex align-[-0.15em] [&_svg]:h-[1.1em] [&_svg]:w-[1.1em]', toneIcon[tone], interactive && 'group-hover:text-primary'), children: item.icon })), item.label] }), jsxs("span", { className: "mt-1 flex items-baseline gap-1.5", children: [typeof item.value === 'number' ? (jsx(AnimatedNumber, { value: item.value, className: "truncate text-xl font-semibold leading-tight tracking-tight text-foreground" })) : (jsx("span", { className: "whitespace-nowrap text-xl font-semibold leading-tight tracking-tight tabular-nums text-foreground", children: item.value })), item.delta && (jsxs("span", { className: cn('inline-flex items-center gap-0.5 text-xs font-medium', item.delta.direction === 'up' && 'text-success', item.delta.direction === 'down' && 'text-destructive', item.delta.direction === 'flat' && 'text-muted-foreground'), children: [item.delta.direction === 'up' && jsx(ArrowUp, { className: "h-3 w-3" }), item.delta.direction === 'down' && jsx(ArrowDown, { className: "h-3 w-3" }), item.delta.direction === 'flat' && jsx(Minus, { className: "h-3 w-3" }), item.delta.label] }))] }), item.hint &&
                                (typeof item.hint === 'string' ? (jsx("span", { className: "mt-1 block line-clamp-2 text-xs text-muted-foreground", title: item.hint, children: item.hint })) : (jsx("span", { className: "mt-1 block text-xs text-muted-foreground", children: item.hint })))] }), item.actions && jsx("span", { className: "shrink-0 self-start", children: item.actions }), interactive && (jsx(CaretRight, { "aria-hidden": "true", className: "h-4 w-4 shrink-0 self-center text-muted-foreground/50 transition-colors group-hover:text-primary" }))] }, item.id));
        }) }));
};

const ScrollArea = React.forwardRef(({ className, children, ...rest }, ref) => (jsx("div", { ref: ref, className: cn('relative overflow-auto', className), ...rest, children: children })));
ScrollArea.displayName = 'ScrollArea';
const ScrollBar = React.forwardRef(() => null);
ScrollBar.displayName = 'ScrollBar';

const ALL = '__all__';
const GAP = 12;
const SEARCH_MIN = 220;
const CUSTOM_FALLBACK = 150;
function useHiddenCount(enabled, filters, hasClear, refs) {
    const [count, setCount] = React.useState(0);
    const measured = React.useRef(new Map());
    React.useLayoutEffect(() => {
        const row = refs.row.current;
        if (!enabled || !row || typeof ResizeObserver === 'undefined')
            return;
        const widthOf = (f) => {
            if (f.type === 'select')
                return f.width ?? 180;
            if (f.type === 'search')
                return SEARCH_MIN;
            const el = refs.items.current.get(f.key);
            if (el)
                measured.current.set(f.key, el.offsetWidth);
            return measured.current.get(f.key) ?? CUSTOM_FALLBACK;
        };
        const check = () => {
            const fixed = (refs.trailing.current?.offsetWidth ?? 0) + (refs.clear.current ? refs.clear.current.offsetWidth : 0);
            const extras = fixed ? GAP + fixed : 0;
            const pinned = filters.filter((f) => f.pinned);
            const optional = filters.filter((f) => !f.pinned);
            let used = pinned.reduce((sum, f, i) => sum + (i ? GAP : 0) + widthOf(f), 0) + extras;
            let fit = 0;
            for (const f of optional) {
                const next = used + GAP + widthOf(f);
                if (next > row.clientWidth)
                    break;
                used = next;
                fit++;
            }
            setCount(optional.length - fit);
        };
        check();
        const ro = new ResizeObserver(check);
        ro.observe(row);
        if (refs.trailing.current)
            ro.observe(refs.trailing.current);
        return () => ro.disconnect();
    }, [enabled, hasClear, filters.map((f) => f.key).join('|')]);
    return enabled ? count : 0;
}
const FilterBar = ({ filters, hasActiveFilters, onClear, clearLabel = 'Clear', trailing, collapsible = false, className, }) => {
    const row = React.useRef(null);
    const trailingRef = React.useRef(null);
    const clearRef = React.useRef(null);
    const items = React.useRef(new Map());
    const hiddenCount = useHiddenCount(collapsible, filters, hasActiveFilters, { row, trailing: trailingRef, clear: clearRef, items });
    const optional = filters.filter((f) => !f.pinned);
    const hidden = new Set(optional.slice(optional.length - hiddenCount).map((f) => f.key));
    const shown = filters.filter((f) => !hidden.has(f.key));
    const tight = collapsible && optional.length > 0 && hiddenCount === optional.length;
    const overflow = { hidden: filters.filter((f) => hidden.has(f.key)).map((f) => f.key), compact: hiddenCount > 0 };
    const trailingNode = typeof trailing === 'function' ? trailing(overflow) : trailing;
    return (jsx(Card, { className: className, children: jsx(CardContent, { className: "p-4", children: jsxs("div", { ref: row, className: "flex flex-nowrap items-center gap-3 overflow-x-auto", children: [shown.map((filter) => {
                        if (filter.type === 'search') {
                            return (jsxs("div", { className: cn('relative', tight ? 'min-w-[120px]' : 'min-w-[220px]', filter.grow !== false && 'flex-1'), children: [jsx(MagnifyingGlass, { className: "pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" }), jsx(Input$1, { type: "text", placeholder: filter.placeholder ?? 'Search...', value: filter.value, onChange: (e) => filter.onChange(e.target.value), className: "pl-9" })] }, filter.key));
                        }
                        if (filter.type === 'custom') {
                            return (jsx("div", { className: "flex shrink-0 items-center", ref: (el) => {
                                    if (el)
                                        items.current.set(filter.key, el);
                                    else
                                        items.current.delete(filter.key);
                                }, children: filter.render() }, filter.key));
                        }
                        return (jsxs(Select, { value: filter.value ?? ALL, onValueChange: (value) => filter.onChange(value === ALL ? undefined : value), children: [jsx(SelectTrigger, { "aria-label": filter.placeholder, className: tight ? 'min-w-[120px] shrink' : 'shrink-0', style: { width: filter.width ?? 180 }, children: jsx(SelectValue, { placeholder: filter.placeholder }) }), jsxs(SelectContent, { children: [jsx(SelectItem, { value: ALL, children: filter.placeholder }), filter.options.map((option) => (jsx(SelectItem, { value: option.value, children: option.label }, option.value)))] })] }, filter.key));
                    }), hasActiveFilters && (jsxs(Button, { ref: clearRef, variant: "outline", size: "sm", className: "shrink-0", onClick: onClear, children: [jsx(X, { className: "mr-2 h-4 w-4" }), clearLabel] })), trailingNode && (jsx("div", { ref: trailingRef, className: "ml-auto flex shrink-0 items-center gap-2", children: trailingNode }))] }) }) }));
};

const EM_DASH = '—';
function isEmptyValue(value) {
    if (value == null)
        return true;
    if (typeof value === 'string')
        return value.trim() === '';
    if (Array.isArray(value))
        return value.length === 0;
    return false;
}
const BareContext = React.createContext(false);
const Root$3 = React.forwardRef(({ className, bare = false, children, ...rest }, ref) => (jsx("div", { ref: ref, className: cn('overflow-hidden', !bare && 'rounded-lg border border-border bg-surface', className), ...rest, children: jsx(BareContext.Provider, { value: bare, children: children }) })));
Root$3.displayName = 'DetailPanel.Root';
const COLS = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
};
const NTH_LAST_ROW = {
    1: '[&>*:last-child]:border-b-0',
    2: '[&>*:nth-last-child(-n+2)]:border-b-0',
    3: '[&>*:nth-last-child(-n+3)]:border-b-0',
};
function hasVisibleContent(children) {
    let sawField = false;
    let sawPopulatedField = false;
    let sawOther = false;
    React.Children.forEach(children, (child) => {
        if (child == null || typeof child === 'boolean')
            return;
        if (React.isValidElement(child) && child.type === Field) {
            sawField = true;
            if (!isEmptyValue(child.props.value))
                sawPopulatedField = true;
            return;
        }
        sawOther = true;
    });
    if (sawOther)
        return true;
    if (sawField)
        return sawPopulatedField;
    return false;
}
const Section = React.forwardRef(({ className, title, columns = 2, action, keepWhenEmpty = false, dividers = false, children, ...rest }, ref) => {
    if (!keepWhenEmpty && !hasVisibleContent(children))
        return null;
    const bare = React.useContext(BareContext);
    const rows = dividers
        ? React.Children.map(children, (child) => React.isValidElement(child) && child.type === Field
            ? React.cloneElement(child, {
                divided: true,
                className: cn('border-b border-border py-2.5', child.props.className),
            })
            : child)
        : children;
    return (jsxs("section", { ref: ref, className: cn(bare ? 'py-4 first:pt-0 last:pb-0' : 'px-5 py-4 first:pt-5 last:pb-5', 'border-t border-border first:border-t-0', className), ...rest, children: [(title || action) && (jsxs("div", { className: "mb-3 flex items-center justify-between gap-3", children: [title && (jsx("h3", { className: "text-xs font-semibold uppercase tracking-wider text-foreground", children: title })), action] })), jsx("dl", { className: cn('grid gap-x-10', dividers ? cn('gap-y-0', NTH_LAST_ROW[columns]) : 'gap-y-2.5', COLS[columns]), children: rows })] }));
});
Section.displayName = 'DetailPanel.Section';
const Field = React.forwardRef(({ className, label, value, full = false, mono = false, hideWhenEmpty = false, divided = false, ...rest }, ref) => {
    const empty = isEmptyValue(value);
    if (empty && hideWhenEmpty)
        return null;
    return (jsxs("div", { ref: ref, className: cn(divided
            ? 'flex items-baseline justify-between gap-4'
            : 'grid grid-cols-[minmax(0,9rem)_1fr] items-baseline gap-x-4', full && 'col-span-full', className), ...rest, children: [jsx("dt", { className: cn('text-xs text-muted-foreground', !divided && 'truncate'), children: label }), jsx("dd", { className: cn('min-w-0 text-sm', divided && 'text-right', mono && 'font-mono tabular-nums', empty ? 'text-muted-foreground' : 'font-medium text-foreground'), children: empty ? EM_DASH : value })] }));
});
Field.displayName = 'DetailPanel.Field';
const DetailPanel = Object.assign(Root$3, { Root: Root$3, Section, Field });

const fillStyles = cva('h-full transition-[width,background-color] duration-slow ease-move', {
    variants: {
        tone: {
            primary: 'bg-primary',
            success: 'bg-success',
            warning: 'bg-warning',
            destructive: 'bg-destructive',
        },
    },
    defaultVariants: { tone: 'primary' },
});
const Progress = React.forwardRef(({ className, value, max = 100, tone, isIndeterminate, ...rest }, ref) => {
    const indeterminate = isIndeterminate ?? value == null;
    return (jsx(RadixProgress.Root, { ref: ref, value: indeterminate ? null : value, max: max, className: cn('relative h-2 w-full overflow-hidden rounded-full bg-muted', className), ...rest, children: jsx(RadixProgress.Indicator, { className: cn(fillStyles({ tone }), indeterminate && 'animate-[shimmer_1500ms_linear_infinite] bg-[length:200%_100%] bg-gradient-to-r from-primary/40 via-primary to-primary/40'), style: { width: indeterminate ? '100%' : `${Math.min(100, ((value ?? 0) / max) * 100)}%` } }) }));
});
Progress.displayName = 'Progress';

const alertStyles = cva('relative w-full rounded-lg border p-4 text-sm', {
    variants: {
        tone: {
            neutral: 'bg-surface border-border text-foreground',
            success: 'bg-success-subtle border-success-border text-success-foreground',
            warning: 'bg-warning-subtle border-warning-border text-warning-foreground',
            destructive: 'bg-destructive-subtle border-destructive-border text-destructive-foreground',
            info: 'bg-info-subtle border-info-border text-info-foreground',
        },
    },
    defaultVariants: { tone: 'neutral' },
});
const Root$2 = React.forwardRef(({ className, tone, role, ...rest }, ref) => (jsx("div", { ref: ref, role: role ?? (tone === 'destructive' ? 'alert' : 'status'), className: cn(alertStyles({ tone }), className), ...rest })));
Root$2.displayName = 'Alert.Root';
const Title$1 = React.forwardRef(({ className, ...rest }, ref) => (jsx("h4", { ref: ref, className: cn('mb-1 text-sm font-semibold leading-none', className), ...rest })));
Title$1.displayName = 'Alert.Title';
const Description$1 = React.forwardRef(({ className, ...rest }, ref) => (jsx("p", { ref: ref, className: cn('text-sm leading-relaxed', className), ...rest })));
Description$1.displayName = 'Alert.Description';
const Alert = Object.assign(Root$2, {
    Root: Root$2,
    Title: Title$1,
    Description: Description$1,
});
const AlertTitle = Title$1;
const AlertDescription = Description$1;

const cardStyles = cva([
    'relative flex items-start gap-3 overflow-hidden rounded-lg border border-border bg-surface p-4 text-surface-foreground',
    'before:absolute before:inset-y-0 before:left-0 before:w-1',
], {
    variants: {
        tone: {
            info: 'before:bg-info',
            warning: 'before:bg-warning',
            critical: 'before:bg-destructive',
            success: 'before:bg-success',
        },
    },
    defaultVariants: { tone: 'info' },
});
const iconTile = cva('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', {
    variants: {
        tone: {
            info: 'bg-info-subtle text-info-foreground',
            warning: 'bg-warning-subtle text-warning-foreground',
            critical: 'bg-destructive-subtle text-destructive-foreground',
            success: 'bg-success-subtle text-success-foreground',
        },
    },
    defaultVariants: { tone: 'info' },
});
const AlertCard = React.forwardRef(({ className, tone, icon, title, description, timestamp, action, onDismiss, ...rest }, ref) => (jsxs("div", { ref: ref, className: cn(cardStyles({ tone }), className), role: "status", ...rest, children: [jsx("div", { className: iconTile({ tone }), children: jsx(Icon$1, { icon: icon, size: 14 }) }), jsxs("div", { className: "min-w-0 flex-1", children: [jsxs("div", { className: "flex items-center gap-3", children: [jsx("div", { className: "truncate text-sm font-semibold", children: title }), timestamp && jsx("div", { className: "ml-auto text-xs font-mono text-muted-foreground shrink-0", children: timestamp })] }), description && jsx("div", { className: "mt-1 text-sm text-muted-foreground", children: description }), action && jsx("div", { className: "mt-2 flex gap-2", children: action })] }), onDismiss && (jsx("button", { type: "button", "aria-label": "Dismiss", onClick: onDismiss, className: "text-muted-foreground hover:text-foreground", children: jsx(Icon$1, { icon: X, size: 14 }) }))] })));
AlertCard.displayName = 'AlertCard';

const ErrorState = ({ title = 'Something went wrong', message, action, className, compact, }) => (jsx("div", { role: "alert", className: cn('animate-fade-in-up rounded-xl border border-destructive/30 bg-destructive/5', compact ? 'p-4' : 'p-5', className), children: jsxs("div", { className: "flex items-start gap-3", children: [jsx(WarningCircle, { className: "mt-0.5 h-5 w-5 flex-shrink-0 text-destructive" }), jsxs("div", { className: "min-w-0 flex-1", children: [jsx("h3", { className: "text-sm font-semibold text-destructive", children: title }), message && jsx("p", { className: "mt-1 text-sm text-destructive/90", children: message }), action && jsx("div", { className: "mt-3", children: action })] })] }) }));

const Provider = RadixToast.Provider;
const toastStyles = cva([
    'group pointer-events-auto relative flex w-full items-center justify-between gap-3 overflow-hidden rounded-lg border p-4 pr-8 shadow-md',
    'data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom-full data-[state=open]:duration-slow data-[state=open]:ease-out',
    'data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:transition-transform',
    'data-[state=closed]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=closed]:duration-fast data-[state=closed]:ease-in',
], {
    variants: {
        tone: {
            neutral: 'bg-surface border-border text-surface-foreground',
            success: 'bg-success-subtle border-success-border text-success-foreground',
            warning: 'bg-warning-subtle border-warning-border text-warning-foreground',
            destructive: 'bg-destructive-subtle border-destructive-border text-destructive-foreground',
            info: 'bg-info-subtle border-info-border text-info-foreground',
        },
    },
    defaultVariants: { tone: 'neutral' },
});
const Root$1 = React.forwardRef(({ className, tone, ...rest }, ref) => (jsx(RadixToast.Root, { ref: ref, className: cn(toastStyles({ tone }), className), ...rest })));
Root$1.displayName = 'Toast.Root';
const Title = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixToast.Title, { ref: ref, className: cn('text-sm font-semibold', className), ...rest })));
Title.displayName = 'Toast.Title';
const Description = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixToast.Description, { ref: ref, className: cn('text-sm opacity-90', className), ...rest })));
Description.displayName = 'Toast.Description';
const Action = RadixToast.Action;
const Close = React.forwardRef(({ className, ...rest }, ref) => (jsx(RadixToast.Close, { ref: ref, "aria-label": "Close", className: cn('absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring', className), ...rest, children: jsx(Icon$1, { icon: X, size: 14 }) })));
Close.displayName = 'Toast.Close';
const Toast = Object.assign(Root$1, {
    Root: Root$1,
    Provider,
    Title,
    Description,
    Action,
    Close,
});
const ToastProvider = Provider;
const ToastTitle = Title;
const ToastDescription = Description;
const ToastAction = Action;
const ToastClose = Close;
function ToastViewport({ className, ...rest }) {
    return (jsx(RadixToast.Viewport, { className: cn('fixed bottom-0 right-0 z-toast flex max-h-screen w-full flex-col-reverse gap-2 p-4 sm:max-w-sm', className), ...rest }));
}
function Toaster() {
    const { toasts } = useToast();
    return (jsxs(RadixToast.Provider, { children: [toasts.map(({ id, title, description, action, ...props }) => (jsxs(Root$1, { ...props, children: [jsxs("div", { className: "grid gap-1", children: [title && jsx(Title, { children: title }), description && jsx(Description, { children: description })] }), action, jsx(Close, {})] }, id))), jsx(ToastViewport, {})] }));
}

const AIInsightBanner = React.forwardRef(({ className, label, title, description, action, onDismiss, ...rest }, ref) => (jsxs("div", { ref: ref, className: cn('relative rounded-lg border border-accent-border bg-accent-subtle p-card-padding', className), ...rest, children: [jsxs("div", { className: "flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-accent", children: [jsx(Icon$1, { icon: Sparkle, size: 12, weight: "fill" }), " ", label] }), jsx("h3", { className: "mt-2 text-lg font-semibold text-foreground", children: title }), jsx("p", { className: "mt-1.5 text-sm leading-relaxed text-muted-foreground", children: description }), action && jsx("div", { className: "mt-3", children: action }), onDismiss && (jsx("button", { type: "button", "aria-label": "Dismiss", onClick: onDismiss, className: "absolute right-3 top-3 text-muted-foreground transition-colors hover:text-foreground", children: jsx(Icon$1, { icon: X, size: 16 }) }))] })));
AIInsightBanner.displayName = 'AIInsightBanner';

const MAX_WIDTH_CLS = {
    sm: 'max-w-sm', md: 'max-w-md', lg: 'max-w-lg',
    xl: 'max-w-xl', '2xl': 'max-w-2xl', '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl', '5xl': 'max-w-5xl', '6xl': 'max-w-6xl', '7xl': 'max-w-7xl',
    full: 'max-w-full',
};
const PageLayout = React.forwardRef(({ className, maxWidth, isCentered, applyVerticalSpacing = true, ...rest }, ref) => (jsx("div", { ref: ref, className: cn(applyVerticalSpacing && 'space-y-6', maxWidth && MAX_WIDTH_CLS[maxWidth], isCentered && 'mx-auto', className), ...rest })));
PageLayout.displayName = 'PageLayout';

const Breadcrumbs = React.forwardRef(({ items, className, ...rest }, ref) => {
    const labels = (items ?? []).map((b) => String(b.label));
    const key = JSON.stringify(labels);
    const [seen, setSeen] = React.useState(() => ({ key, labels, entering: new Set() }));
    if (seen.key !== key) {
        setSeen({
            key,
            labels,
            entering: new Set(labels.map((label, i) => (seen.labels[i] === label ? -1 : i)).filter((i) => i >= 0)),
        });
    }
    const entering = seen.key === key ? seen.entering : new Set();
    if (!items || items.length === 0)
        return null;
    return (jsx("nav", { ref: ref, "aria-label": "Breadcrumb", className: cn('flex items-center gap-1.5 text-xs text-muted-foreground', className), ...rest, children: items.map((b, i) => {
            const isLast = i === items.length - 1;
            const content = b.label;
            const enters = entering.has(i);
            const crumbKey = enters ? `${i}-${key}` : i;
            const enterClass = enters ? 'animate-fade-in-up' : undefined;
            return (jsxs(React.Fragment, { children: [b.href || b.onClick ? (jsx("a", { href: b.href, onClick: b.onClick, className: cn('cursor-pointer transition-colors hover:text-foreground', isLast && 'pointer-events-none font-medium text-foreground', enterClass), children: content })) : (jsx("span", { className: cn(isLast && 'font-medium text-foreground', enterClass), children: content })), !isLast && jsx(Icon$1, { icon: CaretRight, size: 12 })] }, crumbKey));
        }) }));
});
Breadcrumbs.displayName = 'Breadcrumbs';

const PageHeader = React.forwardRef(({ className, title, subtitle, breadcrumbs, actions, hasBorder = false, ...rest }, ref) => (jsxs("div", { ref: ref, className: cn('flex flex-col gap-4 pb-4 md:flex-row md:items-end md:justify-between', hasBorder && 'border-b border-border', className), ...rest, children: [jsxs("div", { className: "min-w-0", children: [breadcrumbs && breadcrumbs.length > 0 && (jsx(Breadcrumbs, { items: breadcrumbs, className: "mb-1.5" })), jsx("h1", { className: "truncate text-2xl font-bold tracking-tight text-foreground", children: title }), subtitle && jsx("p", { className: "mt-1 text-sm text-muted-foreground", children: subtitle })] }), actions && jsx("div", { className: "flex min-w-0 max-w-full items-center gap-2", children: actions })] })));
PageHeader.displayName = 'PageHeader';

const EntityActions = ({ primary, actions, className }) => {
    const [confirming, setConfirming] = React.useState(null);
    const menuActions = (actions ?? []).filter((a) => !a.hidden);
    const run = (action) => {
        if (action.confirm)
            setConfirming(action);
        else
            action.onClick();
    };
    const showPrimary = primary && !primary.hidden;
    if (!showPrimary && menuActions.length === 0)
        return null;
    return (jsxs("div", { className: cn('flex items-center gap-2', className), children: [showPrimary && (jsx(Button, { onClick: () => run(primary), disabled: primary.disabled, leftIcon: primary.icon, children: primary.label })), menuActions.length > 0 && (jsxs(DropdownMenu, { children: [jsx(DropdownMenuTrigger, { asChild: true, children: jsx(Button, { variant: "outline", "aria-label": "More actions", children: jsx(DotsThree, { className: "h-4 w-4" }) }) }), jsx(DropdownMenuContent, { align: "end", className: "w-48", children: menuActions.map((action) => (jsxs(DropdownMenuItem, { disabled: action.disabled ?? false, onSelect: () => run(action), className: cn(action.destructive && 'text-destructive focus:text-destructive'), children: [action.icon && jsx("span", { className: "mr-2 inline-flex", children: action.icon }), action.label] }, action.id))) })] })), jsx(AlertDialog, { open: !!confirming, onOpenChange: (open) => !open && setConfirming(null), children: jsxs(AlertDialogContent, { children: [jsxs(AlertDialogHeader, { children: [jsx(AlertDialogTitle, { children: confirming?.confirm?.title }), confirming?.confirm?.description && (jsx(AlertDialogDescription, { children: confirming.confirm.description }))] }), jsxs(AlertDialogFooter, { children: [jsx(AlertDialogCancel, { children: "Cancel" }), jsx(AlertDialogAction, { className: cn(confirming?.destructive && 'bg-destructive text-destructive-foreground hover:bg-destructive/90'), onClick: () => {
                                        confirming?.onClick();
                                        setConfirming(null);
                                    }, children: confirming?.confirm?.confirmLabel ?? 'Confirm' })] })] }) })] }));
};

const Root = React.forwardRef(({ className, brand, items, activeId, onNavigate, footer, ...rest }, ref) => (jsxs("aside", { ref: ref, className: cn('flex h-full w-64 shrink-0 flex-col gap-4 border-r border-sidebar-border bg-sidebar p-4 text-sidebar-foreground', className), ...rest, children: [brand && jsx("div", { children: brand }), jsx("nav", { className: "flex flex-col gap-4", children: items.map((group) => (jsxs("div", { children: [jsx("div", { className: "mb-1 px-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-neutral-500", children: group.section }), jsx("ul", { className: "flex flex-col gap-0.5", children: group.entries.map((entry) => {
                            const active = entry.id === activeId;
                            return (jsx("li", { children: jsxs("button", { type: "button", disabled: entry.disabled, onClick: () => onNavigate?.(entry.id, entry), className: cn('relative flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors', active
                                        ? 'bg-sidebar-accent/15 text-white font-medium'
                                        : 'text-neutral-300 hover:bg-white/5 hover:text-white', entry.disabled && 'pointer-events-none opacity-50'), "data-active": active || undefined, children: [active && jsx("span", { "aria-hidden": true, className: "absolute -left-2 top-1.5 bottom-1.5 w-[3px] rounded-full bg-sidebar-primary" }), jsx(Icon$1, { icon: entry.icon, size: 16, weight: "duotone" }), jsx("span", { className: "truncate", children: entry.label }), entry.badge !== undefined && (jsx("span", { className: "ml-auto inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-destructive px-1.5 py-0.5 text-[10px] font-semibold text-white", children: entry.badge }))] }) }, entry.id));
                        }) })] }, group.section))) }), footer && jsx("div", { className: "mt-auto", children: footer })] })));
Root.displayName = 'Sidebar.Root';
const Sidebar = { Root };

function navIcon(icon) {
    if (!React.isValidElement(icon) || typeof icon.type === 'string')
        return icon;
    return React.cloneElement(icon, {
        weight: 'duotone',
    });
}
const RAIL_ICON_HOVER = cn('flex', '[&_path[opacity]]:transition-[fill,opacity] [&_path[opacity]]:duration-200', 'group-hover/rail:[&_path[opacity]]:fill-primary group-hover/rail:[&_path[opacity]]:opacity-60', 'group-focus-visible/rail:[&_path[opacity]]:fill-primary group-focus-visible/rail:[&_path[opacity]]:opacity-60');
function railIcon(icon) {
    return (jsx("span", { "aria-hidden": true, className: RAIL_ICON_HOVER, children: navIcon(icon) }));
}
function panelIcon(icon) {
    if (!React.isValidElement(icon) || typeof icon.type === 'string')
        return icon;
    return React.cloneElement(icon, {
        weight: 'duotone',
        size: 16,
    });
}
const PANEL_CLOSED_GROUPS_KEY = 'synapse-nav-closed-groups';
function readStored(key, fallback) {
    try {
        const raw = window.localStorage.getItem(key);
        return raw === null ? fallback : JSON.parse(raw);
    }
    catch {
        return fallback;
    }
}
function writeStored(key, value) {
    try {
        window.localStorage.setItem(key, JSON.stringify(value));
    }
    catch {
    }
}
function pathMatchesPattern(path, pattern) {
    const p = path.split('?')[0].replace(/\/+$/, '');
    const segs = pattern.replace(/\/+$/, '').split('/');
    const parts = p.split('/');
    for (let i = 0; i < segs.length; i++) {
        const seg = segs[i];
        if (seg === '*')
            return true;
        if (parts[i] === undefined)
            return false;
        if (seg.startsWith(':'))
            continue;
        if (seg !== parts[i])
            return false;
    }
    return parts.length === segs.length;
}
function resolveActiveHref(currentPath, items) {
    const path = currentPath.split('?')[0].replace(/\/+$/, '') || '/';
    const candidates = items.filter((i) => !i.isSeparator && !i.isGroupHeader && !i.isCaption && !i.external);
    const exact = candidates.find((i) => i.href.replace(/\/+$/, '') === path);
    if (exact)
        return exact.href;
    let best = null;
    for (const item of candidates) {
        const href = item.href.replace(/\/+$/, '');
        if (href && path.startsWith(href + '/')) {
            if (!best || href.length > best.href.replace(/\/+$/, '').length)
                best = item;
        }
    }
    let declared = null;
    let declaredSpecificity = -1;
    for (const item of candidates) {
        for (const pattern of item.matchPaths ?? []) {
            if (pathMatchesPattern(path, pattern) && literalSegments(pattern) > declaredSpecificity) {
                declared = item;
                declaredSpecificity = literalSegments(pattern);
            }
        }
    }
    if (best && (!declared || literalSegments(best.href) >= declaredSpecificity))
        return best.href;
    return declared ? declared.href : null;
}
function literalSegments(pattern) {
    return pattern
        .replace(/\/+$/, '')
        .split('/')
        .filter((seg) => seg !== '' && seg !== '*' && !seg.startsWith(':')).length;
}
const EASE_MOVE = 'cubic-bezier(0.32, 0.72, 0, 1)';
const PANEL_WIDTH_TRANSITION = {
    transitionProperty: 'width',
    transitionDuration: '260ms',
    transitionTimingFunction: EASE_MOVE,
};
const FLOATING_PANEL_SHADOW = '12px 0 24px -12px rgba(0, 0, 0, 0.25)';
const PANEL_CONTENT_TRANSITION = {
    transitionProperty: 'opacity, transform',
    transitionDuration: '260ms',
    transitionTimingFunction: EASE_MOVE,
};
const DefaultLink = ({ children, ...rest }) => (jsx("a", { ...rest, children: children }));
function LegacyShell({ title, navigationItems = [], LinkComponent = DefaultLink, headerContent, logo, collapsedLogo, sidebarFooter, currentPath = '', onNavigate, userRole, collapsed: collapsedProp, onToggleCollapsed, children, }) {
    const [openMenu, setOpenMenu] = React.useState(null);
    const [selectedGroupKey, setSelectedGroupKey] = React.useState(null);
    const railHighlight = useSlidingHighlight();
    const panelHighlight = useSlidingHighlight({ selector: 'a, button, summary' });
    const [hoverKey, setHoverKey] = React.useState(null);
    const hoverCloseTimer = React.useRef(undefined);
    const cancelHoverClose = () => clearTimeout(hoverCloseTimer.current);
    const showHoverPanel = (key) => {
        if (docked)
            return;
        cancelHoverClose();
        setHoverKey(key);
    };
    const hideHoverPanelSoon = () => {
        if (docked)
            return;
        cancelHoverClose();
        hoverCloseTimer.current = setTimeout(() => setHoverKey(null), 200);
    };
    React.useEffect(() => () => clearTimeout(hoverCloseTimer.current), []);
    const [closedGroups, setClosedGroups] = React.useState(() => readStored(PANEL_CLOSED_GROUPS_KEY, []));
    const setGroupOpen = React.useCallback((id, open) => {
        setClosedGroups((current) => {
            if (open !== current.includes(id))
                return current;
            const next = open ? current.filter((g) => g !== id) : [...current, id];
            writeStored(PANEL_CLOSED_GROUPS_KEY, next);
            return next;
        });
    }, []);
    const isControlled = collapsedProp !== undefined;
    const [collapsedState, setCollapsedState] = React.useState(false);
    const collapsed = isControlled ? collapsedProp : collapsedState;
    const toggleCollapsed = () => {
        onToggleCollapsed?.();
        if (!isControlled)
            setCollapsedState((c) => !c);
    };
    const docked = !collapsed;
    const panelKey = docked ? selectedGroupKey : hoverKey;
    const toggleDocked = () => {
        if (docked)
            setHoverKey(selectedGroupKey);
        else if (hoverKey)
            setSelectedGroupKey(hoverKey);
        toggleCollapsed();
    };
    const railActiveMarker = useSlidingMarker(railHighlight.containerProps.ref, '[data-active]', [currentPath, collapsed]);
    React.useEffect(() => {
        const match = navigationItems.find((item) => item.subItems && item.subItems.some((s) => currentPath.startsWith(s.href.split('?')[0] ?? '')));
        if (match)
            setOpenMenu(match.href);
    }, [currentPath, navigationItems]);
    const filtered = React.useMemo(() => {
        if (!userRole)
            return navigationItems;
        return navigationItems.filter((item) => !item.roles || item.roles.includes(userRole));
    }, [navigationItems, userRole]);
    const go = (href) => {
        onNavigate?.(href);
        if (!docked)
            setHoverKey(null);
    };
    const railActiveHref = React.useMemo(() => resolveActiveHref(currentPath, filtered), [currentPath, filtered]);
    const toggleGroup = (key) => {
        if (!docked) {
            cancelHoverClose();
            setHoverKey((current) => (current === key ? null : key));
            return;
        }
        setSelectedGroupKey((current) => (current === key ? null : key));
    };
    const sidebarLogo = collapsedLogo ?? logo;
    const searchEntries = React.useMemo(() => {
        const out = [];
        const seen = new Set();
        const add = (entry) => {
            if (!entry.href || seen.has(entry.key))
                return;
            seen.add(entry.key);
            out.push(entry);
        };
        let section = '';
        let caption = '';
        for (const item of filtered) {
            if (item.isGroupHeader) {
                section = item.text;
                caption = '';
            }
            else if (item.isSeparator) {
                section = '';
                caption = '';
            }
            else if (item.isCaption) {
                caption = item.text;
            }
            else {
                if (item.startsBlock)
                    caption = '';
                const context = [section, caption].filter(Boolean).join(' · ');
                if (item.subItems?.length) {
                    for (const sub of item.subItems) {
                        add({
                            key: `${sub.href}|${sub.text}`,
                            href: sub.href,
                            text: sub.text,
                            context: [context, item.text].filter(Boolean).join(' · '),
                        });
                    }
                }
                else {
                    add({ key: `${item.href}|${item.text}`, href: item.href, text: item.text, context, external: item.external });
                }
            }
        }
        return out;
    }, [filtered]);
    const blocks = React.useMemo(() => {
        const out = [];
        let current = null;
        filtered.forEach((item, index) => {
            if (item.isGroupHeader) {
                current = { kind: 'section', header: item, index, items: [] };
                out.push(current);
            }
            else if (item.isSeparator) {
                current = null;
            }
            else if (item.isCaption && !current) ;
            else if (current) {
                current.items.push({ item, index });
            }
            else {
                out.push({ kind: 'loose', item, index });
            }
        });
        return out;
    }, [filtered]);
    const activeSectionKey = React.useMemo(() => {
        for (const block of blocks) {
            if (block.kind !== 'section')
                continue;
            const childHref = resolveActiveHref(currentPath, block.items.map(({ item }) => item));
            const match = block.items.some(({ item }) => {
                if (childHref === item.href)
                    return true;
                return item.subItems?.some((s) => currentPath.startsWith(s.href.split('?')[0] ?? ''));
            });
            if (match)
                return block.header.id ?? block.header.text;
        }
        return null;
    }, [blocks, currentPath]);
    React.useEffect(() => {
        if (activeSectionKey)
            setSelectedGroupKey(activeSectionKey);
    }, [activeSectionKey]);
    const renderItem = (item, i, indented = false) => {
        if (item.isSeparator) {
            return jsx("div", { role: "separator", className: "my-1 mx-2 border-t border-white/10" }, item.id ?? `sep-${i}`);
        }
        const parentActive = panelActiveHref === item.href ||
            (item.subItems ? item.subItems.some((s) => currentPath.startsWith(s.href.split('?')[0] ?? '')) : false);
        const menuOpen = openMenu === item.href;
        if (item.subItems && item.subItems.length > 0) {
            return (jsxs("div", { className: "relative", children: [jsxs("button", { type: "button", "data-active": parentActive || undefined, onClick: () => setOpenMenu(menuOpen ? null : item.href), className: cn('flex w-full items-center gap-2 rounded-md py-1.5 pr-2 text-left text-sm transition-colors', indented ? 'pl-8' : 'pl-2', parentActive
                            ? 'font-medium text-white'
                            : 'text-neutral-300 hover:text-white'), children: [jsx("span", { className: "truncate", children: item.text }), jsx(CaretDown, { className: cn('ml-auto h-4 w-4 transition-transform', menuOpen && 'rotate-180') })] }), jsx("div", { className: cn('grid overflow-hidden transition-[grid-template-rows] duration-200 ease-out', menuOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'), children: jsx("div", { className: "min-h-0 overflow-hidden", children: jsx("ul", { className: "space-y-0.5", children: item.subItems.map((sub) => {
                                    const subActive = currentPath === sub.href;
                                    return (jsx("li", { children: jsx(LinkComponent, { href: sub.href, onClick: () => go(sub.href), className: cn('block rounded-md py-1.5 pr-2 text-sm transition-colors', indented ? 'pl-8' : 'pl-2', subActive
                                                ? 'text-white font-medium'
                                                : 'text-neutral-400 hover:text-white'), children: sub.text }) }, sub.href + sub.text));
                                }) }) }) })] }, item.id ?? item.href));
        }
        const active = panelActiveHref === item.href;
        const className = cn('relative flex items-center gap-2 rounded-md py-1.5 pr-2 text-sm transition-colors', indented ? 'pl-8' : 'pl-2', active
            ? 'font-medium text-white'
            : 'text-neutral-300 hover:text-white');
        const activeAttrs = { 'data-active': active || undefined };
        const content = (jsxs(Fragment, { children: [jsx("span", { className: "truncate", children: item.text }), item.badge && jsx("span", { className: "ml-auto flex-shrink-0", children: item.badge })] }));
        if (item.external) {
            return (jsx("a", { href: item.href, target: "_blank", rel: "noopener noreferrer", className: className, ...activeAttrs, children: content }, item.id ?? item.href));
        }
        return (jsx(LinkComponent, { href: item.href, onClick: () => go(item.href), className: className, ...activeAttrs, children: content }, item.id ?? item.href));
    };
    const railButtonClass = (active, selected = false) => cn('group/rail relative flex w-full flex-col items-center gap-1 rounded-md px-1 py-2 text-center text-sm transition-colors', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-primary', active
        ? 'font-medium text-white'
        : selected
            ? 'bg-sidebar-hover text-white'
            : 'text-neutral-300 hover:text-white');
    const renderRailItem = (item, i) => {
        if (item.isSeparator) {
            return jsx("div", { role: "separator", className: "my-1 mx-2 border-t border-white/10" }, item.id ?? `sep-${i}`);
        }
        const key = item.id ?? item.href;
        const hasChildren = !!item.subItems?.length;
        const active = railActiveHref === item.href ||
            (hasChildren && item.subItems.some((s) => currentPath.startsWith(s.href.split('?')[0] ?? '')));
        const selected = hasChildren && panelKey === key;
        if (hasChildren) {
            return (jsxs("button", { type: "button", onClick: () => toggleGroup(key), onMouseEnter: () => showHoverPanel(key), "aria-expanded": selected, className: railButtonClass(active, selected), "data-active": active || undefined, "data-selected": (!active && selected) || undefined, children: [railIcon(item.icon), jsx("span", { className: "break-words text-[10px] leading-tight", children: item.text }), item.badge && jsx("span", { className: "absolute right-1 top-1", children: item.badge })] }, key));
        }
        const content = (jsxs(Fragment, { children: [railIcon(item.icon), jsx("span", { className: "break-words text-[10px] leading-tight", children: item.text }), item.badge && jsx("span", { className: "absolute right-1 top-1", children: item.badge })] }));
        if (item.external) {
            return (jsx("a", { href: item.href, target: "_blank", rel: "noopener noreferrer", className: railButtonClass(active), "data-active": active || undefined, children: content }, key));
        }
        return (jsx(LinkComponent, { href: item.href, onClick: () => {
                go(item.href);
                setSelectedGroupKey(null);
            }, onMouseEnter: () => showHoverPanel(null), className: railButtonClass(active), "data-active": active || undefined, children: content }, key));
    };
    const renderRailGroup = (header, items, sectionKey) => {
        const active = activeSectionKey === sectionKey;
        const selected = panelKey === sectionKey;
        return (jsxs("button", { type: "button", onClick: () => toggleGroup(sectionKey), onMouseEnter: () => showHoverPanel(sectionKey), "aria-expanded": selected, className: railButtonClass(active, selected), "data-active": active || undefined, "data-selected": (!active && selected) || undefined, children: [railIcon(header.icon), jsx("span", { className: "break-words text-[10px] leading-tight", children: header.text })] }, `rail-${sectionKey}`));
    };
    const panelBlock = panelKey
        ? blocks.find((b) => b.kind === 'section' && (b.header.id ?? b.header.text) === panelKey)
        : undefined;
    const panelActiveHref = React.useMemo(() => (panelBlock ? resolveActiveHref(currentPath, panelBlock.items.map(({ item }) => item)) : null), [currentPath, panelBlock]);
    const panelOpen = !!panelBlock;
    const [panelContentMounted, setPanelContentMounted] = React.useState(panelOpen);
    const [displayedKey, setDisplayedKey] = React.useState(panelKey);
    React.useEffect(() => {
        if (panelOpen)
            setDisplayedKey(panelKey);
    }, [panelOpen, panelKey]);
    React.useEffect(() => {
        if (panelOpen) {
            setPanelContentMounted(true);
            return;
        }
        const unmount = setTimeout(() => setPanelContentMounted(false), 300);
        return () => clearTimeout(unmount);
    }, [panelOpen]);
    const displayedPanel = React.useMemo(() => blocks.find((b) => b.kind === 'section' && (b.header.id ?? b.header.text) === displayedKey), [blocks, displayedKey]);
    const panelRows = React.useMemo(() => {
        const rows = [];
        let group = null;
        for (const { item, index } of displayedPanel?.items ?? []) {
            if (item.isCaption) {
                group = { kind: 'group', caption: item, items: [] };
                rows.push(group);
            }
            else if (item.startsBlock) {
                group = null;
                rows.push({ kind: 'item', item, index, standalone: true });
            }
            else if (group) {
                group.items.push({ item, index });
            }
            else {
                rows.push({ kind: 'item', item, index, standalone: false });
            }
        }
        return rows;
    }, [displayedPanel]);
    const groupId = (caption) => caption.id ?? caption.text;
    React.useEffect(() => {
        if (!panelActiveHref)
            return;
        for (const row of panelRows) {
            if (row.kind === 'group' && row.items.some(({ item }) => item.href === panelActiveHref)) {
                setGroupOpen(groupId(row.caption), true);
            }
        }
    }, [panelActiveHref, panelRows, setGroupOpen]);
    const panelActiveMarker = useSlidingMarker(panelHighlight.containerProps.ref, '[data-active]', [currentPath, panelKey, displayedKey, panelContentMounted, panelOpen, closedGroups]);
    const [mobile, setMobile] = React.useState(false);
    const [mobileNavigationOpen, setMobileNavigationOpen] = React.useState(false);
    React.useEffect(() => {
        if (typeof window.matchMedia !== 'function')
            return;
        const media = window.matchMedia('(max-width: 767px)');
        const update = () => setMobile(media.matches);
        update();
        media.addEventListener('change', update);
        return () => media.removeEventListener('change', update);
    }, []);
    React.useEffect(() => setMobileNavigationOpen(false), [currentPath]);
    const navigation = (jsxs("aside", { className: cn('relative z-30 flex h-full w-auto shrink-0 border-r border-sidebar-border bg-sidebar text-sidebar-foreground', docked && 'overflow-hidden'), "data-panel-floating": !docked || undefined, onMouseEnter: cancelHoverClose, onMouseLeave: hideHoverPanelSoon, children: [jsxs("div", { className: "flex h-full w-20 shrink-0 flex-col gap-1 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden", children: [sidebarLogo && (jsx("div", { className: "flex h-14 shrink-0 items-center justify-center px-2", children: sidebarLogo })), jsxs("nav", { className: "relative flex flex-col gap-0.5 px-1 pb-3", ...railHighlight.containerProps, children: [jsx("span", { "aria-hidden": true, className: "pointer-events-none absolute inset-x-1 top-0 rounded-md bg-primary-subtle duration-slow ease-move", style: railActiveMarker.style }), jsx("span", { "aria-hidden": true, className: "pointer-events-none absolute inset-x-1 top-0 rounded-md bg-sidebar-hover duration-slow ease-move", style: railHighlight.style }), blocks.map((block) => block.kind === 'loose'
                                ? renderRailItem(block.item, block.index)
                                : renderRailGroup(block.header, block.items.map(({ item }) => item), block.header.id ?? block.header.text))] }), sidebarFooter && (jsx("div", { className: "mt-auto shrink-0 px-3 pb-3", children: sidebarFooter }))] }), jsx("div", { className: cn('h-full shrink-0 overflow-hidden', panelOpen ? 'w-56' : 'w-0', !docked && 'absolute inset-y-0 left-full'), style: docked
                    ? PANEL_WIDTH_TRANSITION
                    : {
                        ...PANEL_WIDTH_TRANSITION,
                        backgroundColor: 'inherit',
                        boxShadow: panelOpen ? FLOATING_PANEL_SHADOW : undefined,
                    }, "aria-hidden": !panelOpen, children: panelContentMounted && displayedPanel && (jsxs("div", { className: cn('flex h-full w-56 flex-col border-l border-sidebar-border/60 px-2 pb-3 pt-3', panelOpen ? 'translate-x-0 opacity-100' : '-translate-x-2 opacity-0'), style: PANEL_CONTENT_TRANSITION, children: [jsxs("div", { className: "flex shrink-0 items-start gap-1 pb-3 pl-2", children: [jsxs("div", { className: "min-w-0 flex-1", children: [jsx("div", { className: "truncate text-base font-semibold leading-6 text-neutral-50", children: displayedPanel.header.text }), displayedPanel.header.description && (jsx("div", { className: "truncate text-xs leading-4 text-neutral-400", children: displayedPanel.header.description }))] }), jsx(IconButton, { icon: docked ? SidebarSimple : SidebarSimple, size: "md", "aria-label": docked ? 'Show panel on hover' : 'Keep panel open', "aria-pressed": docked, onClick: toggleDocked, className: cn('shrink-0 hover:bg-sidebar-hover focus-visible:ring-offset-0', docked ? 'text-primary hover:text-primary' : 'text-neutral-400 hover:text-white') })] }), jsx(PanelSearch, { entries: searchEntries, LinkComponent: LinkComponent, go: go }), jsxs("div", { className: "relative -mx-2 flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-2", ...panelHighlight.containerProps, children: [jsx("span", { "aria-hidden": true, className: "pointer-events-none absolute inset-x-2 top-0 rounded-md bg-primary-subtle duration-slow ease-move", style: panelActiveMarker.style }), jsx("span", { "aria-hidden": true, className: "pointer-events-none absolute inset-x-2 top-0 rounded-md bg-sidebar-hover duration-slow ease-move", style: panelHighlight.style }), panelRows.map((row, pos) => {
                                    const divider = pos > 0 && (row.kind === 'group' || row.standalone) ? (jsx(Separator$4, { className: "my-2 bg-sidebar-border" })) : null;
                                    if (row.kind === 'item') {
                                        if (!row.standalone)
                                            return renderItem(row.item, row.index);
                                        const active = panelActiveHref === row.item.href;
                                        const className = cn('relative flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-semibold transition-colors', active ? 'text-white' : 'text-neutral-50');
                                        const content = (jsxs(Fragment, { children: [jsx("span", { className: "flex h-4 w-4 shrink-0 items-center justify-center", children: panelIcon(row.item.icon) }), jsx("span", { className: "truncate", children: row.item.text })] }));
                                        return (jsxs(React.Fragment, { children: [divider, row.item.external ? (jsx("a", { href: row.item.href, target: "_blank", rel: "noopener noreferrer", className: className, "data-active": active || undefined, children: content })) : (jsx(LinkComponent, { href: row.item.href, onClick: () => go(row.item.href), className: className, "data-active": active || undefined, children: content }))] }, row.item.id ?? row.item.href));
                                    }
                                    const id = groupId(row.caption);
                                    const open = !closedGroups.includes(id);
                                    const holdsActive = !open &&
                                        row.items.some(({ item }) => item.href === panelActiveHref || (item.subItems?.some((sub) => sub.href === currentPath) ?? false));
                                    return (jsxs(React.Fragment, { children: [divider, jsxs(Collapsible, { open: open, onOpenChange: (next) => setGroupOpen(id, next), children: [jsxs(Collapsible.Trigger, { "data-active": holdsActive || undefined, className: cn('relative flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-primary', holdsActive ? 'text-white' : 'text-neutral-50'), children: [jsx("span", { className: "flex h-4 w-4 shrink-0 items-center justify-center", children: panelIcon(row.caption.icon) }), jsx("span", { className: "min-w-0 flex-1 truncate", title: row.caption.text, children: row.caption.text }), jsx(CaretDown, { className: cn('h-4 w-4 shrink-0 text-neutral-400 transition-transform', !open && '-rotate-90') })] }), jsx(Collapsible.Content, { className: "mt-0.5 flex flex-col gap-0.5", children: row.items.map(({ item, index }) => renderItem(item, index, true)) })] })] }, id));
                                })] })] })) })] }));
    return (jsx(Sheet, { open: mobile && mobileNavigationOpen, onOpenChange: setMobileNavigationOpen, children: jsxs("div", { className: "flex h-screen overflow-hidden bg-background text-foreground", children: [mobile ? jsxs(Sheet.Content, { side: "left", className: "w-[min(90vw,340px)] gap-0 overflow-hidden p-0", children: [jsx(Sheet.Title, { className: "sr-only", children: "Application navigation" }), jsx(Sheet.Description, { className: "sr-only", children: "Choose a section or page." }), navigation] }) : navigation, jsxs("div", { className: "flex min-w-0 flex-1 flex-col", children: [jsxs("header", { className: "relative z-10 flex h-14 shrink-0 items-center border-b border-border bg-surface/90 px-3 md:px-6 backdrop-blur", children: [mobile && jsx(IconButton, { className: "mr-2 shrink-0", icon: SidebarSimple, "aria-label": "Open navigation", variant: "ghost", onClick: () => setMobileNavigationOpen(true) }), title && jsx("h1", { className: "mr-4 text-lg font-semibold", children: title }), headerContent && jsx("div", { className: "flex min-w-0 flex-1 items-center gap-2 md:gap-4", children: headerContent })] }), jsx("main", { className: "relative min-w-0 flex-1 overflow-auto bg-gradient-page bg-background", children: children })] })] }) }));
}
function PanelSearch({ entries, LinkComponent, go }) {
    const [query, setQuery] = React.useState('');
    const [focused, setFocused] = React.useState(false);
    const inputRef = React.useRef(null);
    const listRef = React.useRef(null);
    const showResults = focused && query.trim() !== '';
    const reset = () => {
        setQuery('');
        inputRef.current?.blur();
    };
    const follow = (key) => {
        const link = Array.from(listRef.current?.querySelectorAll('a[data-search-key]') ?? []).find((a) => a.dataset.searchKey === key);
        link?.click();
        reset();
    };
    return (jsxs(Command, { loop: true, label: "Jump to a page", className: "relative z-10 mb-3 h-auto shrink-0 overflow-visible rounded-none bg-transparent text-inherit", onKeyDown: (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                reset();
            }
        }, children: [jsx(Command.Input, { ref: inputRef, value: query, onValueChange: setQuery, onFocus: () => setFocused(true), onBlur: () => setFocused(false), placeholder: "Jump to a page", wrapperClassName: "h-8 rounded-md border border-sidebar-border px-2 text-neutral-400 transition-colors focus-within:border-sidebar-primary", className: "h-full text-sm text-neutral-50 placeholder:text-neutral-400" }), showResults && (jsx("div", { onMouseDown: (e) => e.preventDefault(), className: "absolute inset-x-0 top-full mt-1 overflow-hidden rounded-lg border border-border bg-surface text-surface-foreground shadow-lg", children: jsxs(Command.List, { ref: listRef, className: "max-h-80", children: [jsx(Command.Empty, { className: "py-4", children: "No matching pages" }), entries.map((entry) => (jsx(Command.Item, { value: entry.key, keywords: [entry.text, entry.context], onSelect: () => follow(entry.key), children: entry.external ? (jsx("a", { href: entry.href, target: "_blank", rel: "noopener noreferrer", "data-search-key": entry.key, tabIndex: -1, className: "pointer-events-none min-w-0 flex-1", children: jsx(SearchResultLabel, { entry: entry }) })) : (jsx(LinkComponent, { href: entry.href, onClick: () => go(entry.href), "data-search-key": entry.key, tabIndex: -1, className: "pointer-events-none min-w-0 flex-1", children: jsx(SearchResultLabel, { entry: entry }) })) }, entry.key)))] }) }))] }));
}
function SearchResultLabel({ entry }) {
    return (jsxs(Fragment, { children: [jsx("span", { className: "block truncate", children: entry.text }), entry.context && (jsx("span", { className: "block truncate text-xs text-muted-foreground", children: entry.context }))] }));
}
const AppShell = React.forwardRef((props, ref) => {
    if ('title' in props && props.title !== undefined) {
        return jsx(LegacyShell, { ...props });
    }
    const { className, sidebar, header, children, ...rest } = props;
    return (jsxs("div", { ref: ref, className: cn('flex h-screen overflow-hidden bg-background text-foreground', className), ...rest, children: [sidebar, jsxs("div", { className: "flex min-w-0 flex-1 flex-col", children: [header && (jsx("header", { className: "sticky top-0 z-sticky flex h-14 shrink-0 items-center justify-between border-b border-border bg-surface/90 px-6 backdrop-blur", children: header })), jsx("main", { className: "relative min-w-0 flex-1 overflow-auto bg-gradient-page bg-background", children: children })] })] }));
});
AppShell.displayName = 'AppShell';

const ResourceHero = React.forwardRef(({ leading, icon: Icon, iconClassName, title, subtitle, badges, metric, actions, footer, className }, ref) => (jsxs("div", { ref: ref, className: cn('rounded-lg border border-border bg-surface', className), children: [jsxs("div", { className: "flex flex-col gap-6 p-6 md:flex-row md:items-center md:justify-between", children: [jsxs("div", { className: "flex min-w-0 flex-1 items-start gap-4", children: [leading, Icon && (jsx("div", { className: cn('flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground', iconClassName), children: jsx(Icon, { className: "h-6 w-6" }) })), jsxs("div", { className: "min-w-0 flex-1", children: [jsxs("div", { className: "flex flex-wrap items-center gap-3", children: [jsx("h1", { className: "truncate text-2xl font-bold tracking-tight text-foreground", children: title }), badges] }), subtitle && (jsx("div", { className: "mt-1 text-sm text-muted-foreground", children: subtitle }))] })] }), (metric || actions) && (jsxs("div", { className: "flex shrink-0 items-center gap-6", children: [metric && (jsxs("div", { className: "text-right", children: [jsx("div", { className: "text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground", children: metric.label }), jsx("div", { className: "mt-0.5 text-2xl font-bold tracking-tight text-foreground", children: metric.value }), metric.caption && (jsx("div", { className: "mt-0.5 text-xs font-medium", children: metric.caption }))] })), actions && jsx("div", { className: "flex items-center gap-2", children: actions })] }))] }), footer && jsx("div", { className: "px-6 pb-6", children: footer })] })));
ResourceHero.displayName = 'ResourceHero';

function FormFooter({ children, className, position = 'fixed', reserveSpace = true, style, steps, activeStepId, detail, status, ...props }) {
    const anchor = React.useRef(null);
    const footer = React.useRef(null);
    const [bounds, setBounds] = React.useState();
    const [height, setHeight] = React.useState(88);
    React.useLayoutEffect(() => {
        if (position !== 'fixed' || !anchor.current || !footer.current)
            return;
        const measure = () => {
            if (!anchor.current || !footer.current)
                return;
            const rect = anchor.current.getBoundingClientRect();
            setBounds({ left: rect.left, width: rect.width });
            setHeight(footer.current.getBoundingClientRect().height);
        };
        measure();
        const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(measure);
        observer?.observe(anchor.current);
        observer?.observe(footer.current);
        window.addEventListener('resize', measure);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [position]);
    const currentIndex = steps?.findIndex(step => activeStepId ? step.id === activeStepId : step.state === 'current') ?? -1;
    const current = currentIndex >= 0 ? steps?.[currentIndex] : undefined;
    const actions = (jsx("footer", { ref: footer, "aria-label": "Form actions", ...props, className: cn('z-sticky shrink-0 flex flex-wrap items-center justify-between gap-3 border-t border-border bg-background p-4', position === 'fixed' && 'fixed bottom-0', className), style: {
            ...(position === 'fixed' ? { left: bounds?.left, width: bounds?.width ?? '100%' } : {}),
            paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
            ...style,
        }, children: current ? jsxs(Fragment, { children: [jsxs("div", { className: "min-w-0 flex-1 basis-40", "aria-live": "polite", children: [jsxs("p", { className: "text-xs font-medium", children: ["Step ", currentIndex + 1, " of ", steps.length, " \u00B7 ", current.label] }), (detail || current.blocker || current.detail) && jsx("p", { className: "mt-1 text-xs text-muted-foreground", children: detail ?? current.blocker ?? current.detail })] }), status && jsx("div", { className: "text-xs text-muted-foreground", children: status }), jsx("div", { className: "flex flex-wrap items-center justify-end gap-2 max-sm:basis-full", children: children })] }) : children }));
    return position === 'fixed' ? (jsx("div", { ref: anchor, className: cn("col-span-full min-w-0", reserveSpace && "mt-6"), style: { height: reserveSpace ? height + 16 : 0 }, children: actions })) : (actions);
}

const CHART_COLORS = [
    'var(--primary)',
    'var(--success-default)',
    'var(--warning-default)',
    'var(--destructive-default)',
    'var(--accent)',
];
function ThemedTooltipContent({ active, payload, label }) {
    if (!active || !payload || payload.length === 0)
        return null;
    return (jsxs("div", { className: "rounded-md border border-border bg-surface p-2 text-xs text-surface-foreground shadow-md", children: [label != null && jsx("div", { className: "mb-1 font-semibold", children: label }), jsx("div", { className: "flex flex-col gap-0.5", children: payload.map((p, i) => (jsxs("div", { className: "flex items-center gap-2", children: [jsx("span", { className: "inline-block h-2 w-2 rounded-full", style: { background: String(p.color) } }), jsx("span", { className: "text-muted-foreground", children: p.name }), jsx("span", { className: "ml-auto font-mono", children: p.value })] }, i))) })] }));
}
const ThemedTooltip = (props) => (jsx(Tooltip$1, { content: jsx(ThemedTooltipContent, {}), cursor: { stroke: 'var(--border-strong)', strokeDasharray: '3 3' }, ...props }));
const ThemedGrid = (props) => (jsx(CartesianGrid, { stroke: "var(--border)", strokeDasharray: "3 3", vertical: false, ...props }));
const ThemedXAxis = (props) => (jsx(XAxis, { stroke: "var(--muted-foreground)", tick: { fill: 'var(--muted-foreground)', fontSize: 11 }, tickLine: false, axisLine: false, ...props }));
const ThemedYAxis = (props) => (jsx(YAxis, { stroke: "var(--muted-foreground)", tick: { fill: 'var(--muted-foreground)', fontSize: 11 }, tickLine: false, axisLine: false, ...props }));
const Chart = {
    Tooltip: ThemedTooltip,
    Grid: ThemedGrid,
    XAxis: ThemedXAxis,
    YAxis: ThemedYAxis,
};

const ThemeContext = React.createContext(null);
const STORAGE_KEY = 'synapse-theme';
const VALID_THEMES = ['light', 'dark'];
function ThemeProvider({ defaultTheme = 'light', storageKey = STORAGE_KEY, children, }) {
    const [theme, setThemeState] = React.useState(() => {
        const stored = typeof window === 'undefined'
            ? null
            : window.localStorage.getItem(storageKey);
        return (stored && VALID_THEMES.includes(stored)) ? stored : defaultTheme;
    });
    React.useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
        window.localStorage.setItem(storageKey, theme);
    }, [theme, storageKey]);
    const setTheme = React.useCallback((next) => {
        setThemeState(next);
    }, []);
    const value = React.useMemo(() => ({ theme, setTheme }), [theme, setTheme]);
    return jsx(ThemeContext.Provider, { value: value, children: children });
}
function useTheme() {
    const ctx = React.useContext(ThemeContext);
    if (!ctx)
        throw new Error('useTheme must be used within a <ThemeProvider>.');
    return ctx;
}
const themeInitScript = `(function(){try{var t=localStorage.getItem('${STORAGE_KEY}');if(t==='dark'||t==='light'){document.documentElement.setAttribute('data-theme',t);}else{document.documentElement.setAttribute('data-theme','light');}}catch(e){}})();`;

const THEMES = [
    { name: 'light', label: 'Light', icon: Sun },
    { name: 'dark', label: 'Dark', icon: Moon },
];
const ThemeSwitcher = React.forwardRef(({ className, ...rest }, ref) => {
    const { theme, setTheme } = useTheme();
    return (jsx("div", { ref: ref, role: "group", "aria-label": "Theme", className: cn('inline-flex items-center gap-1 rounded-lg border border-border bg-surface p-1', className), ...rest, children: THEMES.map(({ name, label, icon }) => {
            const active = name === theme;
            return (jsxs("button", { type: "button", "aria-label": label, "aria-pressed": active, onClick: () => setTheme(name), className: cn('inline-flex flex-1 items-center justify-center gap-2 rounded-md px-2.5 py-1 text-sm transition-colors', active
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted'), children: [jsx(Icon$1, { icon: icon, size: 14 }), jsx("span", { children: label })] }, name));
        }) }));
});
ThemeSwitcher.displayName = 'ThemeSwitcher';

const Active = ({ className, size = 20, ...rest }) => (jsxs("svg", { width: size, height: size, viewBox: "0 0 24 24", fill: "none", className: cn('text-primary', className), ...rest, children: [jsx("circle", { cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "2" }), jsx("circle", { cx: "12", cy: "12", r: "4", fill: "currentColor" })] }));
const Complete = ({ className, size = 20, ...rest }) => (jsxs("svg", { width: size, height: size, viewBox: "0 0 24 24", fill: "none", className: cn('text-success', className), ...rest, children: [jsx("circle", { cx: "12", cy: "12", r: "10", fill: "currentColor" }), jsx("path", { d: "M8 12l3 3 5-6", stroke: "white", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" })] }));
const Pending = ({ className, size = 20, ...rest }) => (jsx("svg", { width: size, height: size, viewBox: "0 0 24 24", fill: "none", className: cn('text-muted-foreground', className), ...rest, children: jsx("circle", { cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "2", strokeDasharray: "3 3" }) }));
const Skipped = ({ className, size = 20, ...rest }) => (jsxs("svg", { width: size, height: size, viewBox: "0 0 24 24", fill: "none", className: cn('text-muted-foreground', className), ...rest, children: [jsx("circle", { cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "2" }), jsx("path", { d: "M8 8l8 8M16 8l-8 8", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round" })] }));
const WorkflowIcons = { Active, Complete, Pending, Skipped };

const BLUE = '#4C64D9';
const PURPLE = '#7E47CC';
const BLUE_PATHS = [
    'M1.15063 36.6435C0.805439 36.6435 0.529289 36.5306 0.299163 36.3274C0.115063 36.1242 0 35.8532 0 35.5146V1.12888C0 0.790216 0.115063 0.519285 0.322176 0.293509C0.529289 0.112888 0.805439 0 1.15063 0H2.04812C2.39331 0 2.66946 0.112888 2.89958 0.316087C3.10669 0.519285 3.22176 0.790216 3.22176 1.15146V35.5372C3.22176 35.8758 3.10669 36.1468 2.89958 36.3725C2.69247 36.5757 2.41632 36.6886 2.04812 36.6886H1.15063V36.6435Z',
    'M18.2489 37.1627C15.7176 37.1627 13.6004 36.6435 11.9205 35.6049C10.2176 34.5663 8.92886 33.1665 8.03137 31.3829C7.13388 29.5992 6.65062 27.5898 6.58158 25.3998C6.53555 24.8805 6.53555 24.158 6.53555 23.2324C6.53555 22.3067 6.55857 21.5842 6.58158 21.0649C6.65062 18.8297 7.13388 16.8203 8.03137 15.0593C8.92886 13.2982 10.2176 11.8758 11.9205 10.8598C13.6234 9.82126 15.7406 9.30197 18.2489 9.30197C20.7573 9.30197 22.8975 9.82126 24.5774 10.8598C26.2803 11.8984 27.569 13.2982 28.4665 15.0593C29.364 16.8429 29.8473 18.8297 29.9163 21.0649C29.9623 21.5842 29.9623 22.3067 29.9623 23.2324C29.9623 24.158 29.9393 24.8805 29.9163 25.3998C29.8473 27.6124 29.364 29.5992 28.4665 31.3829C27.569 33.1665 26.2803 34.5889 24.5774 35.6049C22.8975 36.6435 20.7803 37.1627 18.2489 37.1627ZM18.2489 34.1599C20.7113 34.1599 22.7134 33.3923 24.2552 31.8344C25.7971 30.2766 26.6255 28.0414 26.7176 25.1289C26.7406 24.6096 26.7636 23.9774 26.7636 23.2098C26.7636 22.4421 26.7406 21.81 26.7176 21.2907C26.6025 18.333 25.7971 16.0753 24.2552 14.5626C22.7134 13.0499 20.7113 12.2822 18.2489 12.2822C15.7866 12.2822 13.7845 13.0499 12.2427 14.5851C10.7238 16.1204 9.89539 18.3556 9.78032 21.3133C9.7343 21.8325 9.7343 22.4647 9.7343 23.2324C9.7343 24 9.75731 24.6322 9.78032 25.1515C9.89539 28.0865 10.7008 30.3217 12.2427 31.857C13.8075 33.3923 15.8096 34.1599 18.2489 34.1599Z',
    'M43.4477 48C41.1925 48 39.3745 47.7065 37.9477 47.1195C36.5209 46.5324 35.4163 45.81 34.6339 44.9746C33.8515 44.1392 33.2992 43.2813 32.977 42.4459C32.6548 41.6105 32.4937 40.9332 32.4477 40.4139C32.4017 40.0753 32.5167 39.7817 32.7699 39.556C33.023 39.3302 33.2992 39.2173 33.6213 39.2173H34.3577C34.6339 39.2173 34.887 39.285 35.0941 39.3979C35.3012 39.5108 35.4853 39.8269 35.6234 40.301C35.8305 40.8429 36.1757 41.4976 36.659 42.2427C37.1422 42.9878 37.8787 43.6199 38.9142 44.1844C39.9498 44.7262 41.3766 45.0197 43.1945 45.0197C45.1276 45.0197 46.7385 44.7714 48.0272 44.2973C49.3159 43.8231 50.3054 42.9652 50.9958 41.746C51.6862 40.5268 52.0314 38.8335 52.0314 36.666V33.0988C51.1569 34.2728 50.0063 35.2436 48.5795 36.0113C47.1527 36.7789 45.3577 37.1627 43.1945 37.1627C41.1694 37.1627 39.4435 36.8015 38.0167 36.079C36.5899 35.3565 35.4393 34.3857 34.5648 33.1891C33.6904 31.9925 33.046 30.6378 32.6088 29.1703C32.1946 27.6801 31.9644 26.19 31.9184 24.6773C31.8724 23.7065 31.8724 22.7582 31.9184 21.7874C31.9644 20.2747 32.1715 18.7846 32.6088 17.2944C33.023 15.8043 33.6674 14.4722 34.5648 13.2756C35.4393 12.079 36.5899 11.1082 38.0167 10.3857C39.4435 9.66321 41.1694 9.30197 43.1945 9.30197C45.3577 9.30197 47.1757 9.70837 48.5795 10.4986C50.0063 11.2888 51.1569 12.2596 52.0314 13.3885V10.9953C52.0314 10.6566 52.1464 10.3631 52.3535 10.1373C52.5607 9.91157 52.8368 9.79868 53.205 9.79868H54.0565C54.4017 9.79868 54.7008 9.91157 54.931 10.1373C55.1611 10.3631 55.2761 10.6566 55.2761 10.9953V36.1242C55.2761 38.3368 54.931 40.3236 54.2866 42.1072C53.6192 43.8909 52.4456 45.3133 50.7427 46.397C48.9937 47.4807 46.5774 48 43.4477 48ZM43.5167 34.1599C45.5188 34.1599 47.1297 33.6858 48.3494 32.7601C49.569 31.8344 50.4895 30.6604 51.0648 29.2832C51.6402 27.8833 51.9623 26.4835 52.0314 25.0837C52.0544 24.5644 52.0774 23.9548 52.0774 23.2324C52.0774 22.5099 52.0544 21.9003 52.0314 21.381C51.9623 19.9812 51.6402 18.5588 51.0648 17.1816C50.4895 15.7817 49.592 14.6303 48.3494 13.7046C47.1297 12.7789 45.5188 12.3048 43.5167 12.3048C41.4916 12.3048 39.8577 12.7563 38.6611 13.6369C37.4414 14.54 36.5899 15.6914 36.0607 17.1364C35.5314 18.5814 35.2322 20.1392 35.1632 21.7874C35.1171 22.7582 35.1171 23.7065 35.1632 24.6773C35.2322 26.3255 35.5314 27.8833 36.0607 29.3283C36.5899 30.7733 37.4644 31.9473 38.6611 32.8278C39.8577 33.7309 41.4686 34.1599 43.5167 34.1599Z',
];
const SURFACE_PATHS = [
    'M61.0293 36.6435C60.6841 36.6435 60.408 36.5306 60.1778 36.3274C59.9707 36.1242 59.8557 35.8532 59.8557 35.492V10.9501C59.8557 10.6115 59.9707 10.3405 60.1778 10.1148C60.3849 9.91157 60.6611 9.79868 61.0293 9.79868H61.9268C62.272 9.79868 62.5481 9.91157 62.7782 10.1148C62.9854 10.318 63.1004 10.5889 63.1004 10.9501V13.3659C64.0439 12.1693 65.1946 11.1759 66.5523 10.4309C67.91 9.66321 69.705 9.30197 71.9372 9.30197C74.3305 9.30197 76.3096 9.79868 77.8975 10.8147C79.4854 11.8307 80.682 13.2079 81.4644 14.9464C82.2469 16.6848 82.6381 18.6491 82.6381 20.8617V35.5146C82.6381 35.8532 82.523 36.1242 82.3159 36.3499C82.1088 36.5531 81.8326 36.666 81.4644 36.666H80.567C80.2218 36.666 79.9456 36.5531 79.7155 36.3499C79.5084 36.1467 79.3933 35.8758 79.3933 35.5146V21.1101C79.3933 18.3556 78.7029 16.1881 77.3452 14.6303C75.9875 13.0724 73.9854 12.2822 71.3389 12.2822C68.8536 12.2822 66.8515 13.0724 65.3096 14.6303C63.7908 16.1881 63.0314 18.3556 63.0314 21.1101V35.5146C63.0314 35.8532 62.9163 36.1242 62.7092 36.3499C62.5021 36.5531 62.2259 36.666 61.8577 36.666H61.0293V36.6435Z',
    'M126.914 0.767639V3.47695H129.238V41.588H90.3933V3.47695H118.054V0.767639H87.6318V44.2973H132V0.767639H126.914Z',
];
const PURPLE_PATHS = [
    'M103.994 37.1628C102.314 37.1628 100.749 36.8241 99.3452 36.1242C97.9414 35.4243 96.7678 34.5212 95.8933 33.3697C95.0188 32.2183 94.5816 30.9088 94.5816 29.4638C94.5816 27.1157 95.5481 25.2418 97.4812 23.842C99.4142 22.4422 101.923 21.5165 105.006 21.1101L113.314 19.9812V18.175C113.314 16.3462 112.739 14.9238 111.611 13.8627C110.46 12.8241 108.665 12.2822 106.157 12.2822C104.339 12.2822 102.866 12.6435 101.739 13.3434C100.611 14.0433 99.8515 14.969 99.4142 16.0978C99.2301 16.6171 98.908 16.8655 98.4247 16.8655H97.5732C97.159 16.8655 96.8598 16.7526 96.6757 16.5268C96.4916 16.301 96.4226 16.0301 96.4226 15.7366C96.4226 15.2625 96.6067 14.6529 96.9749 13.9304C97.3431 13.2079 97.9184 12.4854 98.6778 11.7855C99.4603 11.0856 100.45 10.476 101.669 10.0019C102.889 9.52776 104.385 9.2794 106.134 9.2794C108.205 9.2794 109.908 9.55034 111.266 10.1148C112.623 10.6566 113.659 11.3791 114.418 12.2596C115.178 13.1402 115.707 14.111 116.029 15.1722C116.351 16.2333 116.513 17.2945 116.513 18.3104V35.492C116.513 35.8307 116.398 36.1016 116.19 36.3274C115.983 36.5306 115.707 36.6435 115.339 36.6435H114.441C114.096 36.6435 113.82 36.5306 113.59 36.3274C113.36 36.1242 113.268 35.8532 113.268 35.492V33.0762C112.854 33.6858 112.255 34.318 111.473 34.9276C110.69 35.5372 109.701 36.079 108.504 36.508C107.331 36.9596 105.812 37.1628 103.994 37.1628ZM104.5 34.1599C106.111 34.1599 107.584 33.8213 108.918 33.1214C110.253 32.444 111.312 31.3829 112.094 29.9379C112.877 28.5155 113.268 26.7093 113.268 24.5419V22.7808L106.479 23.7065C103.603 24.0903 101.439 24.7451 99.9665 25.6707C98.4937 26.5964 97.7573 27.7705 97.7573 29.1703C97.7573 30.2992 98.0795 31.2248 98.7469 31.9473C99.4142 32.6698 100.266 33.2117 101.301 33.5729C102.36 33.9793 103.418 34.1599 104.5 34.1599Z',
    'M121.46 4.28975C121.115 4.28975 120.839 4.17686 120.609 3.97366C120.402 3.77046 120.287 3.49953 120.287 3.13829V1.19661C120.287 0.857949 120.402 0.56444 120.609 0.338664C120.816 0.112888 121.092 0 121.46 0H123.462C123.808 0 124.107 0.112888 124.337 0.338664C124.567 0.56444 124.682 0.857949 124.682 1.19661V3.16087C124.682 3.49953 124.567 3.77046 124.337 3.99624C124.107 4.19944 123.808 4.31232 123.462 4.31232H121.46V4.28975Z',
    'M122.036 36.6435C121.69 36.6435 121.414 36.5306 121.184 36.3274C120.977 36.1242 120.862 35.8532 120.862 35.492V10.9501C120.862 10.6115 120.977 10.3405 121.184 10.1148C121.391 9.91156 121.667 9.79868 122.036 9.79868H122.933C123.278 9.79868 123.554 9.91156 123.785 10.1148C123.992 10.318 124.107 10.5889 124.107 10.9501V35.5146C124.107 35.8532 123.992 36.1242 123.785 36.3499C123.577 36.5531 123.301 36.666 122.933 36.666H122.036V36.6435Z',
];
function Logo({ className, variant = 'white' }) {
    const surfaceFill = variant === 'white' ? '#ffffff' : '#121622';
    return (jsxs("svg", { viewBox: "0 0 132 48", xmlns: "http://www.w3.org/2000/svg", className: className, role: "img", "aria-label": "LognAI", children: [jsx("g", { fill: BLUE, children: BLUE_PATHS.map((d, i) => (jsx("path", { d: d }, i))) }), jsx("g", { fill: surfaceFill, children: SURFACE_PATHS.map((d, i) => (jsx("path", { d: d }, i))) }), jsx("g", { fill: PURPLE, children: PURPLE_PATHS.map((d, i) => (jsx("path", { d: d }, i))) })] }));
}
function LogoMark({ className, variant = 'white' }) {
    const surfaceFill = variant === 'white' ? '#ffffff' : '#121622';
    return (jsxs("svg", { viewBox: "86 -1 47 50", xmlns: "http://www.w3.org/2000/svg", className: className, role: "img", "aria-label": "LognAI", children: [jsx("path", { fill: surfaceFill, d: SURFACE_PATHS[1] }), jsx("g", { fill: PURPLE, children: PURPLE_PATHS.map((d, i) => (jsx("path", { d: d }, i))) })] }));
}

export { AIInsightBanner, Accordion, AccordionContent, AccordionItem, AccordionTrigger, Alert, AlertCard, AlertDescription, AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay, AlertDialogPortal, AlertDialogTitle, AlertDialogTrigger, AlertTitle, AnimatedNumber, AppShell, Avatar, AvatarFallback, AvatarImage, Badge$1 as Badge, Breadcrumbs, Button, CHART_COLORS, Calendar, Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle, Chart, Checkbox, Collapsible, CollapsibleContent, CollapsibleTrigger, ColumnSettingsMenu, Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator, ContextMenu, ContextMenuCheckboxItem, ContextMenuContent, ContextMenuGroup, ContextMenuItem, ContextMenuLabel, ContextMenuPortal, ContextMenuRadioGroup, ContextMenuRadioItem, ContextMenuSeparator, ContextMenuShortcut, ContextMenuSub, ContextMenuSubContent, ContextMenuSubTrigger, ContextMenuTrigger, DataList, DataListItem, DataTable, DatePicker, Field as DetailField, DetailPanel, Section as DetailSection, Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger, DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuPortal, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger, EmptyState, EntityActions, ErrorState, FilterBar, Form, FormControl, FormDescription, FormDrawer, FormField, FormFooter, FormItem, FormLabel, FormMessage, FormSection, Icon$1 as Icon, IconButton, Input$1 as Input, Label$2 as Label, Logo, LogoMark, Menubar, MenubarContent, MenubarItem, MenubarMenu, MenubarSeparator, MenubarShortcut, MenubarTrigger, MetricRibbon, PageHeader, PageLayout, Pagination, Popover, PopoverAnchor, PopoverClose, PopoverContent, PopoverTrigger, Progress, RadioGroup$2 as RadioGroup, RadioGroupItem, ResourceHero, SPACING_BUTTON_GROUP_GAP, SPACING_CARD_PADDING, SPACING_FORM_FIELD, SPACING_HEADING_H1, SPACING_HEADING_H2, SPACING_HEADING_H3, SPACING_INFO_BOX_PADDING, SPACING_INLINE_GAP, SPACING_KPI_GRID_GAP, SPACING_KPI_GRID_GAP_PREMIUM, SPACING_PAGE_CONTAINER, SPACING_PAGE_SECTION, SPACING_PRESETS, SPACING_SECTION_ELEMENT, SPACING_STACK_VERTICAL, SPACING_TABLE_CELL, SPACING_TABLE_TO_PAGINATION, SPACING_WORKFLOW_STEPPER, ScrollArea, ScrollBar, Select, SelectContent, SelectGroup, SelectItem, SelectMultiValue, SelectTrigger, SelectValue, Separator$4 as Separator, Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetOverlay, SheetTitle, SheetTrigger, Sidebar, Skeleton, Slider, StatusPill, Stepper, SummaryCardGrid, SurfaceThemeProvider, Switch, Table, TableBody, TableCaption, TableCell, TableFooter, TableHead, TableHeader, TableRow, Tabs, TabsContent, TabsList, TabsTrigger, Textarea, ThemeProvider, ThemeSwitcher, Toast, ToastAction, ToastClose, ToastDescription, ToastProvider, ToastTitle, ToastViewport, Toaster, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, WorkflowIcons, badgeStyles$1 as badgeStyles, buttonStyles, cardStyles$1 as cardStyles, cn, colors, darkTheme, duration, easing, fontSize, fontWeight, fonts, iconButtonStyles, inputStyles, isEmptyValue, keyframes, lightTheme, lineHeight, radius, semanticSpacing, shadow, spacing, themeInitScript, themes, toast, tracking, typography, useRowTransitions, useSlidingHighlight, useSlidingMarker, useSurfaceTheme, useTheme, useToast, zIndex };
//# sourceMappingURL=index.js.map
